'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
//import io, { Socket } from 'socket.io-client';
import io from 'socket.io-client';
import TutorialDemo from './components/Demo';
import StatisticsPage from './components/StatisticsPage';
import LeaderboardPage from './components/LeaderboardPage';
import AboutPage from './components/AboutPage';
import PlayersPage from './components/PlayersPage';
import { Sandbox } from './components/Sandbox';
import PuzzlesPage from './components/puzzles/PuzzlesPage';
import ShopPage from './components/shop/ShopPage';
import { SpectatorListPage } from './components/spectator/SpectatorListPage';
import TournamentPage from './components/TournamentPage';
import GlobalTournamentOverlays from './components/GlobalTournamentOverlays';
import { GreedyGame } from './components/greedy/GreedyGame';
import Sidebar from './components/Sidebar';
import GlobalChat from './components/GlobalChat';
import AuthModal from './components/AuthModal';
import ChallengeToast from './components/ChallengeToast';
import ChallengeDeclinedToast from './components/ChallengeDeclinedToast';
import EmailVerification from './components/EmailVerification';
import ResetPassword from './components/ResetPassword';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminApiService } from './services/adminApiService';
import { AuthService } from './services/authService';
import { ApiService, ELO_VISIBLE } from './services/apiService';
import { formatEloResultLine } from './utils/eloUtils';
import { useAuth } from './hooks/useAuth';
import { useTimer } from './hooks/useTimer';
import { useGame } from './hooks/useGame';
import { useSocket } from './hooks/useSocket';
import {
  DEFAULT_CUSTOM_COLORS,
  DEFAULT_THEME,
  DEFAULT_GAME_SOUNDS_ENABLED,
  DEFAULT_NOTIFICATIONS_ENABLED,
  DEFAULT_ILLEGAL_HOVER_ENABLED,
  GLOBAL_CHAT_UI_ENABLED,
  GREEDY_NAV_UI_ENABLED,
  TOURNAMENTS_NAV_UI_ENABLED,
} from './utils/constants';
import { GameBoard } from './components/game/GameBoard';
import { GameControls } from './components/game/GameControls';
import { GameChat } from './components/game/GameChat';
import { Timer } from './components/game/Timer';
import { GameTimerDisplay } from './components/game/GameTimerDisplay';
import { SettingsModal } from './components/modals/SettingsModal';
import { TutorialModal } from './components/modals/TutorialModal';
import { ResignModal } from './components/modals/ResignModal';
import { DrawModal } from './components/modals/DrawModal';
import { ResignDrawModal } from './components/modals/ResignDrawModal';
import HumanAvailableModal from './components/modals/HumanAvailableModal';
import { Toast } from './components/ui/Toast';
import PageCloseButton from './components/PageCloseButton';
import { 
  handlePlayAsGuest, 
  handleViewStats,
  getApiUrl 
} from './components/Authentication';
import { trackVisit, trackBotStart, trackBotFinish } from './utils/visitorAnalytics';
import {
  evaluatePosition,
  evaluateMove,
  detectThreeYugoThreat,
  detectIgoFork,
  detectYugoToForkThreat,
  detectYugoTrap,
  detectMustWinThreeYugo,
  detectMustBlockThreeYugo,
  detectOpponentYugoRevealsThreat,
  detectYugoTrapExposure,
  detectDPA
} from './game/ai/evaluation';
import { minimax, TranspositionTable } from './game/ai/minimax';
import { quiescence, getTacticalMoves } from './game/ai/quiescence';
import { selectAIMove, AI_CONFIGS } from './game/ai/difficulties';
import type { Cell, GameState, MoveHistoryEntry, User, AuthState } from './game/types';
import { INITIAL_BOARD } from './game/types';
import {
  isValidMove,
  wouldCreateLineTooLong,
  hasLegalMoves,
  getAllValidMoves,
  copyBoard,
  makeMove,
  isEmptyCell
} from './game/logic/board';
import {
  checkForYugos,
  processYugos
} from './game/logic/yugos';
import {
  checkForIgo
} from './game/logic/igo';
import {
  countYugos
} from './game/logic/scoring';
import './migoyugo-styles.css';

// Types are now imported from ./game/types - duplicates removed
// All game logic functions are now imported from ./game/logic/* and ./game/ai/* - duplicates removed

// Global transposition table instance (using imported TranspositionTable)
const globalTransTable = new TranspositionTable();


const App: React.FC = () => {
  // Helper function to get winner name for personalized messages
  const getWinnerName = useCallback((
    winner: 'white' | 'black' | 'draw',
    gameMode: string,
    authState: any,
    gameState: any,
    opponentName?: string
  ) => {
    if (winner === 'draw') return 'Draw';
    
    if (gameMode === 'online') {
      // Use actual player names from gameState.players (set by server)
      // This ensures we show real usernames for member vs member games
      const whiteName = gameState.players?.white || 'White';
      const blackName = gameState.players?.black || 'Black';
      return winner === 'white' ? whiteName : blackName;
    } else if (gameMode.startsWith('ai-')) {
      const playerColorValue = playerColorRef.current;
      if (!playerColorValue) {
        return winner === 'white' ? (authState.user?.username || 'White') : 'AI';
      }
      
      if (playerColorValue === 'white') {
        return winner === 'white' 
          ? (authState.user?.username || 'White')
          : (gameState.players.black || 'AI');
      } else {
        return winner === 'black'
          ? (authState.user?.username || 'Black')
          : (gameState.players.white || 'AI');
      }
    } else {
      // Local play: bottom (Black) = signed-in user, top (White) = opponent
      return winner === 'black'
        ? (authState.user?.username || 'Black')
        : (gameState?.players?.white || 'White');
    }
  }, []);

  // Game state
  const [gameState, setGameState] = useState<GameState>({
    board: copyBoard(INITIAL_BOARD),
    currentPlayer: 'white',
    scores: { white: 0, black: 0 },
    gameStatus: 'waiting',
    lastMove: null,
    players: { white: 'Player 1', black: 'Player 2' },
    igoLine: null
  });
  const gameStateRef = useRef<GameState>(gameState);

  // Admin authentication state
  const [adminAuthenticated, setAdminAuthenticated] = useState(() => AdminApiService.isAuthenticated());

  // Page navigation state
  const [currentPage, setCurrentPage] = useState<'game' | 'statistics' | 'leaderboard' | 'admin' | 'puzzles' | 'shop' | 'spectator' | 'tournaments' | 'greedy'>(() => {
  if (typeof window === 'undefined') {
    return 'game'; // Default to game for SSR
  }
  const saved = localStorage.getItem('mig_current_page');
  if (saved === 'game' || saved === 'statistics' || saved === 'leaderboard' || saved === 'puzzles' || saved === 'shop' || saved === 'spectator' || saved === 'tournaments') {
    return saved;
  }
  // If 'about' was saved, default to game since it's now a modal
  if (saved === 'about') {
    return 'game';
  }
  // Always default to game page
  return 'game';
});
  const currentPageRef = useRef<'game' | 'statistics' | 'leaderboard' | 'admin' | 'puzzles' | 'shop' | 'spectator' | 'tournaments' | 'greedy'>(currentPage);

  // UI state
  const [showTutorial, setShowTutorial] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showPlayersModal, setShowPlayersModal] = useState(false);
  const showPlayersModalRef = useRef(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showGlobalChat, setShowGlobalChat] = useState(false);
  const [messagePlayerTarget, setMessagePlayerTarget] = useState<{ recipientId: number; recipientUsername: string } | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'signin' | 'signup'>('signin');
  const [showMatchmaking, setShowMatchmaking] = useState(false);
  const [showStatsAuth, setShowStatsAuth] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [viewingOtherPlayerStats, setViewingOtherPlayerStats] = useState<{ userId: number; username: string } | null>(null);
  const [isSearchingMatch, setIsSearchingMatch] = useState(false);
  const [matchmakingTimerPreference, setMatchmakingTimerPreference] = useState<5 | 10 | 'any'>(10); // Timer preference for matchmaking
  const [matchmakingIsRated, setMatchmakingIsRated] = useState(true);
  const [showImportMovesModal, setShowImportMovesModal] = useState(false);
  const [importMovesText, setImportMovesText] = useState('');
  const [userStats, setUserStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showPWABanner, setShowPWABanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const HUMAN_WAITLIST_KEY = 'mig_human_waitlist';
  const [humanWaitlistJoined] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(HUMAN_WAITLIST_KEY) === '1';
  });
  const [humanAvailablePrompt, setHumanAvailablePrompt] = useState<{ id: number; username: string } | null>(null);
  const humanAvailablePromptRef = useRef<{ id: number; username: string } | null>(null);
  const [humanAvailableBusy, setHumanAvailableBusy] = useState(false);
  const waitlistPlayerCooldownRef = useRef<Map<number, number>>(new Map());

  // Mobile detection state
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Settings state
  const [currentTheme, setCurrentTheme] = useState('classic');
  const [gameSoundsEnabled, setGameSoundsEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [illegalHoverEnabled, setIllegalHoverEnabled] = useState<boolean>(DEFAULT_ILLEGAL_HOVER_ENABLED);
  const [dmPrivacy, setDmPrivacy] = useState<'everyone' | 'nobody'>('everyone');
  const [customColors, setCustomColors] = useState({
    whiteMigo: '#ecf0f1',
    blackMigo: '#2c3e50',
    yugoColor: '#e74c3c',
    whiteYugo: '#e74c3c',
    blackYugo: '#e74c3c',
    boardColor: '#d1e6f9',
    hoverColor: '#a8c3e8', // classic hover color (darker than board)
    lastMoveColor: 'rgba(46, 204, 113, 0.2)' // classic last move color
  });

  // Review mode state
  const [isReviewMode, setIsReviewMode] = useState(false);
  const isReviewModeRef = useRef(false); // Ref for use in socket handlers
  const [isImportReviewMode, setIsImportReviewMode] = useState(false);
  
  // Spectator mode state
  const [spectatorMode, setSpectatorMode] = useState(false);
  const spectatorModeRef = useRef(false); // Ref for use in socket handlers
  const [eyeBlink, setEyeBlink] = useState(false); // State to trigger eye blink animation
  const [spectatingGameId, setSpectatingGameId] = useState<string | null>(null);
  const spectatingGameIdRef = useRef<string | null>(null); // Ref for use in socket handlers

  // Tournament context shown in spectator view when watching a tournament game
  const [spectatorTournamentCtx, setSpectatorTournamentCtx] = useState<{ name: string; round: number } | null>(null);

  // Global tournament notification state (persistent — active regardless of current page)
  const [tournamentReadyCheck, setTournamentReadyCheck] = useState<{
    matchId: number;
    tournamentId: number;
    tournamentName: string;
    opponentName: string;
    round: number;
    timeoutSeconds: number;
  } | null>(null);
  const [tournamentRCCountdown, setTournamentRCCountdown] = useState(60);
  const [tournamentRCAccepted, setTournamentRCAccepted] = useState(false);
  /** Opponent clicked Accept — shown on both pre-accept and waiting UIs (socket: tournament:peerReady). */
  const [tournamentOpponentPeerReady, setTournamentOpponentPeerReady] = useState(false);
  const tournamentRCTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tournamentReadyCheckRef = useRef<typeof tournamentReadyCheck>(null);
  const tournamentRCAcceptedRef = useRef(false);
  const [tournamentBanner, setTournamentBanner] = useState<{ name: string; tournamentId: number } | null>(null);
  /** Full-screen celebration when this player wins the tournament final. */
  const [tournamentChampionCelebration, setTournamentChampionCelebration] = useState<{
    tournamentName: string;
  } | null>(null);
  /** True while playing a tournament match (draw offers disabled). */
  const [isTournamentGame, setIsTournamentGame] = useState(false);
  const isTournamentGameRef = useRef(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [spectatorGamesList, setSpectatorGamesList] = useState<any[]>([]);
  const [spectatorTimers, setSpectatorTimers] = useState<Record<string, any>>({});
  const [currentReviewMove, setCurrentReviewMove] = useState(0);
  const currentReviewMoveRef = useRef(0);
  const [moveHistory, setMoveHistory] = useState<MoveHistoryEntry[]>([]);
  const moveHistoryRef = useRef<MoveHistoryEntry[]>([]);
  const [historyHoverIndex, setHistoryHoverIndex] = useState<number | null>(null);
  const [isProcessingMove, setIsProcessingMove] = useState(false);
  const isProcessingMoveRef = useRef(false); // Ref to avoid stale closures
  const [boardHistory, setBoardHistory] = useState<(Cell | null)[][][]>([]);
  const [startingPlayer, setStartingPlayer] = useState<'white' | 'black'>('white'); // Track who started the game
  const [aiMoveCount, setAiMoveCount] = useState(0); // Track AI moves for opening phase
  const [holdScrollInterval, setHoldScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const reviewModeProcessingTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Track setTimeout from goToMove
  const holdStartTimeRef = useRef<number | null>(null);
  const holdScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeReviewMoveRef = useRef<HTMLDivElement | null>(null);
  
  
  // Auto-scroll move history state
  const moveHistoryScrollRef = useRef<HTMLDivElement | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  // Timer state
  const [timers, setTimers] = useState({ white: 600, black: 600 });
  const [activeTimer, setActiveTimer] = useState<'white' | 'black' | null>(null);
  const [timerWarningShown, setTimerWarningShown] = useState({ white: false, black: false }); // Track if 20-second warning has been shown per player (once per game per player)
  const timerWarningShownRef = useRef({ white: false, black: false }); // Ref mirror for socket handlers
  const [boardWarningActive, setBoardWarningActive] = useState(false); // Track if board warning animation is active

  // Game mode state
  const [gameMode, setGameMode] = useState<'local' | 'ai-1' | 'ai-2' | 'ai-challenger' | 'ai-skilled' | 'ai-3' | 'ai-engine' | 'online'>('local');
  const gameModeRef = useRef<'local' | 'ai-1' | 'ai-2' | 'ai-challenger' | 'ai-skilled' | 'ai-3' | 'ai-engine' | 'online'>(gameMode);
 // const [waitingForAI, setWaitingForAI] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    primaryButton?: string;
    secondaryButton?: string;
    onPrimary?: () => void;
    onSecondary?: () => void;
  }>({ show: false, title: '', message: '' });

  // Challenge notification state
  const [currentChallenge, setCurrentChallenge] = useState<{
    challengeId: number;
    challengerUsername: string;
    timerSettings?: { minutesPerPlayer: number; incrementSeconds: number };
    isRated?: boolean;
  } | null>(null);
  const currentChallengeRef = useRef<{
    challengeId: number;
    challengerUsername: string;
    timerSettings?: { minutesPerPlayer: number; incrementSeconds: number };
    isRated?: boolean;
  } | null>(null);
  const [challengeQueue, setChallengeQueue] = useState<Array<{
    challengeId: number;
    challengerUsername: string;
    timerSettings?: { minutesPerPlayer: number; incrementSeconds: number };
    isRated?: boolean;
  }>>([]);
  const challengeQueueRef = useRef<Array<{
    challengeId: number;
    challengerUsername: string;
    timerSettings?: { minutesPerPlayer: number; incrementSeconds: number };
    isRated?: boolean;
  }>>([]);
  const [challengeDeclinedToast, setChallengeDeclinedToast] = useState<{
    challengedUsername: string;
    reason: 'declined' | 'timeout';
  } | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    challengeQueueRef.current = challengeQueue;
  }, [challengeQueue]);

  useEffect(() => {
    currentChallengeRef.current = currentChallenge;
    console.log('🔄 currentChallenge state changed:', currentChallenge ? {
      challengeId: currentChallenge.challengeId,
      challengerUsername: currentChallenge.challengerUsername,
      currentPage
    } : null);
  }, [currentChallenge, currentPage]);

  // Keep showPlayersModalRef in sync with state
  useEffect(() => {
    showPlayersModalRef.current = showPlayersModal;
  }, [showPlayersModal]);

  // Keep moveHistoryRef in sync with moveHistory state
  useEffect(() => {
    moveHistoryRef.current = moveHistory;
  }, [moveHistory]);

  // Keep isProcessingMoveRef in sync with isProcessingMove state
  useEffect(() => {
    isProcessingMoveRef.current = isProcessingMove;
  }, [isProcessingMove]);

  useEffect(() => {
    tournamentReadyCheckRef.current = tournamentReadyCheck;
  }, [tournamentReadyCheck]);

  useEffect(() => {
    tournamentRCAcceptedRef.current = tournamentRCAccepted;
  }, [tournamentRCAccepted]);
  
  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isGuest: false
  });
  const authStateRef = useRef<AuthState>(authState);
  const prevAuthStateRef = useRef<AuthState>(authState); // Track previous auth state to detect transitions
  const endingGameDueToSignInRef = useRef<boolean>(false); // Flag to track if we're ending game due to sign-in

  // Online game state
  const [socket, setSocket] = useState<any>(null);
  const [currentGameIsRated, setCurrentGameIsRated] = useState(false);
  const [gameId, setGameId] = useState<string | null>(null);
  const gameIdRef = useRef<string | null>(null);
  const [playerColor, setPlayerColor] = useState<'white' | 'black' | null>(null);
  const playerColorRef = useRef<'white' | 'black' | null>(null); // Ref to avoid closure issues
  const [opponentName, setOpponentName] = useState<string>('');
  const opponentNameRef = useRef<string>(''); // Ref to avoid closure issues
  const gameStartTimeRef = useRef<number | null>(null); // Track when game started to suppress false positive reconnection messages
  const lastServerTimerUpdateRef = useRef<number>(Date.now()); // Track when we last received server timer update
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [disconnectedPlayerColor, setDisconnectedPlayerColor] = useState<'white' | 'black' | null>(null);
  const [gracePeriodSeconds, setGracePeriodSeconds] = useState(30);
  const gracePeriodIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [minutesPerPlayer, setMinutesPerPlayer] = useState(10);
  const [incrementSeconds, setIncrementSeconds] = useState(0);
  
  // Timestamp-based timer tracking (for AI and local games)
  const timerStartTimeRef = useRef<{ white: number | null; black: number | null }>({ white: null, black: null });
  const timerInitialValuesRef = useRef<{ white: number; black: number }>({ white: 600, black: 600 });
  const timerPausedTimeRef = useRef<{ white: number; black: number }>({ white: 0, black: 0 });
  const timerPauseStartRef = useRef<{ white: number | null; black: number | null }>({ white: null, black: null });
  const animationFrameRef = useRef<number | null>(null);
  const previousTimerValuesRef = useRef<{ white: number; black: number }>({ white: 600, black: 600 }); // Track previous timer values to detect threshold crossing

  // Preloaded audio objects for instant playback (no lag)
  const preloadedSounds = useRef<{
    migo: HTMLAudioElement | null;
    yugo: HTMLAudioElement | null;
    igo: HTMLAudioElement | null;
    wego: HTMLAudioElement | null;
    challengeNotification: HTMLAudioElement | null;
    endGame: HTMLAudioElement | null;
    illegalMove: HTMLAudioElement | null;
    challengeDeclined: HTMLAudioElement | null;
    sendMsg: HTMLAudioElement | null;
    rcvMsg: HTMLAudioElement | null;
    timer: HTMLAudioElement | null;
  }>({
    migo: null,
    yugo: null,
    igo: null,
    wego: null,
    challengeNotification: null,
    endGame: null,
    illegalMove: null,
    challengeDeclined: null,
    sendMsg: null,
    rcvMsg: null,
    timer: null
  });
  
  // Track if audio is ready to play (readyState >= HAVE_CURRENT_DATA)
  const audioReady = useRef<{
    migo: boolean;
    yugo: boolean;
    igo: boolean;
    wego: boolean;
    challengeNotification: boolean;
    endGame: boolean;
    illegalMove: boolean;
    challengeDeclined: boolean;
    sendMsg: boolean;
    rcvMsg: boolean;
    timer: boolean;
  }>({
    migo: false,
    yugo: false,
    igo: false,
    wego: false,
    challengeNotification: false,
    endGame: false,
    illegalMove: false,
    challengeDeclined: false,
    sendMsg: false,
    rcvMsg: false,
    timer: false
  });
  
  // Track if audio context has been warmed up (for mobile browsers)
  const audioContextWarmed = useRef<boolean>(false);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const [isGameStarted, setIsGameStarted] = useState(false);
  const isGameStartedRef = useRef(false);
  
  // Check if user is near bottom of scroll container
  const checkIfAtBottom = useCallback(() => {
    if (moveHistoryScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = moveHistoryScrollRef.current;
      const threshold = 50; // pixels from bottom
      const isAtBottom = scrollHeight - scrollTop - clientHeight < threshold;
      setShouldAutoScroll(isAtBottom);
    }
  }, []);
  
  // Handle scroll events to detect if user scrolled up
  const handleMoveHistoryScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);
  
  // Auto-scroll to bottom when new moves are added (only if at bottom and not in review mode)
  useEffect(() => {
    if (shouldAutoScroll && !isReviewMode && moveHistoryScrollRef.current && moveHistory.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (moveHistoryScrollRef.current) {
          moveHistoryScrollRef.current.scrollTop = moveHistoryScrollRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [moveHistory.length, shouldAutoScroll, isReviewMode]);
  
  // Initial scroll to bottom when game starts
  useEffect(() => {
    if (isGameStarted && moveHistoryScrollRef.current && moveHistory.length > 0) {
      setTimeout(() => {
        if (moveHistoryScrollRef.current) {
          moveHistoryScrollRef.current.scrollTop = moveHistoryScrollRef.current.scrollHeight;
          setShouldAutoScroll(true);
        }
      }, 0);
    }
  }, [isGameStarted, moveHistory.length]);
  
  // Reset auto-scroll when exiting review mode
  useEffect(() => {
    if (!isReviewMode) {
      setShouldAutoScroll(true);
    }
  }, [isReviewMode]);

  // Measure board position for modal centering on desktop
  useEffect(() => {
    const updateBoardCenter = () => {
      if (window.innerWidth >= 1025) {
        const boardArea = document.querySelector('.game-board-area');
        if (boardArea) {
          const rect = boardArea.getBoundingClientRect();
          const boardCenter = rect.left + rect.width / 2;
          document.documentElement.style.setProperty('--board-center-x', `${boardCenter}px`);
        }
      } else {
        // Reset to viewport center on mobile
        document.documentElement.style.setProperty('--board-center-x', '50vw');
      }
    };

    updateBoardCenter();
    window.addEventListener('resize', updateBoardCenter);
    // Also update when page changes or game starts
    const interval = setInterval(updateBoardCenter, 100);

    return () => {
      window.removeEventListener('resize', updateBoardCenter);
      clearInterval(interval);
    };
  }, [currentPage, isGameStarted]);
  const [rematchState, setRematchState] = useState<{
    requested: boolean;
    fromPlayer: string | null;
    requestedBy?: string;
    waitingForResponse?: boolean;
  }>({ requested: false, fromPlayer: null });
  const [toast, setToast] = useState<string>('');
  const [showResignConfirmation, setShowResignConfirmation] = useState(false);
  const [showResignDrawModal, setShowResignDrawModal] = useState(false);
  const [showDrawOffer, setShowDrawOffer] = useState(false);
  const [pendingDrawFrom, setPendingDrawFrom] = useState<string | null>(null);
  const [originalGameState, setOriginalGameState] = useState<GameState | null>(null);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [gameResultRecorded, setGameResultRecorded] = useState(false);
  const gameResultRecordedRef = useRef(false);
  const botFinishTrackedRef = useRef(false);
  const lastGameEndReasonRef = useRef<string | null>(null);

  useEffect(() => {
    gameIdRef.current = gameId;
  }, [gameId]);

  useEffect(() => {
    isGameStartedRef.current = isGameStarted;
  }, [isGameStarted]);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  useEffect(() => {
    humanAvailablePromptRef.current = humanAvailablePrompt;
  }, [humanAvailablePrompt]);

  // Human waitlist: poll for an available opponent (stays on while you play bots)
  useEffect(() => {
    if (!humanWaitlistJoined || !authState.isAuthenticated || authState.isGuest) {
      return;
    }
    const tick = async () => {
      if (humanAvailablePromptRef.current) return;
      const uid = authState.user?.id;
      if (!uid) return;
      try {
        const result = await ApiService.getPlayers(undefined, true);
        if (result.error || !result.players?.length) return;
        const myIdStr = String(uid);
        const available = result.players.filter(
          (p: { id: number; is_online: boolean; in_game: boolean }) =>
            String(p.id) !== myIdStr && p.is_online && !p.in_game
        );
        if (available.length === 0) return;
        const p = available[0];
        const until = waitlistPlayerCooldownRef.current.get(p.id);
        if (until && Date.now() < until) return;
        setHumanAvailablePrompt({ id: p.id, username: p.username });
        if (notificationsEnabled) {
          try {
            const preloadedAudio = preloadedSounds.current.challengeNotification;
            const isReady = audioReady.current.challengeNotification;
            if (preloadedAudio && isReady && preloadedAudio.readyState >= 2) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else if (preloadedAudio) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else {
              const audio = new Audio('/sounds/challenge-notification.mp3');
              audio.volume = 0.5;
              audio.play().catch(() => {});
            }
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    };
    tick();
    const intervalId = window.setInterval(tick, 15000);
    return () => window.clearInterval(intervalId);
  }, [humanWaitlistJoined, authState.isAuthenticated, authState.isGuest, authState.user?.id, notificationsEnabled]);

  // Detect guest → authenticated transition during active game and end game immediately
  useEffect(() => {
    const prevState = prevAuthStateRef.current;
    const currentState = authState;
    
    // Check if transitioned from guest to authenticated
    // A user is a guest if isGuest is explicitly true
    const wasGuest = prevState.isGuest === true;
    const nowAuthenticated = currentState.isAuthenticated === true && currentState.isGuest === false;
    
    // Only act if we're in an active online game
    const isInActiveGame = gameMode === 'online' && isGameStarted && gameId && socket;
    
    if (wasGuest && nowAuthenticated && isInActiveGame) {
      console.log('🚨 Guest signed in during active game - ending game immediately');
      
      // Set flag so we can show custom message
      endingGameDueToSignInRef.current = true;
      
      // Disconnect socket to trigger backend disconnect handler
      // This will end the game and notify opponent with standard disconnect message
      if (socket && socket.connected) {
        socket.disconnect();
      }
      
      // Show custom message to the user who signed in
      setTimeout(() => {
        setNotification({
          title: 'Game Abandoned',
          message: 'The game has ended because you signed in during a guest game. Members can only play against other members.',
          show: true
        });
        endingGameDueToSignInRef.current = false;
      }, 500);
      
      // Reset game state
      setIsGameStarted(false);
      isGameStartedRef.current = false;
      setGameId(null);
      gameIdRef.current = null;
      setPlayerColor(null);
      playerColorRef.current = null;
      setOpponentName('');
      opponentNameRef.current = '';
      setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
      
      // Clear inGame flag since user is now authenticated
      if (currentState.isAuthenticated) {
        ApiService.setInGame(false).catch(console.error);
      }
    }
    
    // Update previous state for next comparison
    prevAuthStateRef.current = { ...authState };
  }, [authState, gameMode, isGameStarted, gameId, socket]);

  useEffect(() => {
    gameModeRef.current = gameMode;
  }, [gameMode]);

  useEffect(() => {
    gameResultRecordedRef.current = gameResultRecorded;
  }, [gameResultRecorded]);

  useEffect(() => {
    isReviewModeRef.current = isReviewMode;
  }, [isReviewMode]);

  useEffect(() => {
    spectatorModeRef.current = spectatorMode;
  }, [spectatorMode]);

  useEffect(() => {
    spectatingGameIdRef.current = spectatingGameId;
  }, [spectatingGameId]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Cleanup when leaving spectator mode
  useEffect(() => {
    return () => {
      // If component unmounts while in spectator mode, clean up
      if (spectatorModeRef.current && socket && socket.connected) {
        const currentGameId = spectatingGameIdRef.current;
        if (currentGameId) {
          console.log('Cleaning up spectator mode on unmount');
          socket.emit('stopWatching', { gameId: currentGameId });
        }
      }
    };
  }, []); // Empty deps - only run on unmount

  // Cleanup when leaving spectator list page
  useEffect(() => {
    // Store current page at effect time
    const wasOnSpectatorPage = currentPageRef.current === 'spectator';
    
    return () => {
      // If we were on spectator page and now leaving, leave spectator lobby
      if (wasOnSpectatorPage && socket && socket.connected) {
        console.log('Leaving spectator lobby');
        socket.emit('leaveSpectatorLobby');
      }
    };
  }, [currentPage, socket]);

  // Random eye blink animation - triggers every 2-3 minutes when in spectator mode
  useEffect(() => {
    if (!spectatorMode) {
      setEyeBlink(false);
      return;
    }

    const scheduleBlink = () => {
      // Random delay between 120-180 seconds (2-3 minutes)
      const delay = Math.random() * 60000 + 120000; // 120000ms to 180000ms
      
      const timeoutId = setTimeout(() => {
        setEyeBlink(true);
        // Reset after animation completes (300ms)
        setTimeout(() => {
          setEyeBlink(false);
          // Schedule next blink
          scheduleBlink();
        }, 300);
      }, delay);

      return timeoutId;
    };

    const timeoutId = scheduleBlink();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [spectatorMode]);

  const shouldProcessServerTimer = useCallback((payloadGameId?: string | null) => {
    // CRITICAL: Never process timer updates if game has finished
    if (gameStateRef.current?.gameStatus === 'finished') {
      return false;
    }
    
    // Spectator: require a matching gameId so leftover rooms can't overwrite clocks
    if (spectatorModeRef.current) {
      const spectatingId = spectatingGameIdRef.current;
      if (!spectatingId) return false;
      if (!payloadGameId || payloadGameId !== spectatingId) {
        return false;
      }
      return true;
    }
    
    // Player: require matching gameId when provided (server now always sends it)
    const activeGameId = gameIdRef.current;

    if (!isGameStartedRef.current || !activeGameId) {
      return false;
    }

    if (payloadGameId && payloadGameId !== activeGameId) {
      return false;
    }

    return true;
  }, []);

  // Centralized function to record game results
  // Around line 1091-1131 - Replace the entire recordGameEnd function

  const recordGameEnd = useCallback((winner: 'white' | 'black' | 'draw', reason: string) => {
  // CRITICAL: Set flag IMMEDIATELY to prevent race conditions and duplicate recordings
  if (gameResultRecordedRef.current) {
    console.log('🚫 Game already recorded, ignoring duplicate call');
    return;
  }

  const endedMode = gameModeRef.current;
  if (endedMode && String(endedMode).startsWith('ai-') && !botFinishTrackedRef.current) {
    botFinishTrackedRef.current = true;
    trackBotFinish(endedMode);
  }
  
  // Set flag immediately before any async operations
  gameResultRecordedRef.current = true;
  setGameResultRecorded(true);
  
  const currentAuthState = authStateRef.current;
  const currentGameState = gameStateRef.current;
  
  // Determine player color from ref first, fallback to gameState.players if ref is null
  let currentPlayerColor = playerColorRef.current;
  if (!currentPlayerColor && currentAuthState.user) {
    // Fallback: determine color from gameState.players
    const currentUsername = currentAuthState.user.username;
    if (currentGameState.players.white === currentUsername) {
      currentPlayerColor = 'white';
    } else if (currentGameState.players.black === currentUsername) {
      currentPlayerColor = 'black';
    }
  }
  
  console.log('🎯 recordGameEnd called:', {
    winner,
    reason,
    currentPlayerColor,
    isAuthenticated: currentAuthState.isAuthenticated,
    hasUser: !!currentAuthState.user,
    players: currentGameState.players
  });
  
  if (!currentPlayerColor) {
    console.log('🚫 No playerColor available, cannot record game result');
    // Reset flag if we can't record
    gameResultRecordedRef.current = false;
    setGameResultRecorded(false);
    return;
  }
  
  if (!currentAuthState.isAuthenticated || !currentAuthState.user) {
    console.log('🚫 Not authenticated, cannot record game result');
    // Reset flag if not authenticated
    gameResultRecordedRef.current = false;
    setGameResultRecorded(false);
    return;
  }
  
  const opponentName = opponentNameRef.current || 
    (currentPlayerColor === 'white' ? currentGameState.players.black : currentGameState.players.white);
  
  if (!opponentName) {
    console.log('🚫 No opponent name available, cannot record game result');
    // Reset flag if we can't record
    gameResultRecordedRef.current = false;
    setGameResultRecorded(false);
    return;
  }
  
  console.log('✅ Recording for:', {
    username: currentAuthState.user?.username,
    myColor: currentPlayerColor,
    opponent: opponentName,
    winner
  });
  
  // At this point, currentPlayerColor is guaranteed to be 'white' or 'black' (not null)
  // because we checked and returned early if it was null
  const playerColor: 'white' | 'black' = currentPlayerColor;
  
  import('./services/authService').then(({ AuthService }) => {
    // Determine result: if winner matches my color, I won; otherwise I lost
    const resultType = winner === 'draw' ? 'draw' : 
                      winner === playerColor ? 'win' : 'loss';
    
    console.log(`📈 Final result: ${resultType} (I am ${playerColor}, winner is ${winner})`);
    
    const currentGameMode = gameModeRef.current;
    // Get current moveHistory from ref (always up-to-date)
    const currentMoveHistory = moveHistoryRef.current || [];
    // Convert moveHistory to format expected by backend (remove moveNumber, keep yugos)
    const movesForReplay = currentMoveHistory.map(move => ({
      row: move.row,
      col: move.col,
      player: move.player,
      yugos: move.yugos || 0
    }));
    AuthService.recordGameResult(resultType, opponentName, playerColor, currentGameMode, movesForReplay).then(result => {
      if (result.success) {
        console.log(`✅ ${resultType} recorded successfully:`, result);
      } else {
        console.error(`❌ Failed to record ${resultType}:`, result.error);
        // Reset flag on failure so user can retry
        gameResultRecordedRef.current = false;
        setGameResultRecorded(false);
      }
    }).catch(err => {
      console.error(`❌ Error recording ${resultType}:`, err);
      // Reset flag on error so user can retry
      gameResultRecordedRef.current = false;
      setGameResultRecorded(false);
    });
  }).catch((error) => {
    console.error('❌ Error importing AuthService:', error);
    // Reset flag on error
    gameResultRecordedRef.current = false;
    setGameResultRecorded(false);
  });
}, []);

  const pendingResetForfeitureRef = useRef<'self' | 'opponent' | null>(null);

  const clearBoardAfterResetWin = useCallback(() => {
    const perspective = pendingResetForfeitureRef.current;
    if (perspective === 'self') {
      setGameState(prev => ({
        ...prev,
        board: copyBoard(INITIAL_BOARD),
        currentPlayer: 'white',
        scores: { white: 0, black: 0 },
        lastMove: null,
        igoLine: null
      }));
      setMoveHistory([]);
      setBoardHistory([]);
      setNewlyPlacedDots(new Set());
      setFadingDots(new Set());
      setActiveTimer(null);
      setIsGameStarted(false);
      isGameStartedRef.current = false;
    }
    pendingResetForfeitureRef.current = null;
  }, []);

  const hideNotification = useCallback((options?: { resetContent?: boolean; preserveBoard?: boolean }) => {
    if (options?.resetContent) {
      setNotification({ title: '', message: '', show: false });
    } else {
      setNotification(prev => ({ ...prev, show: false }));
    }

    if (!options?.preserveBoard && lastGameEndReasonRef.current === 'reset') {
      clearBoardAfterResetWin();
      lastGameEndReasonRef.current = null;
    }
  }, [clearBoardAfterResetWin]);

  // Room-based multiplayer state
  const [currentRoom, setCurrentRoom] = useState<{
    code: string;
    isHost: boolean;
    hostName: string;
    guestName?: string;
    status: 'waiting' | 'ready' | 'active';
  } | null>(null);
  const currentRoomRef = useRef<{
    code: string;
    isHost: boolean;
    hostName: string;
    guestName?: string;
    status: 'waiting' | 'ready' | 'active';
  } | null>(null);

  useEffect(() => {
    currentRoomRef.current = currentRoom;
  }, [currentRoom]);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [roomCodeInput, setRoomCodeInput] = useState('');
  const [roomTimerPreference, setRoomTimerPreference] = useState<5 | 10>(10); // Timer preference for rooms

  // Tutorial animation ref
 // const animationRef = useRef<NodeJS.Timeout | null>(null);

  // Animation state for migo placement and removal
  const [newlyPlacedDots, setNewlyPlacedDots] = useState<Set<string>>(new Set());
  const [fadingDots, setFadingDots] = useState<Set<string>>(new Set());
  const [illegalMoveCell, setIllegalMoveCell] = useState<{ row: number; col: number } | null>(null);

  // Load settings from backend or localStorage on component mount and when auth state changes
  useEffect(() => {
    loadSavedSettings();
  }, [authState.isAuthenticated]);

  // Preload audio files for instant playback (eliminates lag)
  useEffect(() => {
    const soundFiles: Array<'migo' | 'yugo' | 'igo' | 'wego'> = ['migo', 'yugo', 'igo', 'wego'];
    
    soundFiles.forEach(soundName => {
      const audio = new Audio(`/sounds/${soundName}.mp3`);
      // Set volume based on sound type
      if (soundName === 'igo') {
        audio.volume = 0.168;
      } else {
        audio.volume = 0.3;
      }
      // Preload the audio file
      audio.preload = 'auto';
      // Store the preloaded audio object
      preloadedSounds.current[soundName] = audio;
      
      // Wait for audio to be ready before marking as ready
      const markAsReady = () => {
        // Check readyState: HAVE_CURRENT_DATA (2) or higher means we can play
        if (audio.readyState >= 2) {
          audioReady.current[soundName] = true;
        }
      };
      
      // Check if already ready (some browsers load instantly)
      markAsReady();
      
      // Listen for canplaythrough event (audio is fully loaded and ready)
      audio.addEventListener('canplaythrough', markAsReady);
      // Also listen for loadeddata as fallback
      audio.addEventListener('loadeddata', markAsReady);
      
      // Try to load the audio (some browsers need this)
      try {
        audio.load();
      } catch (e) {
        // Preload failed - silently continue, fallback will create new Audio when needed
      }
    });
    
    // Preload challenge notification sound
    const challengeAudio = new Audio('/sounds/challenge-notification.mp3');
    challengeAudio.volume = 0.5;
    challengeAudio.preload = 'auto';
    preloadedSounds.current.challengeNotification = challengeAudio;
    
    const markChallengeReady = () => {
      if (challengeAudio.readyState >= 2) {
        audioReady.current.challengeNotification = true;
      }
    };
    
    markChallengeReady();
    challengeAudio.addEventListener('canplaythrough', markChallengeReady);
    challengeAudio.addEventListener('loadeddata', markChallengeReady);
    
    try {
      challengeAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }
    
    // Preload end-game sound (for opponent disconnection wins)
    const endGameAudio = new Audio('/sounds/end-game.mp3');
    endGameAudio.volume = 0.5;
    endGameAudio.preload = 'auto';
    preloadedSounds.current.endGame = endGameAudio;
    
    const markEndGameReady = () => {
      if (endGameAudio.readyState >= 2) {
        audioReady.current.endGame = true;
      }
    };
    
    markEndGameReady();
    endGameAudio.addEventListener('canplaythrough', markEndGameReady);
    endGameAudio.addEventListener('loadeddata', markEndGameReady);
    
    try {
      endGameAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }
    
    // Preload illegal move sound
    const illegalMoveAudio = new Audio('/sounds/illegal-move.mp3');
    illegalMoveAudio.volume = 0.5;
    illegalMoveAudio.preload = 'auto';
    preloadedSounds.current.illegalMove = illegalMoveAudio;
    
    const markIllegalMoveReady = () => {
      if (illegalMoveAudio.readyState >= 2) {
        audioReady.current.illegalMove = true;
      }
    };
    
    markIllegalMoveReady();
    illegalMoveAudio.addEventListener('canplaythrough', markIllegalMoveReady);
    illegalMoveAudio.addEventListener('loadeddata', markIllegalMoveReady);
    
    try {
      illegalMoveAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }

    // Preload challenge declined sound
    const challengeDeclinedAudio = new Audio('/sounds/challenge-declined.mp3');
    challengeDeclinedAudio.volume = 0.5;
    challengeDeclinedAudio.preload = 'auto';
    preloadedSounds.current.challengeDeclined = challengeDeclinedAudio;
    
    const markChallengeDeclinedReady = () => {
      if (challengeDeclinedAudio.readyState >= 2) {
        audioReady.current.challengeDeclined = true;
      }
    };
    
    markChallengeDeclinedReady();
    challengeDeclinedAudio.addEventListener('canplaythrough', markChallengeDeclinedReady);
    challengeDeclinedAudio.addEventListener('loadeddata', markChallengeDeclinedReady);
    
    try {
      challengeDeclinedAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }

    // Preload send message sound
    const sendMsgAudio = new Audio('/sounds/send-msg.mp3');
    sendMsgAudio.volume = 0.5;
    sendMsgAudio.preload = 'auto';
    preloadedSounds.current.sendMsg = sendMsgAudio;
    
    const markSendMsgReady = () => {
      if (sendMsgAudio.readyState >= 2) {
        audioReady.current.sendMsg = true;
      }
    };
    
    markSendMsgReady();
    sendMsgAudio.addEventListener('canplaythrough', markSendMsgReady);
    sendMsgAudio.addEventListener('loadeddata', markSendMsgReady);
    
    try {
      sendMsgAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }

    // Preload receive message sound
    const rcvMsgAudio = new Audio('/sounds/rcv-msg.mp3');
    rcvMsgAudio.volume = 0.5;
    rcvMsgAudio.preload = 'auto';
    preloadedSounds.current.rcvMsg = rcvMsgAudio;
    
    const markRcvMsgReady = () => {
      if (rcvMsgAudio.readyState >= 2) {
        audioReady.current.rcvMsg = true;
      }
    };
    
    markRcvMsgReady();
    rcvMsgAudio.addEventListener('canplaythrough', markRcvMsgReady);
    rcvMsgAudio.addEventListener('loadeddata', markRcvMsgReady);
    
    try {
      rcvMsgAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }

    // Preload timer warning sound
    const timerAudio = new Audio('/sounds/timer.mp3');
    timerAudio.volume = 0.25;
    timerAudio.preload = 'auto';
    preloadedSounds.current.timer = timerAudio;
    
    const markTimerReady = () => {
      if (timerAudio.readyState >= 2) {
        audioReady.current.timer = true;
      }
    };
    
    markTimerReady();
    timerAudio.addEventListener('canplaythrough', markTimerReady);
    timerAudio.addEventListener('loadeddata', markTimerReady);
    
    try {
      timerAudio.load();
    } catch (e) {
      // Preload failed - fallback will create new Audio when needed
    }
  }, []);

  // Warm up audio context on first user interaction (critical for mobile browsers)
  useEffect(() => {
    if (audioContextWarmed.current) {
      return; // Already warmed up
    }

    const warmUpAudio = () => {
      if (audioContextWarmed.current) {
        return; // Already warmed up
      }

      // Play silent sounds to initialize audio context and unlock audio elements
      // This is required by many mobile browsers before audio can play
      const warmUpElement = (el: HTMLAudioElement) => {
        const vol = el.volume;
        el.volume = 0;
        const p = el.play();
        if (p !== undefined) {
          p.then(() => { el.pause(); el.currentTime = 0; el.volume = vol; })
           .catch(() => { el.volume = vol; });
        }
      };

      try {
        const migo = preloadedSounds.current.migo || new Audio('/sounds/migo.mp3');
        warmUpElement(migo);

        // Also warm up the timer sound so it can play from RAF/socket callbacks
        if (preloadedSounds.current.timer) {
          warmUpElement(preloadedSounds.current.timer);
        }

        audioContextWarmed.current = true;
      } catch (e) {
        // Silently fail - warm-up is best effort
      }
    };

    // Listen for first user interaction (click or touch)
    const events = ['click', 'touchstart', 'keydown'];
    const handleInteraction = () => {
      warmUpAudio();
      // Remove listeners after first interaction
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };

    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, []);

  // Check if we're on admin route
  // Email verification state
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  // Password reset state
  const [resetPasswordToken, setResetPasswordToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      
      // Check for email verification token
      if (pathname === '/verify-email' && searchParams.get('token')) {
        setVerificationToken(searchParams.get('token'));
        return; // Don't process other routing
      }
      
      // Check for password reset token
      if (pathname === '/reset-password' && searchParams.get('token')) {
        setResetPasswordToken(searchParams.get('token'));
        return; // Don't process other routing
      }
      
      if (pathname === '/admin' || pathname.startsWith('/admin/')) {
        setCurrentPage('admin');
        // Check admin auth state when navigating to admin page
        setAdminAuthenticated(AdminApiService.isAuthenticated());
      }

      if (
        GREEDY_NAV_UI_ENABLED &&
        (window.location.hash === '#greedy' || searchParams.get('greedy') === '1')
      ) {
        setCurrentPage('greedy');
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const toSave = currentPage === 'greedy' ? 'game' : currentPage;
      localStorage.setItem('mig_current_page', toSave);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!GREEDY_NAV_UI_ENABLED && currentPage === 'greedy') {
      setCurrentPage('game');
    }
  }, [currentPage]);

  useEffect(() => {
    if (!TOURNAMENTS_NAV_UI_ENABLED && currentPage === 'tournaments') {
      setCurrentPage('game');
    }
  }, [currentPage]);


  // Keep playerColorRef in sync with playerColor to avoid closure issues
  useEffect(() => {
    playerColorRef.current = playerColor;
    console.log('🎨 playerColorRef updated:', playerColor);
  }, [playerColor]);

  // Keep timerWarningShownRef in sync
  useEffect(() => {
    timerWarningShownRef.current = timerWarningShown;
  }, [timerWarningShown]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 600 || 
                      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobileDevice(isMobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Manage body class for game page
  useEffect(() => {
    document.body.classList.add('game-page');
    
    return () => {
      document.body.classList.remove('game-page');
    };
  }, [currentPage]);

  // Add game-playing class only when on the actual game view.
  // This allows CSS to target the game board layout specifically,
  // distinct from other pages (stats, leaderboard, puzzles) that
  // also live inside the App component and always carry game-page.
  useEffect(() => {
    if (currentPage === 'game') {
      document.body.classList.add('game-playing');
    } else {
      document.body.classList.remove('game-playing');
    }
    return () => {
      document.body.classList.remove('game-playing');
    };
  }, [currentPage]);

  // Cleanup: Clear inGame flag when page closes or component unmounts
  useEffect(() => {
    const handleBeforeUnload = async () => {
      // Update inGame status via API if user is authenticated
      if (ApiService.isAuthenticated()) {
        ApiService.setInGame(false).catch(console.error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (ApiService.isAuthenticated()) {
        ApiService.setInGame(false).catch(console.error);
      }
    };
  }, []);
 
  // Load saved settings from backend (if authenticated) or localStorage
  const loadSavedSettings = async () => {
    // If authenticated, try to load from backend first
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        const result = await ApiService.getUserPreferences();
        if (result.preferences) {
          // Load from backend
          if (result.preferences.theme && ['classic', 'high-contrast', 'galaxy', 'neon', 'marble', 'wood', 'custom'].includes(result.preferences.theme)) {
            setCurrentTheme(result.preferences.theme);
            // Also save to localStorage for consistency
            localStorage.setItem('migoyugoTheme', result.preferences.theme);
          }
          
          if (result.preferences.customColors) {
            const colors = result.preferences.customColors;
            // Backward compatibility: if whiteYugo or blackYugo are missing, use yugoColor
            if (!colors.whiteYugo && colors.yugoColor) {
              colors.whiteYugo = colors.yugoColor;
            }
            if (!colors.blackYugo && colors.yugoColor) {
              colors.blackYugo = colors.yugoColor;
            }
            setCustomColors(colors);
            localStorage.setItem('migoyugoCustomColors', JSON.stringify(colors));
          }
          
          // Handle new preferences
          if (result.preferences.gameSoundsEnabled !== undefined) {
            setGameSoundsEnabled(result.preferences.gameSoundsEnabled);
            localStorage.setItem('migoyugoGameSoundsEnabled', JSON.stringify(result.preferences.gameSoundsEnabled));
          }
          if (result.preferences.notificationsEnabled !== undefined) {
            setNotificationsEnabled(result.preferences.notificationsEnabled);
            localStorage.setItem('migoyugoNotificationsEnabled', JSON.stringify(result.preferences.notificationsEnabled));
          }
          // Migration: handle old soundEnabled preference
          if (result.preferences.soundEnabled !== undefined && result.preferences.gameSoundsEnabled === undefined) {
            const oldValue = result.preferences.soundEnabled;
            setGameSoundsEnabled(oldValue);
            setNotificationsEnabled(oldValue);
            localStorage.setItem('migoyugoGameSoundsEnabled', JSON.stringify(oldValue));
            localStorage.setItem('migoyugoNotificationsEnabled', JSON.stringify(oldValue));
          }
          if (result.preferences.dmPrivacy) {
            setDmPrivacy(result.preferences.dmPrivacy as 'everyone' | 'nobody');
          }
          
          // Load game settings and return
          const savedGameMode = localStorage.getItem('migoyugoGameMode');
          const savedTimerEnabled = localStorage.getItem('migoyugoTimerEnabled');
          const savedMinutesPerPlayer = localStorage.getItem('migoyugoMinutesPerPlayer');
          const savedIncrementSeconds = localStorage.getItem('migoyugoIncrementSeconds');
          const savedPlayerColorChoice = localStorage.getItem('migoyugoPlayerColorChoice');

          if (savedGameMode && ['local', 'ai-1', 'ai-2', 'ai-challenger', 'ai-skilled', 'ai-3', 'ai-engine', 'online'].includes(savedGameMode)) {
            setGameMode(savedGameMode as any);
          }
          if (savedTimerEnabled !== null) {
            setTimerEnabled(savedTimerEnabled === 'true');
          }
          if (savedMinutesPerPlayer) {
            setMinutesPerPlayer(parseInt(savedMinutesPerPlayer));
          }
          if (savedIncrementSeconds) {
            setIncrementSeconds(parseInt(savedIncrementSeconds));
          }
          if (savedPlayerColorChoice) {
            setPlayerColorChoice(savedPlayerColorChoice as any);
          }
          return;
        }
      } catch (error) {
        console.error('Error loading preferences from backend:', error);
        // Fall through to localStorage fallback
      }
    }
    
    // Fallback to localStorage (for guests or if backend fails)
    const savedTheme = localStorage.getItem('migoyugoTheme');
    if (savedTheme && ['classic', 'high-contrast', 'galaxy', 'neon', 'marble', 'wood', 'custom'].includes(savedTheme)) {
      setCurrentTheme(savedTheme);
    } else {
      setCurrentTheme('classic');
    }

    const savedGameSoundsEnabled = localStorage.getItem('migoyugoGameSoundsEnabled');
    const savedNotificationsEnabled = localStorage.getItem('migoyugoNotificationsEnabled');
    const savedSoundEnabled = localStorage.getItem('migoyugoSoundEnabled'); // Legacy support
    const savedCustomColors = localStorage.getItem('migoyugoCustomColors');

    // Load new preferences, or migrate from old soundEnabled if present
    if (savedGameSoundsEnabled !== null) {
      setGameSoundsEnabled(JSON.parse(savedGameSoundsEnabled));
    } else if (savedSoundEnabled !== null) {
      // Migration: if old format exists, use it for both preferences
      const oldValue = JSON.parse(savedSoundEnabled);
      setGameSoundsEnabled(oldValue);
      setNotificationsEnabled(oldValue);
      // Save in new format
      localStorage.setItem('migoyugoGameSoundsEnabled', JSON.stringify(oldValue));
      localStorage.setItem('migoyugoNotificationsEnabled', JSON.stringify(oldValue));
    }
    
    if (savedNotificationsEnabled !== null) {
      setNotificationsEnabled(JSON.parse(savedNotificationsEnabled));
    }
    const savedIllegalHoverEnabled = localStorage.getItem('migoyugoIllegalHoverEnabled');
    if (savedIllegalHoverEnabled !== null) {
      setIllegalHoverEnabled(JSON.parse(savedIllegalHoverEnabled));
    }
    if (savedCustomColors) {
      const parsedColors = JSON.parse(savedCustomColors);
      // Backward compatibility: if whiteYugo or blackYugo are missing, use yugoColor
      if (!parsedColors.whiteYugo && parsedColors.yugoColor) {
        parsedColors.whiteYugo = parsedColors.yugoColor;
      }
      if (!parsedColors.blackYugo && parsedColors.yugoColor) {
        parsedColors.blackYugo = parsedColors.yugoColor;
      }
      setCustomColors(parsedColors);
    }

    // Load game settings
    const savedGameMode = localStorage.getItem('migoyugoGameMode');
    const savedTimerEnabled = localStorage.getItem('migoyugoTimerEnabled');
    const savedMinutesPerPlayer = localStorage.getItem('migoyugoMinutesPerPlayer');
    const savedIncrementSeconds = localStorage.getItem('migoyugoIncrementSeconds');
    const savedPlayerColorChoice = localStorage.getItem('migoyugoPlayerColorChoice');

    if (savedGameMode && ['local', 'ai-1', 'ai-2', 'ai-challenger', 'ai-skilled', 'ai-3', 'ai-engine', 'online'].includes(savedGameMode)) {
      setGameMode(savedGameMode as any);
    }
    if (savedTimerEnabled !== null) {
      setTimerEnabled(savedTimerEnabled === 'true');
    }
    if (savedMinutesPerPlayer) {
      const minutes = parseInt(savedMinutesPerPlayer);
      if (!isNaN(minutes) && minutes > 0) {
        setMinutesPerPlayer(minutes);
      }
    }
    if (savedIncrementSeconds) {
      const increment = parseInt(savedIncrementSeconds);
      if (!isNaN(increment) && increment >= 0) {
        setIncrementSeconds(increment);
      }
    }
    if (savedPlayerColorChoice && ['white', 'black', 'random'].includes(savedPlayerColorChoice)) {
      setPlayerColorChoice(savedPlayerColorChoice as any);
    }
  };

  // Apply custom colors to CSS variables
  const applyCustomColors = useCallback((colors: typeof customColors) => {
    const root = document.documentElement;
    root.style.setProperty('--white-migo', colors.whiteMigo);
    root.style.setProperty('--black-migo', colors.blackMigo);
    root.style.setProperty('--yugo-color', colors.yugoColor);
    root.style.setProperty('--white-yugo', colors.whiteYugo);
    root.style.setProperty('--black-yugo', colors.blackYugo);
    root.style.setProperty('--board-color', colors.boardColor);
    root.style.setProperty('--hover-color', colors.hoverColor);
    root.style.setProperty('--last-move-color', colors.lastMoveColor);
  }, []);

  // Clear custom colors from CSS variables
  const clearCustomColors = useCallback(() => {
    const root = document.documentElement;
    root.style.removeProperty('--white-migo');
    root.style.removeProperty('--black-migo');
    root.style.removeProperty('--yugo-color');
    root.style.removeProperty('--white-yugo');
    root.style.removeProperty('--black-yugo');
    root.style.removeProperty('--board-color');
    root.style.removeProperty('--hover-color');
    root.style.removeProperty('--last-move-color');
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((theme: string) => {
    document.documentElement.setAttribute('data-theme', theme);
    
    // If it's a custom theme, apply custom colors
    if (theme === 'custom') {
      applyCustomColors(customColors);
    } else {
      // Clear custom colors when switching away from custom theme
      clearCustomColors();
    }
  }, [customColors, applyCustomColors, clearCustomColors]);


   // Apply theme when currentTheme changes
   useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme, applyTheme]);

  // Apply custom colors when they change (if using custom theme)
  useEffect(() => {
    if (currentTheme === 'custom') {
      applyCustomColors(customColors);
    }
  }, [customColors, currentTheme, applyCustomColors]);



  // Handle theme change
  const handleThemeChange = async (theme: string) => {
    setCurrentTheme(theme);
    localStorage.setItem('migoyugoTheme', theme);
    
    // If authenticated, auto-save to backend for cross-device sync
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        const preferences: any = { theme };
        if (theme === 'custom') {
          preferences.customColors = customColors;
        }
        const result = await ApiService.saveUserPreferences(preferences);
        if (!result.success) {
          showToast('Settings saved locally. Failed to sync to server: ' + result.error);
        }
      } catch (error) {
        console.error('Error saving theme preference:', error);
        showToast('Settings saved locally. Failed to sync to server.');
        // Continue - localStorage is already updated
      }
    }
  };

  // Handle game sounds toggle
  const handleGameSoundsToggle = async (enabled: boolean) => {
    setGameSoundsEnabled(enabled);
    localStorage.setItem('migoyugoGameSoundsEnabled', JSON.stringify(enabled));
    
    // If authenticated, also save to backend
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        await ApiService.saveUserPreferences({ gameSoundsEnabled: enabled });
      } catch (error) {
        console.error('Error saving game sounds preference:', error);
        // Continue silently - localStorage is already updated
      }
    }
  };

  // Handle notifications toggle
  const handleNotificationsToggle = async (enabled: boolean) => {
    setNotificationsEnabled(enabled);
    localStorage.setItem('migoyugoNotificationsEnabled', JSON.stringify(enabled));
    
    // If authenticated, also save to backend
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        await ApiService.saveUserPreferences({ notificationsEnabled: enabled });
      } catch (error) {
        console.error('Error saving notifications preference:', error);
      }
    }
  };

  const handleIllegalHoverToggle = (enabled: boolean) => {
    setIllegalHoverEnabled(enabled);
    localStorage.setItem('migoyugoIllegalHoverEnabled', JSON.stringify(enabled));
  };

  const handleDmPrivacyChange = async (value: 'everyone' | 'nobody') => {
    setDmPrivacy(value);
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        await ApiService.saveUserPreferences({ dmPrivacy: value });
      } catch (error) {
        console.error('Error saving DM privacy preference:', error);
      }
    }
  };

  // Handle custom color change
  const handleCustomColorChange = async (colorType: keyof typeof customColors, color: string) => {
    const newColors = { ...customColors, [colorType]: color };
    setCustomColors(newColors);
    localStorage.setItem('migoyugoCustomColors', JSON.stringify(newColors));
    
    // Immediately apply colors if using custom theme
    if (currentTheme === 'custom') {
      applyCustomColors(newColors);
    }
    
    // If authenticated and using custom theme, auto-save to backend for cross-device sync
    if (authState.isAuthenticated && !authState.isGuest && currentTheme === 'custom') {
      try {
        const result = await ApiService.saveUserPreferences({ 
          theme: 'custom',
          customColors: newColors 
        });
        if (!result.success) {
          showToast('Settings saved locally. Failed to sync to server: ' + result.error);
        }
      } catch (error) {
        console.error('Error saving custom colors preference:', error);
        showToast('Settings saved locally. Failed to sync to server.');
        // Continue - localStorage is already updated
      }
    }
  };

  // Set current theme as default
  const setAsDefault = async () => {
    // Save to localStorage (for immediate use and guest fallback)
    localStorage.setItem('migoyugoTheme', currentTheme);
    if (currentTheme === 'custom') {
      localStorage.setItem('migoyugoCustomColors', JSON.stringify(customColors));
    }
    
    // If authenticated, also save to backend
    if (authState.isAuthenticated && !authState.isGuest) {
      try {
        const preferences: any = { theme: currentTheme };
        if (currentTheme === 'custom') {
          preferences.customColors = customColors;
        }
        preferences.gameSoundsEnabled = gameSoundsEnabled;
        preferences.notificationsEnabled = notificationsEnabled;
        
        const result = await ApiService.saveUserPreferences(preferences);
        if (result.success) {
          showToast('Theme set as default. It will persist across devices and sessions.');
        } else {
          showToast('Theme saved locally. Failed to save to server: ' + result.error);
        }
      } catch (error) {
        console.error('Error saving preferences:', error);
        showToast('Theme saved locally. Failed to save to server.');
      }
    } else {
      showToast('Theme set as default. It will be applied when you reload the page.');
    }
  };

  // Reset settings to defaults
  const resetSettings = () => {
    // Reset state to default values
    setCurrentTheme('classic');
    setGameSoundsEnabled(true);
    setNotificationsEnabled(true);
    setIllegalHoverEnabled(DEFAULT_ILLEGAL_HOVER_ENABLED);
    setCustomColors({
      whiteMigo: '#ecf0f1',
      blackMigo: '#2c3e50',
      yugoColor: '#e74c3c',
      whiteYugo: '#e74c3c',
      blackYugo: '#e74c3c',
      boardColor: '#d1e6f9',
      hoverColor: '#a8c3e8',
      lastMoveColor: 'rgba(46, 204, 113, 0.2)'
    });
  
    // Remove from localStorage
    localStorage.removeItem('migoyugoTheme');
    localStorage.removeItem('migoyugoSoundEnabled');
    localStorage.removeItem('migoyugoGameSoundsEnabled');
    localStorage.removeItem('migoyugoNotificationsEnabled');
    localStorage.removeItem('migoyugoIllegalHoverEnabled');
    localStorage.removeItem('migoyugoCustomColors');
    
    // If authenticated, also clear backend preferences
    if (authState.isAuthenticated && !authState.isGuest) {
      ApiService.saveUserPreferences({ 
        theme: null, 
        customColors: null, 
        gameSoundsEnabled: true,
        notificationsEnabled: true 
      }).catch((error) => {
        console.error('Error resetting preferences on backend:', error);
        // Continue silently - localStorage is already cleared
      });
    }
  
    // Show confirmation
    showToast('Settings have been reset to classic theme.');
  };

  // Sound function - uses preloaded audio for instant playback (no lag)
  const playSound = useCallback((soundName: 'migo' | 'yugo' | 'igo' | 'wego') => {
    // Check if game sounds are enabled before playing
    if (!gameSoundsEnabled) {
      return;
    }
    
    try {
      // Get the preloaded audio object
      const preloadedAudio = preloadedSounds.current[soundName];
      const isReady = audioReady.current[soundName];
      
      if (preloadedAudio && isReady) {
        // Check readyState one more time before playing (for extra safety)
        if (preloadedAudio.readyState >= 2) {
          // Clone the preloaded audio to allow multiple simultaneous plays
          // This is instant since the file is already loaded and ready
          const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
          audio.volume = preloadedAudio.volume; // Volume is already set during preload
          // Play immediately - no await needed, fire and forget
          audio.play().catch(() => {
            // Silently fail - audio play errors are common on mobile and shouldn't break gameplay
          });
        } else {
          // Audio not ready yet, use fallback
          const audio = new Audio(`/sounds/${soundName}.mp3`);
          audio.volume = preloadedAudio.volume;
          audio.play().catch(() => {});
        }
      } else if (preloadedAudio) {
        // Audio object exists but not ready yet - try to play anyway (might work)
        const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
        audio.volume = preloadedAudio.volume;
        audio.play().catch(() => {});
      } else {
        // Fallback: if preload hasn't completed yet, create new audio
        const audio = new Audio(`/sounds/${soundName}.mp3`);
        if (soundName === 'igo') {
          audio.volume = 0.168;
        } else {
          audio.volume = 0.3;
        }
        audio.play().catch(() => {});
      }
    } catch (e) {
      // Silently fail - sound errors shouldn't break gameplay
    }
  }, [gameSoundsEnabled]);

  // Chat sound function - for send/receive message sounds
  const playChatSound = useCallback((soundName: 'sendMsg' | 'rcvMsg') => {
    // Check if notifications are enabled before playing
    if (!notificationsEnabled) {
      return;
    }
    
    try {
      // Get the preloaded audio object
      const preloadedAudio = preloadedSounds.current[soundName];
      const isReady = audioReady.current[soundName];
      
      if (preloadedAudio && isReady) {
        // Check readyState one more time before playing (for extra safety)
        if (preloadedAudio.readyState >= 2) {
          // Clone the preloaded audio to allow multiple simultaneous plays
          // This is instant since the file is already loaded and ready
          // Using cloneNode() is more reliable on mobile than creating new Audio instances
          const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
          audio.volume = preloadedAudio.volume; // Volume is already set during preload (0.5)
          audio.currentTime = 0; // Reset to start
          // Play immediately - fire and forget
          audio.play().catch(() => {});
        } else {
          // Audio not ready yet, use fallback
          const audio = new Audio(`/sounds/${soundName === 'sendMsg' ? 'send-msg' : 'rcv-msg'}.mp3`);
          audio.volume = preloadedAudio.volume;
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      } else if (preloadedAudio) {
        // Audio object exists but not ready yet - try to play anyway (might work)
        const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
        audio.volume = preloadedAudio.volume;
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else {
        // Fallback: if preload hasn't completed yet, create new audio
        const audio = new Audio(`/sounds/${soundName === 'sendMsg' ? 'send-msg' : 'rcv-msg'}.mp3`);
        audio.volume = 0.5;
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
    } catch (error) {
      // Silently fail - sound errors shouldn't break chat
    }
  }, [notificationsEnabled]);

    // PWA installation detection
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: any) => {
      console.log('PWA: beforeinstallprompt event fired');
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show our custom banner after a short delay
      setTimeout(() => {
        // Only show if not already installed and not dismissed recently
        const dismissed = localStorage.getItem('pwa-banner-dismissed');
        const dismissedTime = dismissed ? parseInt(dismissed) : 0;
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        
        if (!dismissedTime || dismissedTime < oneDayAgo) {
          setShowPWABanner(true);
        }
        }, 5000); // Show after 5 seconds on mobile (longer delay)
    };

    const handleAppInstalled = () => {
      console.log('PWA: App installed');
      setShowPWABanner(false);
      setDeferredPrompt(null);
    };

    // Enhanced mobile/iOS detection and debugging
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isStandalone = (window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    console.log('PWA: Device detection', {
      userAgent,
      isIOS,
      isStandalone,
      isMobile,
      maxTouchPoints: navigator.maxTouchPoints,
      platform: navigator.platform
    });
    
         // Show PWA banner for mobile devices (iOS or Android) that aren't already installed
    if (isMobile && !isStandalone) {
      console.log('PWA: Mobile device detected (not standalone), will show banner after 5 seconds');
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-banner-dismissed');
        const dismissedTime = dismissed ? parseInt(dismissed) : 0;
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        
        console.log('PWA: Checking if should show banner', {
          dismissed,
          dismissedTime,
          oneDayAgo,
          shouldShow: !dismissedTime || dismissedTime < oneDayAgo
        });
        
        if (!dismissedTime || dismissedTime < oneDayAgo) {
          console.log('PWA: Showing mobile banner');
          setShowPWABanner(true);
        }
      }, 5000);
    } else if (isMobile && isStandalone) {
      console.log('PWA: Running in standalone mode - no banner needed');
    } else if (!isMobile) {
      console.log('PWA: Desktop device - will rely on beforeinstallprompt event');
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // PWA banner actions
  const handleInstallPWA = async () => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    console.log('PWA: Install button clicked', { isIOS, deferredPrompt: !!deferredPrompt });
    
    if (isIOS) {
      // iOS doesn't support programmatic install, show instructions
      alert('To install: Tap the Share button in Safari, then select "Add to Home Screen"');
      setShowPWABanner(false);
      return;
    }
    
    if (!deferredPrompt) {
      console.log('PWA: No deferred prompt available - showing manual instructions');
      alert('To install: Use your browser menu to "Install App" or "Add to Home Screen"');
      setShowPWABanner(false);
      return;
    }
    
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA: User choice:', outcome);
      
      if (outcome === 'accepted') {
        setShowPWABanner(false);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('PWA: Error during install prompt', error);
      alert('To install: Use your browser menu to "Install App" or "Add to Home Screen"');
      setShowPWABanner(false);
    }
  };

  const dismissPWABanner = () => {
    setShowPWABanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  // Dynamic viewport height detection for mobile.
  // Uses visualViewport.height when available because window.innerHeight on
  // iOS Safari does not update when the browser address bar appears/disappears,
  // whereas visualViewport.height always reflects the truly visible area.
  useEffect(() => {
    const setVH = () => {
      const height = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
    };

    // Set initial value
    setVH();

    // visualViewport fires reliably on iOS Safari when the address bar
    // shows/hides; window resize handles desktop and orientation changes.
    window.addEventListener('resize', setVH);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVH);
    }

    const orientationHandler = () => setTimeout(setVH, 100);
    window.addEventListener('orientationchange', orientationHandler);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', orientationHandler);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setVH);
      }
    };
  }, []);


  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      // Check for API auth token
      const token = ApiService.getToken();
      if (token) {
        try {
          const result = await ApiService.getCurrentUser();
          if (result.user) {
            setAuthState({
              isAuthenticated: true,
              user: {
                id: result.user.id || result.user.user_id || '',
                username: result.user.username || 'User',
                email: result.user.email || '',
                stats: {
                  gamesPlayed: result.user.stats?.gamesPlayed || 0,
                  wins: result.user.stats?.wins || 0,
                  losses: result.user.stats?.losses || 0
                }
              },
              isGuest: false
            });
            
            // Check if user has an active game to reconnect to
            // This will trigger socket connection and potential reconnection
            setGameMode('online');
          } else {
            // Token is invalid, remove it
            ApiService.signOut();
            setAuthState({
              isAuthenticated: false,
              user: null,
              isGuest: false
            });
          }
        } catch (error) {
          console.error('Error checking auth state:', error);
          // Token might be invalid, remove it
          ApiService.signOut();
          setAuthState({
            isAuthenticated: false,
            user: null,
            isGuest: false
          });
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isGuest: false
        });
      }
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    trackVisit();
  }, [authState.isAuthenticated]);

  // Auth state changes are handled by token-based authentication
  // Token is checked on mount and when auth state changes

  // Helper functions for animation management
  const addNewDotAnimation = useCallback((row: number, col: number) => {
    // Don't animate in review mode
    if (isReviewMode) return;
    
    const cellKey = `${row}-${col}`;
    setNewlyPlacedDots(prev => new Set([...Array.from(prev), cellKey]));
    
    // Remove the animation class after 200ms (duration of bounce-in animation)
    setTimeout(() => {
      setNewlyPlacedDots(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }, 200);
  }, [isReviewMode]);

  const addFadeOutAnimation = useCallback((cellsToFade: {row: number, col: number}[]) => {
    // Don't animate in review mode
    if (isReviewMode) return;
    
    const cellKeys = cellsToFade.map(({row, col}) => `${row}-${col}`);
    setFadingDots(prev => new Set([...Array.from(prev), ...cellKeys]));
    
    // Remove the animation class after 200ms (duration of fade-out animation)
    setTimeout(() => {
      setFadingDots(prev => {
        const newSet = new Set(prev);
        cellKeys.forEach(key => newSet.delete(key));
        return newSet;
      });
    }, 200);
  }, [isReviewMode]);

  const showToast = useCallback((message: string, duration: number = 4000) => {
    setToast(message);
    setTimeout(() => setToast(''), duration);
  }, []);

  const showGameOverNotification = useCallback((title: string, message: string, delay: number = 1000) => {
    setTimeout(() => {
      setNotification({
        title,
        message,
        show: true
      });
    }, delay);
  }, []);

  const getCurrentWinnerName = useCallback(
    (winner: 'white' | 'black' | 'draw', mode: string = gameMode) =>
      getWinnerName(winner, mode, authStateRef.current, gameStateRef.current),
    [gameMode, getWinnerName]
  );

  // Initialize socket connection for authenticated users (persistent for challenges)
  // Also connect when gameMode === 'online' for matchmaking (including guests)
  // Guests also connect on Tournaments / while watching a tournament game
  // CRITICAL: Always require authentication before connecting to prevent race conditions
  // EXCEPTION: Guests can connect when gameMode === 'online' for quick match
  useEffect(() => {
    console.log('Socket effect triggered with gameMode:', gameMode, 'authState:', authStateRef.current);
    const token = localStorage.getItem('authToken');
    const currentAuthState = authStateRef.current;
    const hasToken = !!token;
    const isGuest = !hasToken && (!currentAuthState.isAuthenticated || currentAuthState.isGuest);
    // Require authentication for all socket connections (including online mode)
    // This prevents creating sockets before auth completes, which causes connection errors
    const isAuthenticated = hasToken && !isGuest && currentAuthState.isAuthenticated;
    const guestWantsLive =
      isGuest && (gameMode === 'online' || currentPage === 'tournaments' || spectatorMode);
    const shouldConnect = isAuthenticated || guestWantsLive;
    
    // CRITICAL: If socket already exists and is connected, don't recreate it
    // This prevents false disconnects when gameMode changes to 'online' during an active game
    if (shouldConnect && socket && socket.connected) {
      console.log('Socket already exists and is connected, skipping recreation:', socket.id);
      // Return early - cleanup will be handled by next effect run if shouldConnect becomes false
      return;
    }
    
    if (shouldConnect) {
      console.log('Creating socket connection...', {
        production: process.env.NODE_ENV === 'production',
        connectionURL: process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3002',
        hasToken,
        authState: currentAuthState,
        reason: gameMode === 'online' ? 'online game' : 'persistent for challenges',
        existingSocket: socket ? (socket.connected ? 'connected' : 'disconnected') : 'none'
      });
      
      // Determine socket URL based on environment variables
      const socketUrl =
  (process.env.REACT_APP_SOCKET_URL || '').trim() ||
  (process.env.NODE_ENV === 'production'
    ? 'https://api.migoyugo.com'
    : 'http://localhost:3002');
      
      console.log('Connecting to socket:', socketUrl, 'NODE_ENV:', process.env.NODE_ENV);
      
      console.log('Socket auth determination:', {
        hasToken,
        isAuthenticated: currentAuthState.isAuthenticated,
        isGuestFlag: currentAuthState.isGuest,
        finalIsGuest: isGuest,
        username: currentAuthState.user?.username
      });
      
      const newSocket = io(socketUrl, {
        auth: {
          token,
          isGuest,
          user: isGuest ? null : currentAuthState.user
        },
        // Production-optimized connection settings
        timeout: process.env.NODE_ENV === 'production' ? 10000 : 5000,
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: process.env.NODE_ENV === 'production' ? 10 : 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });
      setSocket(newSocket);

      // Leave YugoVision immediately (update refs sync) so late spectatorGameState
      // cannot leave the client stuck unable to click moves after becoming a player.
      const forceExitSpectatorMode = (reason?: string) => {
        const oldSpectatingId = spectatingGameIdRef.current;
        if (oldSpectatingId && newSocket.connected) {
          newSocket.emit('stopWatching', { gameId: oldSpectatingId });
        }
        if (reason) {
          console.log('[Spectator] forceExitSpectatorMode:', reason, 'wasWatching:', oldSpectatingId);
        }
        spectatorModeRef.current = false;
        spectatingGameIdRef.current = null;
        setSpectatorMode(false);
        setSpectatingGameId(null);
        setViewerCount(0);
        setSpectatorTournamentCtx(null);
      };

      newSocket.on('connect', () => {
        console.log('Socket connected successfully:', newSocket.id);
        console.log('Production environment:', process.env.NODE_ENV === 'production');
        
        // Handle reconnection for spectator mode (never while an active online player)
        const reconnectingAsPlayer =
          !!playerColorRef.current &&
          gameModeRef.current === 'online' &&
          isGameStartedRef.current &&
          !!gameIdRef.current;
        if (spectatorModeRef.current && spectatingGameIdRef.current && !reconnectingAsPlayer) {
          const spectatingId = spectatingGameIdRef.current;
          console.log('Reconnecting to spectator game:', spectatingId);
          newSocket.emit('watchGame', { gameId: spectatingId });
        } else if (reconnectingAsPlayer && spectatorModeRef.current) {
          forceExitSpectatorMode('connect-while-playing');
        }
        
        // Handle reconnection for spectator list page
        if (currentPageRef.current === 'spectator' && !spectatorModeRef.current) {
          console.log('Reconnecting to spectator lobby');
          newSocket.emit('joinSpectatorLobby');
        }
        
        const activeGameId = gameIdRef.current;
        // If we're in an online game, request timer sync immediately on connect
        // This ensures we get accurate server timer state after reconnection
        if (activeGameId && isGameStartedRef.current) {
          console.log('Requesting timer sync on socket connect');
          newSocket.emit('requestTimerSync', { gameId: activeGameId });
        }
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        if (process.env.NODE_ENV === 'production') {
          console.log('Retrying connection in production mode...');
        }
      });

      newSocket.on('disconnect', (reason) => {
        console.log('Socket disconnected:', reason);
        
        // Clear inGame flag on disconnect
        if (ApiService.isAuthenticated()) {
          ApiService.setInGame(false).catch(console.error);
        }
        
        // Clear challenge queue on disconnect to prevent stale challenges after reconnect
        // Don't clear currentChallenge here - let it persist if user reconnects quickly
        // The cleanup function will handle clearing everything if socket is fully closed
      });

      // Heartbeat/ping mechanism - keeps online status accurate
      // Send ping every 20 seconds to verify connection and update online status
      let pingInterval: NodeJS.Timeout | null = null;
      if (currentAuthState.isAuthenticated && !currentAuthState.isGuest) {
        pingInterval = setInterval(() => {
          if (newSocket.connected) {
            newSocket.emit('ping');
          }
        }, 20000); // Ping every 20 seconds
        
        // Listen for pong response (confirms server received ping)
        newSocket.on('pong', () => {
          // Connection is alive, online status is updated server-side
        });
      }
      
      // Clean up ping interval when socket disconnects
      newSocket.on('disconnect', () => {
        if (pingInterval) {
          clearInterval(pingInterval);
          pingInterval = null;
        }
      });

      newSocket.on('gameStart', (data) => {
        
        // Track when game started to suppress false positive reconnection messages
        gameStartTimeRef.current = Date.now();

        // Tournament ready-check must dismiss the instant the real game starts — otherwise
        // the overlay stays up while the server runs clocks and players appear "timed out".
        if (data.fromTournament) {
          if (tournamentRCTimerRef.current) {
            clearInterval(tournamentRCTimerRef.current);
            tournamentRCTimerRef.current = null;
          }
          setTournamentReadyCheck(null);
          setTournamentRCAccepted(false);
          setTournamentOpponentPeerReady(false);
          if (data.isPlayoff && data.playoffMessage) {
            showToast(data.playoffMessage, 8000);
          }
        }

        // Reset timer warning for the new game
        setTimerWarningShown({ white: false, black: false });
        timerWarningShownRef.current = { white: false, black: false };
        
        // Always leave YugoVision when becoming a player (refs updated sync).
        // Do not require spectator refs to already be set — a pending watch can
        // still deliver spectatorGameState after this and lock out moves.
        forceExitSpectatorMode('gameStart');

        isTournamentGameRef.current = !!data.fromTournament;
        setIsTournamentGame(!!data.fromTournament);
        
        setGameId(data.gameId);
        gameIdRef.current = data.gameId;
        setPlayerColor(data.playerColor);
        setOpponentName(data.opponentName);
        opponentNameRef.current = data.opponentName; // Update ref for closure access
        setOpponentDisconnected(false);
        setCurrentGameIsRated(data.isRated || false);
        setDisconnectedPlayerColor(null);
        setGameState(prev => ({
          ...prev,
          ...data.gameState,
          gameStatus: 'active',
          igoLine: null,      // Explicitly clear Igo highlight from previous game
          lastMove: null      // Explicitly clear last move highlight from previous game
        }));
      lastGameEndReasonRef.current = null;
        
        // Update inGame flag with gameId
        const currentUser = AuthService.getCurrentUser();
        if (currentUser && data.gameId) {
          ApiService.setInGame(true, data.gameId);
        }
        
        // Apply standard timer settings for online games
        if (data.timerSettings) {
          console.log('Applying standard timer settings from server:', data.timerSettings);
          setTimerEnabled(data.timerSettings.timerEnabled);
          setMinutesPerPlayer(data.timerSettings.minutesPerPlayer);
          setIncrementSeconds(data.timerSettings.incrementSeconds);
          
          // Initialize timers from server data
          if (data.timerSettings.timerEnabled && data.timers) {
            lastServerTimerUpdateRef.current = Date.now();
            setTimers(data.timers);
            
            // CRITICAL: Initialize timer refs immediately so timer starts right away
            const currentPlayer = data.gameState.currentPlayer as 'white' | 'black';
            const now = Date.now();
            timerStartTimeRef.current[currentPlayer] = now;
            timerInitialValuesRef.current[currentPlayer] = data.timers[currentPlayer];
            timerPausedTimeRef.current[currentPlayer] = 0;
            timerPauseStartRef.current[currentPlayer] = null;
            
            // Set active timer - this will trigger the timer useEffect to start counting
            setActiveTimer(currentPlayer);
            
            console.log('✅ Timer initialized immediately:', {
              currentPlayer,
              timerValue: data.timers[currentPlayer],
              startTime: now
            });
          }
        }
        
        setIsGameStarted(true);
        isGameStartedRef.current = true;
        setStartingPlayer('white'); // Online games always start with white
        
        // Clear previous game data when starting new game
        setMoveHistory([]);
        setBoardHistory([]);
        
        // Exit review mode if currently in review - completely move on from previous game
        setIsReviewMode(false);
        setCurrentReviewMove(0);
        setOriginalGameState(null);
        
        setShowMatchmaking(false);
        setIsSearchingMatch(false);
        
        // Clear room state when game starts (works for both room games and quick match)
        setCurrentRoom(null);
        
        // CRITICAL: Close ALL modals when game starts - ensure only game board is visible
        hideNotification({ resetContent: true });
        setShowPlayersModal(false);
        setShowStatisticsModal(false);
        setShowStatsAuth(false);
        setShowAboutModal(false);
        setShowLogin(false);
        setShowSignup(false);
        setAuthModalOpen(false);
        
        // Also close any open player profile inside the modal
        if ((window as any).__closePlayersPageModal) {
          (window as any).__closePlayersPageModal();
        }
        
        // CRITICAL: Set gameMode to 'online' FIRST (before navigation) so moves are sent via socket
        setGameMode('online');
        
        // Auto-navigate to game board immediately - works for challenges and other game starts
        // This ensures both players see the game board right away, regardless of what page they're on
        setCurrentPage('game');
        
        // CRITICAL: If this is from a challenge, clear all pending challenges when game starts
        // Decline all queued challenges so challengers are notified
        if (data.fromChallenge) {
          const queuedChallenges = challengeQueueRef.current.slice(); // Copy the array using ref
          if (queuedChallenges.length > 0) {
            console.log(`🎮 Game starting from challenge - declining ${queuedChallenges.length} queued challenges`);
            // AWAIT to ensure challenges are declined before clearing state
            Promise.all(
              queuedChallenges.map(challenge => 
                ApiService.declineChallenge(challenge.challengeId).catch(err => {
                  console.error(`Failed to decline queued challenge ${challenge.challengeId}:`, err);
                })
              )
            ).then(() => {
              // Clear queue after declining (don't block game start)
              setChallengeQueue([]);
            });
          } else {
            // Clear queue immediately if no queued challenges
            setChallengeQueue([]);
          }
          
          // Clear current challenge immediately
          setCurrentChallenge(null);
        }
        
        showToast(`Game start - you are playing as ${data.playerColor} (${data.timerSettings?.timerEnabled ? `${data.timerSettings.minutesPerPlayer}+${data.timerSettings.incrementSeconds}` : 'no timer'})`);
      });

      // Challenge received - show toast notification
      newSocket.on('challengeReceived', (data: { challengeId: number; challengerUsername: string; timerSettings?: { minutesPerPlayer: number; incrementSeconds: number }; isRated?: boolean }) => {
        console.log('📨 Challenge received:', data);
        
        // Only skip if already in an active *online* game (allow during bot/local so you can accept)
        if (
          isGameStartedRef.current &&
          gameStateRef.current.gameStatus === 'active' &&
          gameModeRef.current === 'online'
        ) {
          console.log('⏭️ Skipping challenge — player is in an active online game');
          return;
        }
        
        if (notificationsEnabled) {
          try {
            const preloadedAudio = preloadedSounds.current.challengeNotification;
            const isReady = audioReady.current.challengeNotification;
            
            if (preloadedAudio && isReady) {
              if (preloadedAudio.readyState >= 2) {
                const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
                audio.volume = preloadedAudio.volume;
                audio.currentTime = 0;
                audio.play().catch(() => {});
              } else {
                const audio = new Audio('/sounds/challenge-notification.mp3');
                audio.volume = 0.5;
                audio.play().catch(() => {});
              }
            } else if (preloadedAudio) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else {
              const audio = new Audio('/sounds/challenge-notification.mp3');
              audio.volume = 0.5;
              audio.play().catch(() => {});
            }
          } catch (e) {
            // Silently fail - sound errors shouldn't break gameplay
          }
        }
        
        // NEW RULE: Only one challenge can be considered at a time
        // If there's already a challenge showing, this shouldn't happen (backend prevents it)
        // But handle it gracefully just in case
        if (currentChallengeRef.current) {
          console.warn('⚠️ Received challenge while one is already showing - this should not happen. Replacing current challenge.');
          // Replace the current challenge (backend should prevent this, but handle edge cases)
          setCurrentChallenge(data);
          // Clear queue since we're replacing
          setChallengeQueue([]);
        } else {
          // No current challenge, so set this one as current
          console.log('✅ Setting currentChallenge:', data);
          setCurrentChallenge(data);
        }
      });

      // Challenge cancelled - notify challenged player that challenger accepted another game
      newSocket.on('challengeCancelled', (data: { challengeId: number; challengerUsername: string; reason: string }) => {
        console.log('📨 Challenge cancelled notification:', data);
        
        // Remove from current challenge if it matches
        if (currentChallengeRef.current?.challengeId === data.challengeId) {
          console.log('🗑️ Removing cancelled challenge from current challenge');
          setCurrentChallenge(null);
        }
        
        // Remove from queue if it's there
        setChallengeQueue(prev => prev.filter(c => c.challengeId !== data.challengeId));
        
        // Show a brief toast notification
        showToast(`${data.challengerUsername} accepted another game. Challenge cancelled.`, 3000);
      });

      // Challenge declined - notify challenger (toast always; Players also updates the profile button)
      newSocket.on('challengeDeclined', (data: { challengeId: number; challengedUsername: string; reason: string }) => {
        console.log('📨 Challenge declined notification:', data);
        
        // Play challenge declined sound using preloaded audio (same pattern as challengeNotification)
        if (notificationsEnabled) {
          try {
            const preloadedAudio = preloadedSounds.current.challengeDeclined;
            const isReady = audioReady.current.challengeDeclined;
            
            if (preloadedAudio && isReady) {
              // Check readyState one more time before playing (for extra safety)
              if (preloadedAudio.readyState >= 2) {
                // Clone the preloaded audio to allow multiple simultaneous plays
                // This is instant since the file is already loaded and ready
                // Using cloneNode() is more reliable on mobile than creating new Audio instances
                const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
                audio.volume = preloadedAudio.volume; // Volume is already set during preload (0.5)
                audio.currentTime = 0; // Reset to start
                // Play immediately - fire and forget
                audio.play().catch(() => {});
              } else {
                const audio = new Audio('/sounds/challenge-declined.mp3');
                audio.volume = 0.5;
                audio.currentTime = 0;
                audio.play().catch(() => {});
              }
            } else if (preloadedAudio) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else {
              const audio = new Audio('/sounds/challenge-declined.mp3');
              audio.volume = 0.5;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            }
          } catch (error) {
            // Silently fail - sound errors shouldn't break gameplay
          }
        }
        
        // Always show the declined toast, including while Players is open.
        // Players also updates the profile button; the toast was previously suppressed
        // there and then sat behind the overlay, so the challenger often saw nothing.
        setChallengeDeclinedToast({
          challengedUsername: data.challengedUsername,
          reason: data.reason === 'timeout' ? 'timeout' : 'declined'
        });
      });

      newSocket.on('waitingForOpponent', () => {
        setIsSearchingMatch(true);
      });

      newSocket.on('matchmakingError', (data) => {
        console.log('Matchmaking error:', data);
        setIsSearchingMatch(false);
        setShowMatchmaking(false);
        
        if (data.error === 'email_not_verified') {
          showToast('Please verify your email address before playing online games. Check your inbox for the verification email.', 8000);
        } else {
          showToast(data.message || 'Matchmaking failed. Please try again.', 5000);
        }
      });

      newSocket.on('timerUpdate', (data) => {
        // Check if this is a spectator lobby update (has gameId and we're on spectator list page)
        const isOnSpectatorListPage = currentPageRef.current === 'spectator';
        
        if (data.gameId && isOnSpectatorListPage && !spectatorModeRef.current) {
          // Update spectator timers for the game list
          setSpectatorTimers(prev => ({
            ...prev,
            [data.gameId]: {
              white: data.timers?.white || prev[data.gameId]?.white || 600,
              black: data.timers?.black || prev[data.gameId]?.black || 600,
              activePlayer: data.activeTimer || prev[data.gameId]?.activePlayer || null
            }
          }));
          return; // Don't process as game room update
        }
        
        // Check if in spectator mode - if so, process events with gameId
        if (spectatorModeRef.current) {
          // For spectators, check if this update is for the game we're watching
          if (!shouldProcessServerTimer(data?.gameId ?? null)) {
            console.log('Ignoring timerUpdate for mismatched spectator game', {
              payloadGameId: data?.gameId ?? null,
              spectatingGameId: spectatingGameIdRef.current
            });
            return;
          }
        } else {
          // Player mode: accept room ticks that match our active game (server always sends gameId)
          if (!shouldProcessServerTimer(data?.gameId ?? null)) {
            console.log('Ignoring timerUpdate for inactive or mismatched game', {
              payloadGameId: data?.gameId ?? null,
              activeGameId: gameIdRef.current,
              isGameStarted: isGameStartedRef.current
            });
            return;
          }
        }

        // For online games, server is the single source of truth
        // Replace any local/cached timer value with server value
        if (data.timestamp) {
          const timeDiff = Date.now() - data.timestamp;
          // If more than 3 seconds difference, request sync
          if (timeDiff > 3000) {
            console.log('Timer sync issue detected, requesting sync...');
            if (gameIdRef.current) {
              newSocket.emit('requestTimerSync', { gameId: gameIdRef.current });
            }
            return;
          }
        }
        
        // Record that we received a server update
        lastServerTimerUpdateRef.current = Date.now();
        
        // Server value ALWAYS takes precedence - replace any cached value
        setTimers(data.timers);
        setActiveTimer(data.activeTimer);

        // Check for 20-second timer warning (online games)
        const activeColor = data.activeTimer as 'white' | 'black' | null;
        if (activeColor && data.timers[activeColor] <= 20 && data.timers[activeColor] > 0 && !timerWarningShownRef.current[activeColor]) {
          timerWarningShownRef.current = { ...timerWarningShownRef.current, [activeColor]: true };
          handleTimerWarning(activeColor);
        }
      });

      newSocket.on('timerSync', (data) => {
        if (!shouldProcessServerTimer(data?.gameId ?? null)) {
          console.log('Ignoring timerSync for inactive or mismatched game', {
            payloadGameId: data?.gameId ?? null,
            activeGameId: gameIdRef.current,
            isGameStarted: isGameStartedRef.current
          });
          return;
        }

        // Record that we received a server sync
        lastServerTimerUpdateRef.current = Date.now();
        
        // Server value ALWAYS replaces any cached/local timer value
        // This is critical for accurate timer after reconnection
        setTimers(data.timers);
        setActiveTimer(data.activeTimer);

        // Check for 20-second timer warning (online games)
        const syncActiveColor = data.activeTimer as 'white' | 'black' | null;
        if (syncActiveColor && data.timers[syncActiveColor] <= 20 && data.timers[syncActiveColor] > 0 && !timerWarningShownRef.current[syncActiveColor]) {
          timerWarningShownRef.current = { ...timerWarningShownRef.current, [syncActiveColor]: true };
          handleTimerWarning(syncActiveColor);
        }
      });

      newSocket.on('moveError', (data) => {
        console.error('Move error received:', data);
        showToast(data.message || 'Illegal move attempted');
        
        // Play illegal move sound (only if game sounds are enabled)
        if (gameSoundsEnabled) {
          try {
            const preloadedAudio = preloadedSounds.current.illegalMove;
            const isReady = audioReady.current.illegalMove;
            if (preloadedAudio && (isReady || preloadedAudio.readyState >= 2)) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else if (preloadedAudio) {
              const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
              audio.volume = preloadedAudio.volume;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            } else {
              const audio = new Audio('/sounds/illegal-move.mp3');
              audio.volume = 0.5;
              audio.currentTime = 0;
              audio.play().catch(() => {});
            }
          } catch (e) {
            // Silently fail - sound errors shouldn't break gameplay
          }
        }
        
        // If this was our move that failed, we need to handle it
        // For human players, just show the error (move already prevented)
        // For AI in online games (future), we might need to retry
        
        // Note: This primarily helps with debugging and user feedback
        // The move should already be prevented on client side before sending
      });


      newSocket.on('moveUpdate', (moveData) => {
        // Check if this is a spectator lobby update (has gameId and we're on spectator list page)
        const isOnSpectatorListPage = currentPageRef.current === 'spectator';
        
        if (moveData.gameId && isOnSpectatorListPage && !spectatorModeRef.current) {
          // Update move count for games in the spectator list
          // The SpectatorListPage will handle updating the display via spectatorGamesList state
          // For now, we just let the periodic refresh handle it
          // In the future, we could update spectatorGamesList directly here
          return; // Don't process as game room update
        }
        
        // Check if in spectator mode or online player mode
        const isSpectator = spectatorModeRef.current;
        const isOnlinePlayer = gameModeRef.current === 'online';
        
        if (isSpectator || isOnlinePlayer) {
          if (!shouldProcessServerTimer(moveData?.gameId ?? null)) {
            console.log('Ignoring moveUpdate for inactive or mismatched game', {
              payloadGameId: moveData?.gameId ?? null,
              activeGameId: gameIdRef.current,
              spectatingGameId: spectatingGameIdRef.current,
              isGameStarted: isGameStartedRef.current,
              gameMode: gameModeRef.current,
              isSpectator
            });
            return;
          }
        }

        // Check if user is in review mode - handle opponent moves differently
        if (isReviewModeRef.current) {
          console.log('⚠️ [REVIEW] Opponent moved during review mode - updating originalGameState to prevent freeze');
          
          // Update originalGameState to include the new move (so exitReviewMode restores current state)
          setOriginalGameState(prev => {
            if (!prev) {
              // If no originalGameState, get current gameState from ref
              const currentState = gameStateRef.current;
              return {
                ...currentState,
                board: moveData.board,
                currentPlayer: moveData.currentPlayer,
                scores: moveData.scores,
                lastMove: { row: moveData.row, col: moveData.col, player: moveData.player },
                gameStatus: moveData.gameOver ? 'finished' : 'active',
                igoLine: moveData.igo || null
              };
            }
            return {
              ...prev,
              board: moveData.board,
              currentPlayer: moveData.currentPlayer,
              scores: moveData.scores,
              lastMove: { row: moveData.row, col: moveData.col, player: moveData.player },
              gameStatus: moveData.gameOver ? 'finished' : 'active',
              igoLine: moveData.igo || null
            };
          });
          
          // Still update moveHistory (safe - just adds to end, review can navigate to it)
          setMoveHistory(prev => [
            ...prev,
            {
              row: moveData.row,
              col: moveData.col,
              player: moveData.player,
              yugos: moveData.yugos,
              moveNumber: prev.length + 1
            }
          ]);
          
          // Still update timers (important for game state and clock display)
          if (moveData.timers) {
            if (moveData.timestamp) {
              const timeDiff = Date.now() - moveData.timestamp;
              if (timeDiff > 3000) {
                console.log('Move timer sync issue detected, requesting sync...');
                if (gameIdRef.current) {
                  newSocket.emit('requestTimerSync', { gameId: gameIdRef.current });
                }
              }
            }
            lastServerTimerUpdateRef.current = Date.now();
            setTimers(moveData.timers);

            // Check for 20-second timer warning (online games)
            const moveActiveColor = moveData.currentPlayer as 'white' | 'black' | null;
            if (!moveData.gameOver && moveActiveColor && moveData.timers[moveActiveColor] <= 20 && moveData.timers[moveActiveColor] > 0 && !timerWarningShownRef.current[moveActiveColor]) {
              timerWarningShownRef.current = { ...timerWarningShownRef.current, [moveActiveColor]: true };
              handleTimerWarning(moveActiveColor);
            }
          }
          
          // If game ended, exit review mode automatically and handle game over
          if (moveData.gameOver) {
            console.log('⚠️ [REVIEW] Game ended during review - exiting review mode');
            setIsReviewMode(false);
            setIsImportReviewMode(false);
            setCurrentReviewMove(0);
            isReviewModeRef.current = false;
            
            // Handle game over (same logic as below)
            let message = '';
            if (moveData.winner === 'draw') {
              if (moveData.reason === 'wego') {
                if (gameSoundsEnabled) {
                  playSound('wego');
                }
                const whiteYugos = moveData.scores?.white || 0;
                const blackYugos = moveData.scores?.black || 0;
                message = `Wego! No legal moves remain.\nDraw - Yugos tied at ${whiteYugos}`;
              } else {
                message = 'Game ended in a draw!';
              }
            } else if (moveData.igo) {
              const winnerName = getCurrentWinnerName(moveData.winner, 'online');
              message = `${winnerName} wins with an Igo!`;
            } else {
              const winnerName = getCurrentWinnerName(moveData.winner, 'online');
              if (moveData.reason === 'wego') {
                if (gameSoundsEnabled) {
                  playSound('wego');
                }
                const whiteYugos = moveData.scores?.white || 0;
                const blackYugos = moveData.scores?.black || 0;
                const winnerYugos = moveData.winner === 'white' ? whiteYugos : blackYugos;
                const loserYugos = moveData.winner === 'white' ? blackYugos : whiteYugos;
                message = `Wego! No legal moves remain.\n${winnerName} wins ${winnerYugos} Yugos to ${loserYugos}`;
              } else {
                message = `${winnerName} wins by yugo count!`;
              }
            }
            
            // Restore game state from originalGameState (which now includes the final move)
            setOriginalGameState(prev => {
              if (prev) {
                setGameState(prev);
              }
              return null;
            });
            
            // Clear inGame flag when game ends
            if (ApiService.isAuthenticated()) {
              ApiService.setInGame(false).catch(console.error);
            }
            
            // Record game result
            const currentPlayerColor = playerColorRef.current;
            const currentAuthState = authStateRef.current;
            recordGameEnd(moveData.winner, 'moveUpdate - game over');
            
            showGameOverNotification('Game Over', message);
            setActiveTimer(null);
          } else {
            // Game still active - just update active timer
            setActiveTimer(moveData.currentPlayer);
          }
          
          // Don't update current gameState - keep review state intact
          // User will see updated state when they exit review mode
          return;
        }

        // Play appropriate sound effects (only if game sounds are enabled)
        if (gameSoundsEnabled) {
          if (moveData.gameOver && moveData.igo) {
            playSound('igo'); // Igo formed
          } else if (moveData.yugos > 0) {
            playSound('yugo'); // Yugo formed
          } else {
            playSound('migo'); // Regular migo placement
          }
        }

        // Trigger animations for online moves
        setGameState(prev => {
          // Calculate removed dots by comparing old and new boards
          if (moveData.yugos > 0) {
            const removedCells: {row: number, col: number}[] = [];
            for (let row = 0; row < 8; row++) {
              for (let col = 0; col < 8; col++) {
                // If there was a migo here before but not now (except the new placement)
                if (prev.board[row][col] && !moveData.board[row][col] && 
                    !(row === moveData.row && col === moveData.col)) {
                  removedCells.push({row, col});
                }
              }
            }
            if (removedCells.length > 0) {
              addFadeOutAnimation(removedCells);
            }
          }
          
          // Trigger bounce-in animation for newly placed dot
          addNewDotAnimation(moveData.row, moveData.col);

          return {
            ...prev,
            board: moveData.board,
            currentPlayer: moveData.currentPlayer,
            scores: moveData.scores,
            lastMove: { row: moveData.row, col: moveData.col, player: moveData.player },
            gameStatus: moveData.gameOver ? 'finished' : 'active',
            igoLine: moveData.igo || null
          };
        });

        // Add to move history
        setMoveHistory(prev => [
          ...prev,
          {
            row: moveData.row,
            col: moveData.col,
            player: moveData.player,
            yugos: moveData.yugos,
            moveNumber: prev.length + 1
          }
        ]);

        // Update timers from server
        if (moveData.timers) {
          // Validate timer sync for move updates too
          if (moveData.timestamp) {
            const timeDiff = Date.now() - moveData.timestamp;
            if (timeDiff > 3000) {
              console.log('Move timer sync issue detected, requesting sync...');
              if (gameIdRef.current) {
                newSocket.emit('requestTimerSync', { gameId: gameIdRef.current });
              }
            }
          }
          lastServerTimerUpdateRef.current = Date.now();
          setTimers(moveData.timers);

          // Check for 20-second timer warning (online games)
          const moveActiveColor2 = moveData.currentPlayer as 'white' | 'black' | null;
          if (!moveData.gameOver && moveActiveColor2 && moveData.timers[moveActiveColor2] <= 20 && moveData.timers[moveActiveColor2] > 0 && !timerWarningShownRef.current[moveActiveColor2]) {
            timerWarningShownRef.current = { ...timerWarningShownRef.current, [moveActiveColor2]: true };
            handleTimerWarning(moveActiveColor2);
          }
        }

        if (moveData.gameOver) {
          let message = '';
          if (moveData.winner === 'draw') {
            // Check if it's a Wego draw
            if (moveData.reason === 'wego') {
              if (gameSoundsEnabled) {
                playSound('wego'); // Wego occurred
              }
              const whiteYugos = moveData.scores?.white || 0;
              const blackYugos = moveData.scores?.black || 0;
              message = `Wego! No legal moves remain.\nDraw - Yugos tied at ${whiteYugos}`;
            } else {
              message = 'Game ended in a draw!';
            }
          } else if (moveData.igo) {
            const winnerName = getCurrentWinnerName(moveData.winner, 'online');
            message = `${winnerName} wins with an Igo!`;
          } else {
            const winnerName = getCurrentWinnerName(moveData.winner, 'online');
            // Check if it's a Wego win
            if (moveData.reason === 'wego') {
              if (gameSoundsEnabled) {
                playSound('wego'); // Wego occurred
              }
              const whiteYugos = moveData.scores?.white || 0;
              const blackYugos = moveData.scores?.black || 0;
              const winnerYugos = moveData.winner === 'white' ? whiteYugos : blackYugos;
              const loserYugos = moveData.winner === 'white' ? blackYugos : whiteYugos;
              message = `Wego! No legal moves remain.\n${winnerName} wins ${winnerYugos} Yugos to ${loserYugos}`;
            } else {
              message = `${winnerName} wins by yugo count!`;
            }
          }
          
          // Clear inGame flag when game ends
          if (ApiService.isAuthenticated()) {
            ApiService.setInGame(false).catch(console.error);
          }
          
          // Record game result for authenticated users
          const currentPlayerColor = playerColorRef.current;
          const currentAuthState = authStateRef.current;
          console.log('🔍 Checking auth state for recording:', { 
            isAuthenticated: currentAuthState.isAuthenticated,
            hasUser: !!currentAuthState.user,
            userId: currentAuthState.user?.id,
            winner: moveData.winner,
            playerColor: currentPlayerColor
          });
          
          // Record game result using centralized function
          recordGameEnd(moveData.winner, 'moveUpdate - game over');
          
          // Add 1 second delay for players to see the final move
          showGameOverNotification('Game Over', message);
          setActiveTimer(null);
        } else {
          setActiveTimer(moveData.currentPlayer);
        }
      }); 
      newSocket.on('gameEnd', (data) => {
        const isSpectator = spectatorModeRef.current;
        const isOnSpectatorListPage = currentPage === 'spectator';
        
        // If on spectator list page and game ended, remove it from the list
        if (data.gameId && isOnSpectatorListPage && !isSpectator) {
          setSpectatorGamesList(prev => prev.filter(game => game.gameId !== data.gameId));
          // Also remove from spectator timers
          setSpectatorTimers(prev => {
            const updated = { ...prev };
            delete updated[data.gameId];
            return updated;
          });
          return; // Don't process as game room update
        }
        
        // Check if this game end is for the game we're watching (if spectator)
        if (isSpectator && data.gameId && data.gameId !== spectatingGameIdRef.current) {
          console.log('Ignoring gameEnd for different game:', data.gameId);
          return;
        }
        
        // Regular game end handling
        // Update timers FIRST (before gameStatus) to ensure timer display updates immediately
        // This prevents timeout message appearing before timer shows 0
        if (data.timers) {
          setTimers(data.timers);
        }
        
        setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
        setActiveTimer(null);
        // Keep isTournamentGame true after finish so rematch UI stays suppressed
        
        // Clear disconnect modal state when game ends (only for players)
        if (!isSpectator) {
          setOpponentDisconnected(false);
          setDisconnectedPlayerColor(null);
          setGracePeriodSeconds(30);
          if (gracePeriodIntervalRef.current) {
            clearInterval(gracePeriodIntervalRef.current);
            gracePeriodIntervalRef.current = null;
          }
          
          // Clear game start time ref when game ends
          gameStartTimeRef.current = null;
          
          // Clear inGame flag when game ends (only for players)
          const currentUser = AuthService.getCurrentUser();
          if (currentUser) {
            ApiService.setInGame(false);
          }
        }
        
        // Reset logic only applies to players, not spectators
        if (!isSpectator) {
          const isReset = data.reason === 'reset';
          lastGameEndReasonRef.current = isReset ? 'reset' : null;
          if (isReset) {
            const resetBy = data.resetBy;
            const myColor = playerColorRef.current;
            pendingResetForfeitureRef.current = myColor && resetBy && myColor === resetBy ? 'self' : 'opponent';
          } else {
            pendingResetForfeitureRef.current = null;
          }
        }

        // Add 1 second delay for players to see the final move the yugo  system according to the system ytoy weare  working io the project shees ikram 
        setTimeout(() => {
          let title = 'Game Over';
          let message = '';
          const playerColor = playerColorRef.current;
          
          // CRITICAL: Use player names from gameEnd event if available (most reliable)
          // Fallback to gameState only if event doesn't include names (backward compatibility)
          const whitePlayerName = data.players?.white || gameStateRef.current?.players?.white || 'White';
          const blackPlayerName = data.players?.black || gameStateRef.current?.players?.black || 'Black';
          
          // Determine winner name from event player names (ensures correctness)
          const winnerName = data.winner === 'white' ? whitePlayerName : (data.winner === 'black' ? blackPlayerName : 'Draw');
          const resetByName = data.resetByName || 'Opponent';

          if (data.reason === 'draw') {
            title = 'Game Drawn';
            message = 'The game has ended in a draw by mutual agreement.';
          } else if (data.reason === 'resignation') {
            message = `${winnerName} wins by resignation!`;
          } else if (data.reason === 'timeout') {
            title = 'Time Out';
            if (isSpectator) {
              // For spectators, specify which player ran out of time
              const loserColor = data.winner === 'white' ? 'black' : 'white';
              const loserName = loserColor === 'white' ? whitePlayerName : blackPlayerName;
              message = `${loserName} ran out of time.\n${winnerName} wins!`;
            } else {
              message = `${winnerName} wins on time!`;
            }
          } else if (data.reason === 'reset') {
            title = 'Game Reset';
            if (isSpectator) {
              // For spectators, always use neutral language
              message = `${resetByName} reset the board.\n${winnerName} wins by default.`;
            } else {
              const resetBy = data.resetBy;
              if (playerColor && resetBy && playerColor === resetBy) {
                message = 'You reset the board. Your opponent wins by default.';
              } else if (playerColor && resetBy && playerColor === data.winner) {
                message = `${resetByName} reset the board — you win by default!`;
              } else {
                message = `${resetByName} reset the board. ${winnerName} wins by default.`;
              }
            }
          } else if (data.reason === 'disconnection' && endingGameDueToSignInRef.current) {
            // Custom message for user who signed in during game
            title = 'Game Abandoned';
            message = 'The game has ended because you signed in during a guest game. Members can only play against other members.';
            endingGameDueToSignInRef.current = false; // Clear flag
          } else if (data.reason === 'disconnection') {
            if (isSpectator) {
              // For spectators, specify which player disconnected
              const disconnectedColor = data.winner === 'white' ? 'black' : 'white';
              const disconnectedName = disconnectedColor === 'white' ? whitePlayerName : blackPlayerName;
              message = `${disconnectedName} disconnected.\n${winnerName} wins!`;
            } else {
              // Opponent disconnected - check if current player won
              if (playerColor && playerColor === data.winner) {
                // Current player won due to opponent disconnection - play end-game sound (only if game sounds are enabled)
                if (gameSoundsEnabled) {
                  try {
                    const preloadedAudio = preloadedSounds.current.endGame;
                    const isReady = audioReady.current.endGame;
                    if (preloadedAudio && isReady && preloadedAudio.readyState >= 2) {
                      const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
                      audio.volume = preloadedAudio.volume;
                      audio.currentTime = 0;
                      audio.play().catch(() => {});
                    } else if (preloadedAudio) {
                      const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
                      audio.volume = preloadedAudio.volume;
                      audio.currentTime = 0;
                      audio.play().catch(() => {});
                    } else {
                      const audio = new Audio('/sounds/end-game.mp3');
                      audio.volume = 0.5;
                      audio.currentTime = 0;
                      audio.play().catch(() => {});
                    }
                  } catch (e) {
                    // Silently fail - sound errors shouldn't break gameplay
                  }
                }
              }
              message = `${winnerName} wins!`;
            }
          } else if (data.reason === 'opponent_joined_tournament') {
            message = `${data.opponentName || winnerName} left to join a tournament match.\n${winnerName} wins!`;
          } else if (data.reason === 'igo') {
            message = `${winnerName} wins with an Igo!`;
          } else if (data.reason === 'wego') {
            const scores = gameStateRef.current?.scores || { white: 0, black: 0 };
            const whiteYugos = scores.white || 0;
            const blackYugos = scores.black || 0;
            if (data.winner === 'draw') {
              message = `Wego! No legal moves remain.\nDraw - Yugos tied at ${whiteYugos}`;
            } else {
              const winnerYugos = data.winner === 'white' ? whiteYugos : blackYugos;
              const loserYugos = data.winner === 'white' ? blackYugos : whiteYugos;
              message = `Wego! No legal moves remain.\n${winnerName} wins ${winnerYugos} Yugos to ${loserYugos}`;
            }
          } else {
            message = `${winnerName} wins!`;
          }
          
          if (ELO_VISIBLE && data.eloResult) {
            const elo = data.eloResult;
            const whiteLine = formatEloResultLine(
              whitePlayerName,
              elo.white.change,
              elo.white.newRating,
              elo.white.isProvisional
            );
            const blackLine = formatEloResultLine(
              blackPlayerName,
              elo.black.change,
              elo.black.newRating,
              elo.black.isProvisional
            );
            message += data.winner === 'black'
              ? `\n\n${blackLine}\n${whiteLine}`
              : `\n\n${whiteLine}\n${blackLine}`;
          }
          
          if (isSpectator) {
            const hasMoveHistory = moveHistoryRef.current.length > 0;
            setNotification({
              title,
              message,
              show: true,
              primaryButton: hasMoveHistory ? 'Review Game' : undefined,
              secondaryButton: 'Back to List',
              onPrimary: hasMoveHistory ? () => {
                hideNotification({ preserveBoard: true });
                enterReviewMode();
              } : undefined,
              onSecondary: () => {
                stopWatching();
                setCurrentPage('spectator');
                localStorage.setItem('mig_current_page', 'spectator');
                hideNotification();
              }
            });
          } else {
            setNotification({
              title,
              message,
              show: true
            });
          }
          
          // Auto-hide disconnect modal 5 seconds after game ends (if it's still showing)
          setTimeout(() => {
            setOpponentDisconnected(false);
            setDisconnectedPlayerColor(null);
            setGracePeriodSeconds(30);
            if (gracePeriodIntervalRef.current) {
              clearInterval(gracePeriodIntervalRef.current);
              gracePeriodIntervalRef.current = null;
            }
          }, 5000); // 5 seconds after game end notification
        }, 1000);
      });

      // Spectator mode event handlers
      newSocket.on('spectatorGameState', (data) => {
        console.log('Spectator game state received:', data);

        // Never enter/stay in YugoVision while playing an online/tournament game —
        // a late watch ack after gameStart was leaving clicks permanently blocked.
        const isActiveOnlinePlayer =
          !!playerColorRef.current &&
          gameModeRef.current === 'online' &&
          isGameStartedRef.current &&
          !!gameIdRef.current;
        if (isActiveOnlinePlayer) {
          console.log('[Spectator] Ignoring spectatorGameState — active online player in', gameIdRef.current);
          if (data.gameId && newSocket.connected) {
            newSocket.emit('stopWatching', { gameId: data.gameId });
          }
          forceExitSpectatorMode('spectatorGameState-while-playing');
          return;
        }
        
        // Set spectator mode
        spectatorModeRef.current = true;
        spectatingGameIdRef.current = data.gameId;
        setSpectatorMode(true);
        setSpectatingGameId(data.gameId);
        
        // Ensure we're not in review mode or import review mode when joining as spectator
        setIsReviewMode(false);
        setIsImportReviewMode(false);
        setCurrentReviewMove(0);
        setOriginalGameState(null);
        
        // Initialize game state from spectator data
        setGameState(prev => ({
          ...prev,
          board: data.board || prev.board,
          currentPlayer: data.currentPlayer || prev.currentPlayer,
          scores: data.scores || prev.scores,
          players: {
            white: data.players?.white?.name || prev.players.white,
            black: data.players?.black?.name || prev.players.black
          },
          gameStatus: data.gameStatus || 'active',
          lastMove: data.lastMove || null,
          igoLine: null
        }));
        
        // Initialize move history
        if (data.moveHistory && Array.isArray(data.moveHistory)) {
          setMoveHistory(data.moveHistory);
        }
        
        // Initialize timers from the watched game only
        if (data.timers) {
          setTimers(data.timers);
        }
        if (data.currentPlayer === 'white' || data.currentPlayer === 'black') {
          setActiveTimer(data.currentPlayer);
        }
        
        // Set timer settings if provided
        if (data.timerSettings) {
          setTimerEnabled(data.timerSettings.timerEnabled !== false);
          setMinutesPerPlayer(data.timerSettings.minutesPerPlayer || 10);
          setIncrementSeconds(data.timerSettings.incrementSeconds || 0);
        }
        
        // Set game ID for reference
        setGameId(data.gameId);
        gameIdRef.current = data.gameId;
        
        // Mark game as started so move history and chat are visible
        setIsGameStarted(true);
        isGameStartedRef.current = true;
        
        // Set viewer count if provided
        if (data.spectatorCount !== undefined) {
          setViewerCount(data.spectatorCount);
        }
        
        console.log('Spectator mode activated for game:', data.gameId);
      });

      newSocket.on('spectatorError', (data) => {
        console.error('Spectator error:', data);
        const errorMessage = data.message || 'An error occurred while trying to watch the game';
        showToast(errorMessage);
        
        // Clear spectator state on error (refs sync)
        spectatorModeRef.current = false;
        spectatingGameIdRef.current = null;
        setSpectatorMode(false);
        setSpectatingGameId(null);
        setSpectatorTournamentCtx(null);
      });

      newSocket.on('spectatorJoined', (data) => {
        console.log('Spectator joined:', data);
        // Only update viewer count if this is for the game we're watching
        if (data.gameId === spectatingGameIdRef.current) {
          setViewerCount(data.spectatorCount || 0);
        }
      });

      newSocket.on('spectatorLeft', (data) => {
        console.log('Spectator left:', data);
        // Only update viewer count if this is for the game we're watching
        if (data.gameId === spectatingGameIdRef.current) {
          setViewerCount(data.spectatorCount || 0);
        }
      });

      newSocket.on('rematchStarted', (data: { oldGameId: string; newGameId: string; players?: { white: { name: string }; black: { name: string } } }) => {
        // Only show to spectators still viewing the game that just had a rematch
        if (!spectatorModeRef.current || spectatingGameIdRef.current !== data.oldGameId) {
          return;
        }
        const whiteName = data.players?.white?.name || 'White';
        const blackName = data.players?.black?.name || 'Black';
        setNotification({
          title: 'Rematch Started',
          message: `${whiteName} and ${blackName} have agreed to a rematch. Would you like to watch?`,
          show: true,
          primaryButton: 'Watch Rematch',
          secondaryButton: 'Back to Lobby',
          onPrimary: () => {
            hideNotification({ resetContent: true });
            newSocket.emit('stopWatching', { gameId: data.oldGameId });
            newSocket.emit('watchGame', { gameId: data.newGameId });
          },
          onSecondary: () => {
            hideNotification({ resetContent: true });
            stopWatching();
            setCurrentPage('spectator');
            localStorage.setItem('mig_current_page', 'spectator');
          }
        });
      });

      newSocket.on('opponentDisconnected', (data) => {
        console.log('Opponent disconnected:', data);
        setOpponentDisconnected(true);
        setGracePeriodSeconds(data.gracePeriodSeconds || 30);
        
        // Determine which player disconnected
        if (spectatorModeRef.current) {
          // Spectator mode: try to determine from event data or infer from game state
          if (data.disconnectedPlayerColor) {
            setDisconnectedPlayerColor(data.disconnectedPlayerColor);
          } else {
            // Try to infer: if we have game state, check which player's info might be stale
            // For now, default to null (will show on top) - backend should send this info
            setDisconnectedPlayerColor(null);
            console.warn('Spectator mode: disconnectedPlayerColor not provided in event, defaulting to top');
          }
        } else {
          // Regular player: opponent is always at top regardless of color
          // null means "opponent" which is always top for regular players
          setDisconnectedPlayerColor(null);
        }
        
        // Start countdown timer
        if (gracePeriodIntervalRef.current) {
          clearInterval(gracePeriodIntervalRef.current);
        }
        
        gracePeriodIntervalRef.current = setInterval(() => {
          setGracePeriodSeconds(prev => {
            if (prev <= 1) {
              if (gracePeriodIntervalRef.current) {
                clearInterval(gracePeriodIntervalRef.current);
                gracePeriodIntervalRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Show notification
        showToast('Opponent disconnected. Grace period: 30 seconds');
      });

      newSocket.on('opponentReconnected', () => {
        console.log('Opponent reconnected');
        
        // Suppress false positive reconnection messages that happen right after game start
        // This prevents the brief "opponent reconnected" flash when accepting challenges
        const GAME_START_GRACE_PERIOD_MS = 5000; // 5 seconds
        if (gameStartTimeRef.current) {
          const timeSinceGameStart = Date.now() - gameStartTimeRef.current;
          if (timeSinceGameStart < GAME_START_GRACE_PERIOD_MS) {
            console.log(`Suppressing reconnection toast - game started ${Math.round(timeSinceGameStart)}ms ago (likely false positive)`);
            // Still update state, just don't show toast
            setOpponentDisconnected(false);
            setDisconnectedPlayerColor(null);
            setGracePeriodSeconds(30);
            if (gracePeriodIntervalRef.current) {
              clearInterval(gracePeriodIntervalRef.current);
              gracePeriodIntervalRef.current = null;
            }
            return;
          }
        }
        
        setOpponentDisconnected(false);
        setDisconnectedPlayerColor(null);
        setGracePeriodSeconds(30);
        
        // Clear countdown timer
        if (gracePeriodIntervalRef.current) {
          clearInterval(gracePeriodIntervalRef.current);
          gracePeriodIntervalRef.current = null;
        }
        
        showToast('Opponent reconnected!');
      });

newSocket.on('gameReconnected', (data) => {
  console.log('Reconnected to existing game:', data);
  
  forceExitSpectatorMode('gameReconnected');

  isTournamentGameRef.current = !!data.fromTournament;
  setIsTournamentGame(!!data.fromTournament);
  
  // Clear disconnect state
  setOpponentDisconnected(false);
  setDisconnectedPlayerColor(null);
  setGracePeriodSeconds(30);
  if (gracePeriodIntervalRef.current) {
    clearInterval(gracePeriodIntervalRef.current);
    gracePeriodIntervalRef.current = null;
  }
  
  // Set up the game state similar to gameStart
  setGameId(data.gameId);
  gameIdRef.current = data.gameId;
  setPlayerColor(data.playerColor);
  setOpponentName(data.opponentName);
  opponentNameRef.current = data.opponentName; // Update ref for closure access
  setGameState(prev => ({
    ...prev,
    ...data.gameState,
    gameStatus: 'active',
    igoLine: data.gameState.igoLine || null,  // Use server value if provided, otherwise clear
    lastMove: data.gameState.lastMove || null // Use server value if provided, otherwise clear
  }));
  
  // CRITICAL: Replace any cached timer value with server value immediately
  // Server has calculated elapsed time during disconnect - trust it completely
  lastServerTimerUpdateRef.current = Date.now();
  setTimers(data.timers); // Server value always takes precedence
  setActiveTimer(data.gameState.currentPlayer);
  setIsGameStarted(true);
  isGameStartedRef.current = true;
  setStartingPlayer('white'); // Online games always start with white
  
  // Set game mode and hide menus
  setGameMode('online');
  setShowMatchmaking(false);
  setIsSearchingMatch(false);
  
  // IMMEDIATELY request timer sync to get most current server state
  // This ensures accuracy even if gameReconnected data is slightly stale
  if (newSocket && data.gameId) {
    console.log('Requesting timer sync after game reconnection');
    newSocket.emit('requestTimerSync', { gameId: data.gameId });
  }
  
  // Show success message
  showToast('Reconnected to game successfully!');
  
  console.log('Game reconnection complete');
});

      // Handle server maintenance/deployment notifications
      newSocket.on('serverMaintenance', (data) => {
        console.log('Server maintenance notification:', data);
        showToast(
          data.message || 'Server maintenance in progress. Your game will be saved.',
          10000 // Show for 10 seconds
        );
      });

      // ── Global tournament notifications (always active, any page) ──────────
      newSocket.on('tournament:started', (data: { tournamentId: number; tournamentName: string }) => {
        setTournamentBanner({ name: data.tournamentName, tournamentId: data.tournamentId });
      });

      newSocket.on('tournament:readyCheck', (data: {
        matchId: number; tournamentId: number; tournamentName: string;
        opponentName: string; round: number; timeoutSeconds: number;
      }) => {
        // Drop YugoVision before the match so Accept → gameStart can't race with spectating
        forceExitSpectatorMode('tournament:readyCheck');
        if (TOURNAMENTS_NAV_UI_ENABLED) {
          setCurrentPage('tournaments');
          localStorage.setItem('mig_current_page', 'tournaments');
        }
        setTournamentReadyCheck(data);
        setTournamentRCCountdown(data.timeoutSeconds);
        setTournamentRCAccepted(false);
        setTournamentOpponentPeerReady(false);
      });

      newSocket.on('tournament:playoffStarting', (data: { message: string; tournamentName?: string }) => {
        showToast(data.message, 8000);
      });

      newSocket.on('tournament:error', (data: { message?: string }) => {
        if (data.message) showToast(data.message, 6000);
      });

      newSocket.on('tournament:cancelled', (data: { message?: string; tournamentName?: string }) => {
        showToast(data.message || `Tournament "${data.tournamentName || ''}" was cancelled.`, 8000);
      });

      newSocket.on('tournament:peerReady', (data: { matchId: number; tournamentId: number; readyUsername: string }) => {
        const cur = tournamentReadyCheckRef.current;
        if (cur && data.matchId === cur.matchId) {
          setTournamentOpponentPeerReady(true);
        }
      });

      newSocket.on('tournament:matchResult', (data: {
        matchId: number; tournamentId: number; result?: string; reason?: string;
      }) => {
        // Dismiss the ready-check modal
        if (tournamentRCTimerRef.current) clearInterval(tournamentRCTimerRef.current);
        setTournamentReadyCheck(null);
        setTournamentRCAccepted(false);
        setTournamentOpponentPeerReady(false);
        if (data.reason === 'double_forfeit') {
          showToast('Both players missed the ready check — both are eliminated.');
        } else if (data.result === 'win' && data.reason === 'opponent_no_show') {
          showToast(
            'Your opponent did not accept the match in time — you win by walkover and advance.',
            8000
          );
        } else if (data.result === 'win') {
          showToast('You won the tournament match!');
        } else if (data.result === 'loss' && data.reason === 'no_show') {
          showToast('You missed the ready check — your opponent advances.', 8000);
        } else if (data.result === 'loss') {
          showToast('You lost the tournament match.');
        }
      });

      newSocket.on('tournament:won', (data: { tournamentId: number; tournamentName: string }) => {
        setTournamentChampionCelebration({
          tournamentName: data.tournamentName || 'Tournament'
        });
      });

      newSocket.on('tournament:completed', (data: { tournamentId: number; winnerUsername: string }) => {
        // Winner already gets the full celebration via tournament:won
        const myName = authStateRef.current.user?.username;
        if (data.winnerUsername && myName && data.winnerUsername === myName) return;
        if (!data.winnerUsername) {
          showToast('Tournament complete — no champion (final double forfeit).', 6000);
          return;
        }
        showToast(`Tournament complete! Champion: ${data.winnerUsername}`, 6000);
      });

      // ────────────────────────────────────────────────────────────────────────

      // Debug: Listen for all socket events
      newSocket.onAny((event, ...args) => {
        console.log(`Socket event received: ${event}`, args);
      });

      // Rematch event handlers
      newSocket.on('rematchRequested', (data) => {
        if (isTournamentGameRef.current) return;
        console.log('*** REMATCH REQUESTED EVENT RECEIVED ***');
        console.log('Rematch requested by:', data.requesterName, 'Full data:', data);
        setRematchState({
          requested: true,
          fromPlayer: data.requesterName,
          requestedBy: data.requesterName,
          waitingForResponse: false
        });
        
        // Force update the notification to show the rematch request
        setNotification(prev => ({
          ...prev,
          show: true // Make sure modal stays open
        }));
      });

      newSocket.on('rematchRequestSent', () => {
        console.log('Rematch request sent confirmation received');
        setRematchState(prev => ({
          ...prev,
          waitingForResponse: true
        }));
        showToast('Rematch request sent to opponent');
      });

      // Around line 2051-2088
newSocket.on('rematchAccepted', (data) => {
  console.log('Rematch accepted, starting new game:', data);
  
  // Exit review mode immediately if currently in review
  setIsReviewMode(false);
  setCurrentReviewMove(0);
  setOriginalGameState(null);

  // Reset timer warning for the new game
  setTimerWarningShown({ white: false, black: false });
  timerWarningShownRef.current = { white: false, black: false };
  
  // Reset rematch state
  setRematchState({
    requested: false,
    fromPlayer: null,
    requestedBy: '',
    waitingForResponse: false
  });
  
  // CRITICAL FIX: Explicitly clear the game state to remove all remnants
  // Don't use spread operator which might keep old values
  setGameState({
    board: data.gameState.board,
    currentPlayer: data.gameState.currentPlayer,
    scores: data.gameState.scores,
    gameStatus: 'active',
    lastMove: null,  // Explicitly null to clear last move highlight
    players: data.gameState.players,
    igoLine: null    // Explicitly null to clear green igo line
  });
  
  // Clear animation states
  setNewlyPlacedDots(new Set());
  setFadingDots(new Set());
  
  // Set up new game identifiers
  setGameId(data.gameId);
  gameIdRef.current = data.gameId;
  setPlayerColor(data.playerColor);
  setOpponentName(data.opponentName);
  opponentNameRef.current = data.opponentName;
  setOpponentDisconnected(false);
  setDisconnectedPlayerColor(null);
  setMoveHistory([]);
  setBoardHistory([]);
  pendingResetForfeitureRef.current = null;
      pendingResetForfeitureRef.current = null;
      hideNotification({ resetContent: true, preserveBoard: true });
  lastGameEndReasonRef.current = null;
  
  // Reset game result recording flag for new game
  setGameResultRecorded(false);
  gameResultRecordedRef.current = false;
  
        setIsGameStarted(true);
        isGameStartedRef.current = true;
        setStartingPlayer('white'); // Online games always start with white
        setShowMatchmaking(false);
        setIsSearchingMatch(false);

  // Reset timers from server data
  if (data.timers) {
    setTimers(data.timers);
    setActiveTimer(data.gameState.currentPlayer);
  }
  
  showToast(`Rematch started - you are now playing as ${data.playerColor}`);
});

      newSocket.on('rematchDeclined', () => {
        console.log('Rematch declined by opponent');
        setRematchState({
          requested: false,
          fromPlayer: null,
          requestedBy: '',
          waitingForResponse: false
        });
        // Close the modal completely and show toast notification
        pendingResetForfeitureRef.current = null;
        hideNotification({ resetContent: true });
        showToast('Opponent declined the rematch');
      });

      newSocket.on('rematchRequestFailed', (data) => {
        console.log('Rematch request failed', data);
        setRematchState({
          requested: false,
          fromPlayer: null,
          requestedBy: '',
          waitingForResponse: false
        });
        const reason = data?.reason ?? '';
        const message =
          data?.message ??
          (reason === 'opponent_offline'
            ? 'Your opponent has left the match. Rematch is unavailable.'
            : 'Unable to start a rematch right now.');
        showToast(message);
      });

      // Draw offer events
      newSocket.on('drawOffered', (data) => {
        if (isTournamentGameRef.current) {
          // Should not happen — decline silently if a stale offer arrives mid-tournament
          if (data.gameId && newSocket.connected) {
            newSocket.emit('draw-decline', { gameId: data.gameId });
          }
          return;
        }
        console.log('Draw offer received from opponent');
        setPendingDrawFrom(data.fromPlayer);
        setShowDrawOffer(true);
      });

      newSocket.on('drawError', (data) => {
        showToast(data?.message || 'Draw action failed');
        setShowDrawOffer(false);
        setPendingDrawFrom(null);
      });

      newSocket.on('drawAccepted', () => {
        console.log('Draw offer accepted');
        setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
        
        // Record the draw using centralized function
        recordGameEnd('draw', 'drawAccepted - mutual agreement');
        
        // Keep isGameStarted true so move history remains visible for review
        // setIsGameStarted(false);  // Removed - players should be able to review after draw
        // Add 1 second delay for players to see the final position
        setTimeout(() => {
          setNotification({
            title: 'Game Drawn',
            message: 'The game has ended in a draw by mutual agreement.',
            show: true
          });
        }, 1000);
        setActiveTimer(null);
      });

      newSocket.on('drawDeclined', () => {
        console.log('Draw offer declined');
        showToast('Opponent declined the draw offer');
      });

      // Room-based multiplayer event handlers
      newSocket.on('roomCreated', (data) => {
        setCurrentRoom({
          code: data.roomCode,
          isHost: true,
          hostName: data.playerName,
          status: 'waiting'
        });
        setShowRoomModal(false);
        showToast(`Room ${data.roomCode} created! Share this code with your friend.`);
      });

      newSocket.on('roomJoined', (data) => {
        console.log('roomJoined event received:', data);
        
        // Update room state for both host and guest
        setCurrentRoom(prev => {
          const isJoiningGuest = !prev; // If no previous room state, this socket is the guest joining
          
          if (isJoiningGuest) {
            // This is the guest joining
            const timerMsg = data.timerSettings 
              ? `Joined room ${data.roomCode}! Time control: ${data.timerSettings.minutesPerPlayer} minutes`
              : `Joined room ${data.roomCode}!`;
            showToast(timerMsg);
            return {
              code: data.roomCode,
              isHost: false,
              hostName: data.host.name,
              guestName: data.guest.name,
              status: data.status,
              timerSettings: data.timerSettings
            };
          } else {
            // This is the host receiving the update that guest joined
            showToast(`${data.guest.name} joined the room!`);
            return {
              ...prev,
              guestName: data.guest.name,
              status: data.status,
              timerSettings: data.timerSettings
            };
          }
        });
        setShowRoomModal(false);
      });

      newSocket.on('guestLeft', (data) => {
        const activeRoom = currentRoomRef.current;
        if (activeRoom && activeRoom.code === data.roomCode) {
          setCurrentRoom(prev => prev ? {
            ...prev,
            guestName: undefined,
            status: 'waiting'
          } : null);
          showToast(data.reason === 'disconnected' ? 'Guest disconnected' : 'Guest left the room');
        }
      });

      newSocket.on('roomClosed', (data) => {
        setCurrentRoom(null);
        showToast(data.message);
      });

      newSocket.on('roomError', (data) => {
        showToast(data.message);
      });

      return () => {
        // CRITICAL: Don't close socket if we're in an active game
        // This prevents false disconnects when gameMode changes to 'online' during gameStart
        // The new effect will see the socket is already connected and skip recreation
        if (isGameStartedRef.current && gameIdRef.current) {
          console.log('Skipping socket cleanup - active game in progress, socket will be reused');
          return;
        }

        const token = localStorage.getItem('authToken');
        const auth = authStateRef.current;
        const isMember = !!token && auth.isAuthenticated && !auth.isGuest;
        if (isMember) {
          console.log('Skipping socket cleanup - member persistent socket');
          return;
        }

        const guestStillNeeds =
          gameModeRef.current === 'online' ||
          currentPageRef.current === 'tournaments' ||
          spectatorModeRef.current;
        if (guestStillNeeds) {
          console.log('Skipping socket cleanup - guest still watching tournaments or playing online');
          return;
        }
        
        console.log('Cleaning up socket connection');
        // Remove all event listeners before closing
        newSocket.removeAllListeners();
        newSocket.close();
        // Reset recording flag when socket is cleaned up
        gameResultRecordedRef.current = false;
        setGameResultRecorded(false);
        // Clear challenge queue on socket disconnect to prevent stale challenges
        setCurrentChallenge(null);
        setChallengeQueue([]);
      };
    } else {
      // If not online mode but still authenticated, keep socket for challenges
      // Also keep socket for guests when in online mode (for quick match)
      // or when they are on Tournaments / watching a tournament game
      const token = localStorage.getItem('authToken');
      const currentAuthState = authStateRef.current;
      const hasToken = !!token;
      const isGuest = !hasToken && (!currentAuthState.isAuthenticated || currentAuthState.isGuest);
      const shouldKeepSocket = (hasToken && !isGuest && currentAuthState.isAuthenticated) ||
                               (isGuest && (gameMode === 'online' || currentPage === 'tournaments' || spectatorMode));
      
      if (!shouldKeepSocket && socket) {
        // User is not authenticated and not a guest who still needs a live socket
        socket.removeAllListeners();
        socket.close();
        setSocket(null);
      }
      // If shouldKeepSocket is true, socket stays connected for challenges or guest matchmaking
      
      gameResultRecordedRef.current = false;
      setGameResultRecorded(false);
    }
    // Depend on gameMode and authState - socket should recreate when switching to/from online mode
    // or when authentication state changes
    // Use user?.id instead of user object to prevent unnecessary reconnections when user object reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode, authState.isAuthenticated, authState.user?.id, currentPage, spectatorMode]);

  // Timer timeout handler for local/AI games (called by GameTimerDisplay when time runs out)
  const handleTimerTimeout = useCallback(() => {
    if (!activeTimer) return;
    const loser = activeTimer; // Player who ran out of time
    const winner = activeTimer === 'white' ? 'black' : 'white';
    // CRITICAL: Set loser's timer to 0 so the display shows 0 when game ends.
    // Without this, GameTimerDisplay switches to showing the stale `seconds` prop
    // (never updated via onTimerStopped on timeout) and would show e.g. 2 seconds.
    setTimers(prev => ({ ...prev, [loser]: 0 }));
    setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
    const winnerName = getCurrentWinnerName(winner);
    showGameOverNotification('Time Out', `${winnerName} wins on time!`);
    setActiveTimer(null);
    timerStartTimeRef.current = { white: null, black: null };
    timerPauseStartRef.current = { white: null, black: null };
  }, [activeTimer, getCurrentWinnerName, showGameOverNotification]);

  // Timer stopped handler - sync remaining time to parent when switching players
  const handleTimerStopped = useCallback((player: 'white' | 'black', remainingSeconds: number) => {
    setTimers((prev) => {
      const next = { ...prev };
      const add = (timerEnabled && incrementSeconds > 0 && gameMode !== 'online') ? incrementSeconds : 0;
      next[player] = remainingSeconds + add;
      return next;
    });
  }, [timerEnabled, incrementSeconds, gameMode]);

  // Timer warning handler (called by GameTimerDisplay at 20 seconds for local/AI, or by online timer logic)
  const handleTimerWarning = useCallback((player: 'white' | 'black') => {
    setTimerWarningShown(prev => ({ ...prev, [player]: true }));
    setBoardWarningActive(true);
    setTimeout(() => setBoardWarningActive(false), 1000);

    // Pulse the entire player bar (player-info container) red twice
    const playerEl = document.getElementById(`player-${player}`);
    const playerBar = playerEl?.closest('.player-info');
    if (playerBar) {
      playerBar.classList.add('timer-pulse');
      setTimeout(() => playerBar.classList.remove('timer-pulse'), 2000);
    }
    if (gameSoundsEnabled) {
      try {
        const preloadedAudio = preloadedSounds.current.timer;
        const isReady = audioReady.current.timer;
        if (preloadedAudio && (isReady || preloadedAudio.readyState >= 2)) {
          const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
          audio.volume = preloadedAudio.volume;
          audio.currentTime = 0;
          audio.play().catch(() => {});
        } else if (preloadedAudio) {
          const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
          audio.volume = preloadedAudio.volume;
          audio.currentTime = 0;
          audio.play().catch(() => {});
        } else {
          const audio = new Audio('/sounds/timer.mp3');
          audio.volume = 0.25;
          audio.currentTime = 0;
          audio.play().catch(() => {});
        }
      } catch {
        // Silently fail - sound errors shouldn't break gameplay
      }
    }
  }, [gameSoundsEnabled]);



  // Navigation functions — dismissible overlays are mutually exclusive with navigation
  const closeDismissibleOverlays = useCallback(() => {
    setShowTutorial(false);
    setShowRules(false);
    setShowSettings(false);
    setShowStats(false);
    setShowPlayersModal(false);
    setShowAboutModal(false);
    setShowGlobalChat(false);
    setShowMatchmaking(false);
    setShowMobileControls(false);
    setShowStatisticsModal(false);
    setViewingOtherPlayerStats(null);
    setShowImportMovesModal(false);
    setShowLogin(false);
    setShowSignup(false);
    setShowStatsAuth(false);
    setMessagePlayerTarget(null);
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setShowMobileControls(false);
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handlePlayClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('game');
    localStorage.setItem('mig_current_page', 'game');
  }, [closeDismissibleOverlays]);

  const handlePuzzlesClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('puzzles');
    localStorage.setItem('mig_current_page', 'puzzles');
  }, [closeDismissibleOverlays]);

  const handleShopClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('shop');
    localStorage.setItem('mig_current_page', 'shop');
  }, [closeDismissibleOverlays]);

  const handleSpectatorClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('spectator');
    localStorage.setItem('mig_current_page', 'spectator');
    if (socket && socket.connected) {
      socket.emit('joinSpectatorLobby');
    }
  }, [closeDismissibleOverlays, socket]);

  const handleTournamentsClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('tournaments');
    localStorage.setItem('mig_current_page', 'tournaments');
  }, [closeDismissibleOverlays]);

  const handleHowToPlayClick = useCallback(() => {
    closeDismissibleOverlays();
    setShowTutorial(true);
    setTutorialStep(0);
  }, [closeDismissibleOverlays]);

  const handleRulesClick = useCallback(() => {
    closeDismissibleOverlays();
    setShowRules(true);
  }, [closeDismissibleOverlays]);

  const handleSettingsClick = useCallback(() => {
    closeDismissibleOverlays();
    setShowSettings(true);
  }, [closeDismissibleOverlays]);

  const handlePlayersClick = useCallback(() => {
    closeDismissibleOverlays();
    if (authState.isAuthenticated && authState.user) {
      setShowPlayersModal(true);
    } else {
      setAuthModalMode('signin');
      setAuthModalOpen(true);
    }
  }, [closeDismissibleOverlays, authState.isAuthenticated, authState.user?.id]);

  const handleStatsClick = useCallback(() => {
    closeDismissibleOverlays();
    if (authState.isAuthenticated && authState.user) {
      setShowStatisticsModal(true);
    } else {
      setAuthModalMode('signin');
      setAuthModalOpen(true);
    }
  }, [closeDismissibleOverlays, authState.isAuthenticated, authState.user?.id]);

  const handleStatisticsClick = useCallback(() => {
    handleStatsClick();
  }, [handleStatsClick]);

  const handleLeaderboardClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('leaderboard');
    localStorage.setItem('mig_current_page', 'leaderboard');
  }, [closeDismissibleOverlays]);

  const handleAboutClick = useCallback(() => {
    closeDismissibleOverlays();
    setShowAboutModal(true);
  }, [closeDismissibleOverlays]);

  const handleGlobalChatClick = useCallback(() => {
    closeDismissibleOverlays();
    setShowGlobalChat(true);
  }, [closeDismissibleOverlays]);

  const handleGreedyNavClick = useCallback(() => {
    closeDismissibleOverlays();
    setCurrentPage('greedy');
  }, [closeDismissibleOverlays]);

  const openMobileControls = useCallback(() => {
    closeDismissibleOverlays();
    setShowMobileControls(true);
  }, [closeDismissibleOverlays]);

  // Tournament ready-check countdown — keeps ticking after accept so player sees remaining time
  useEffect(() => {
    if (!tournamentReadyCheck) return;
    if (tournamentRCTimerRef.current) clearInterval(tournamentRCTimerRef.current);
    tournamentRCTimerRef.current = setInterval(() => {
      setTournamentRCCountdown(prev => {
        if (prev <= 1) {
          clearInterval(tournamentRCTimerRef.current!);
          if (!tournamentRCAcceptedRef.current) {
            setTournamentReadyCheck(null);
            setTournamentOpponentPeerReady(false);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (tournamentRCTimerRef.current) clearInterval(tournamentRCTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentReadyCheck]);

  const handleAcceptTournamentReady = useCallback(() => {
    if (!tournamentReadyCheck || !socket) return;
    // Ensure we are not still in YugoVision when the match launches
    const oldSpectatingId = spectatingGameIdRef.current;
    if (oldSpectatingId && socket.connected) {
      socket.emit('stopWatching', { gameId: oldSpectatingId });
    }
    spectatorModeRef.current = false;
    spectatingGameIdRef.current = null;
    setSpectatorMode(false);
    setSpectatingGameId(null);
    setViewerCount(0);
    setSpectatorTournamentCtx(null);
    setTournamentRCAccepted(true);
    socket.emit('tournament:ready', { matchId: tournamentReadyCheck.matchId });
  }, [tournamentReadyCheck, socket]);


  const handleStartGameFromSandbox = useCallback((board: (Cell | null)[][], startingPlayer: 'white' | 'black') => {
    // Clear any saved sandbox position
    localStorage.removeItem('sandboxSavedPosition');
    
    // Set game mode to local for sandbox games
    setGameMode('local');
    gameModeRef.current = 'local';
    localStorage.setItem('migoyugoGameMode', 'local');
    
    // Switch to game page and start game with custom board
    setCurrentPage('game');
    localStorage.setItem('mig_current_page', 'game');
    
    // Start game with custom board and starting player - use setTimeout to ensure state updates complete
    setTimeout(() => {
      startGame(board, startingPlayer);
    }, 0);
  }, []);

  // Memoized challenge handlers to prevent unnecessary re-renders
  const handleChallengeAccept = useCallback(async () => {
    console.log('✅ Challenge accepted, clearing currentChallenge and declining queued challenges');
    
    // Decline all queued challenges so challengers are notified
    // Use ref to get latest queue value
    const queuedChallenges = challengeQueueRef.current.slice(); // Copy the array
    if (queuedChallenges.length > 0) {
      console.log(`📋 Declining ${queuedChallenges.length} queued challenges`);
      // Decline all queued challenges in parallel - AWAIT to ensure they're declined before clearing state
      await Promise.all(
        queuedChallenges.map(challenge => 
          ApiService.declineChallenge(challenge.challengeId).catch(err => {
            console.error(`Failed to decline queued challenge ${challenge.challengeId}:`, err);
          })
        )
      );
    }
    
    // Clear current challenge and queue
    setCurrentChallenge(null);
    setChallengeQueue([]);
    
    // Don't show next challenge - game is starting
  }, []);

  const handleChallengeDecline = useCallback(() => {
    console.log('❌ Challenge declined, clearing currentChallenge');
    // Use ref to get latest queue value to avoid stale closure
    const currentQueue = challengeQueueRef.current;
    setCurrentChallenge(null);
    
    // Show next challenge in queue if any
    if (currentQueue.length > 0) {
      const next = currentQueue[0];
      setChallengeQueue(prev => prev.slice(1));
      // Set next challenge after queue is updated
      setCurrentChallenge(next);
      console.log('📋 Showing next challenge from queue:', next);
    } else {
      setChallengeQueue([]);
    }
  }, []);

  const handleChallengeTimeout = useCallback(async () => {
    const challengeToDecline = currentChallengeRef.current;
    if (!challengeToDecline) {
      console.log('⚠️ Challenge timeout called but no current challenge');
      return;
    }
    
    console.log('⏰ Challenge timeout - auto-declining:', challengeToDecline.challengeId);
    try {
      await ApiService.declineChallenge(challengeToDecline.challengeId);
      // Use ref to get latest queue value to avoid stale closure
      const currentQueue = challengeQueueRef.current;
      setCurrentChallenge(null);
      
      // Show next challenge in queue if any
      if (currentQueue.length > 0) {
        const next = currentQueue[0];
        setChallengeQueue(prev => prev.slice(1));
        // Set next challenge after queue is updated
        setCurrentChallenge(next);
        console.log('📋 Showing next challenge from queue after timeout:', next);
      } else {
        setChallengeQueue([]);
      }
    } catch (err) {
      console.error('Failed to auto-decline challenge:', err);
      setCurrentChallenge(null);
    }
  }, []);

  // Helper function to initialize timers
  const initializeTimers = () => {
    if (timerEnabled) {
      const totalSeconds = minutesPerPlayer * 60;
      setTimers({ white: totalSeconds, black: totalSeconds });
      // Reset timer tracking when initializing
      timerStartTimeRef.current = { white: null, black: null };
      timerInitialValuesRef.current = { white: totalSeconds, black: totalSeconds };
      timerPausedTimeRef.current = { white: 0, black: 0 };
      timerPauseStartRef.current = { white: null, black: null };
      // Reset warning flags (once per game per player)
      setTimerWarningShown({ white: false, black: false });
      setBoardWarningActive(false);
      // Reset previous timer values tracking
      previousTimerValuesRef.current = { white: totalSeconds, black: totalSeconds };
    }
  };

  // Start timer when game starts or player changes
  useEffect(() => {
    if (gameMode === 'online') {
      return; // Online games use server timers
    }

    if (isGameStarted && gameState.gameStatus === 'active' && timerEnabled) {
      const newActivePlayer = gameState.currentPlayer;
      const previousActivePlayer = activeTimer;
      
      // If switching players, reset the timer start time for the new active player
      if (previousActivePlayer !== null && previousActivePlayer !== newActivePlayer) {
        // Stop tracking the previous player's timer
        timerStartTimeRef.current[previousActivePlayer] = null;
        timerPauseStartRef.current[previousActivePlayer] = null;
        
        // Start tracking the new player's timer
        // Use current timer value as the starting point (don't reset initial value!)
        timerStartTimeRef.current[newActivePlayer] = Date.now();
        // Set initial value to current timer value - this is the amount of time they have left
        timerInitialValuesRef.current[newActivePlayer] = timers[newActivePlayer];
        timerPausedTimeRef.current[newActivePlayer] = 0;
        timerPauseStartRef.current[newActivePlayer] = null;
      } else if (previousActivePlayer === null) {
        // First time starting timer for the game
        timerStartTimeRef.current[newActivePlayer] = Date.now();
        timerInitialValuesRef.current[newActivePlayer] = timers[newActivePlayer];
        timerPausedTimeRef.current[newActivePlayer] = 0;
        timerPauseStartRef.current[newActivePlayer] = null;
      }
      
      setActiveTimer(newActivePlayer);
    }
  }, [isGameStarted, gameState.currentPlayer, gameState.gameStatus, timerEnabled, gameMode, activeTimer, timers]);

  // 20-second timer warning is now handled by GameTimerDisplay via onTimerWarning

  // Reset warning flags when game ends
  useEffect(() => {
    if (gameState.gameStatus === 'finished') {
      setTimerWarningShown({ white: false, black: false });
      setBoardWarningActive(false);
    }
  }, [gameState.gameStatus]);

  // Apply board warning class when warning is active
  useEffect(() => {
    const boardElement = document.getElementById('game-board');
    if (boardElement) {
      if (boardWarningActive) {
        boardElement.classList.add('timer-warning');
        console.log('⏰ Board warning class added to game-board element');
      } else {
        boardElement.classList.remove('timer-warning');
      }
    } else {
      console.warn('⏰ Board element not found when trying to apply warning class');
    }
  }, [boardWarningActive]);

  // Request timer sync when reconnecting to online game or when socket connects
  useEffect(() => {
    if (socket && gameId && gameMode === 'online' && isGameStarted) {
      // Request current timer state - this ensures we get accurate server timer
      // after any disconnection or page reload
      console.log('Requesting timer sync for online game');
      socket.emit('requestTimerSync', { gameId });
    }
  }, [socket, gameId, gameMode, isGameStarted]);

  // Authentication handlers
  const handlePlayAsGuestWrapper = () => {
    handlePlayAsGuest(setAuthState, setShowMatchmaking);
  };

  const handleLogoutWrapper = async () => {
    try {
      // Update user online status and clear inGame flag before signing out
      if (authState.isAuthenticated && authState.user) {
        const onlineStatusResult = await ApiService.updateOnlineStatus(false);
        const inGameResult = await ApiService.setInGame(false);
        if (onlineStatusResult.success && inGameResult.success) {
          console.log('📴 User online status set to false and inGame cleared');
        } else {
          console.warn('⚠️ Some status updates failed during logout:', {
            onlineStatus: onlineStatusResult.error,
            inGame: inGameResult.error
          });
        }
      }
      
      // Sign out via API
      await ApiService.signOut();
      
      // Refresh auth state to update UI immediately
      await refreshAuthState();
      
      // Reset game state
      setGameMode('local');
      resetGame();
      showToast('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      showToast('Error logging out');
      // Still refresh auth state even on error
      await refreshAuthState();
    }
  };

  // Set online status when page loads (if authenticated)
  useEffect(() => {
    const setOnlineOnLoad = async () => {
      if (authState.isAuthenticated && authState.user) {
        try {
          const result = await ApiService.updateOnlineStatus(true);
          if (result.success) {
            console.log('✅ User marked as online on page load');
          } else {
            console.error('❌ Failed to update online status on page load:', result.error);
          }
        } catch (error) {
          console.error('Error setting online status on page load:', error);
        }
      }
    };
    
    setOnlineOnLoad();
  }, [authState.isAuthenticated, authState.user?.id]);

  // Set offline status when page unloads (closing tab/browser or navigating away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (authState.isAuthenticated && authState.user) {
        // Use sendBeacon for reliable delivery even if page is closing
        try {
          const token = ApiService.getToken();
          if (token) {
            const apiUrl = getApiUrl();
            const url = `${apiUrl}/api/auth/update-online-status`;
            const data = JSON.stringify({ isOnline: false });
            
            // Use sendBeacon for reliable delivery during page unload
            if (typeof navigator.sendBeacon === 'function') {
              const blob = new Blob([data], { type: 'application/json' });
              // Note: sendBeacon doesn't support custom headers, so we'll need to handle auth differently
              // For now, we'll rely on the backend Socket.IO disconnect handler as backup
              // navigator.sendBeacon(url, blob);
            } else {
              // Fallback: use fetch with keepalive (less reliable but better than nothing)
              fetch(url, {
                method: 'POST',
                body: data,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                keepalive: true
              }).catch(() => {
                // Ignore errors during page unload
              });
            }
          }
        } catch (error) {
          // Ignore errors during page unload
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [authState.isAuthenticated, authState.user?.id]);

  // Function to refresh auth state (used after sign in/out and username setup)
  const refreshAuthState = useCallback(async () => {
    try {
      const token = ApiService.getToken();
      if (token) {
        const result = await ApiService.getCurrentUser();
        if (result.user) {
          setAuthState({
            isAuthenticated: true,
            user: {
              id: result.user.id || result.user.user_id || '',
              username: result.user.username || 'User',
              email: result.user.email || '',
              stats: {
                gamesPlayed: result.user.stats?.gamesPlayed || 0,
                wins: result.user.stats?.wins || 0,
                losses: result.user.stats?.losses || 0
              }
            },
            isGuest: false
          });
          // Load user preferences after authentication
          loadSavedSettings();
        } else {
          // Token is invalid, clear auth state
          setAuthState({
            isAuthenticated: false,
            user: null,
            isGuest: false
          });
        }
      } else {
        // No token, clear auth state
        setAuthState({
          isAuthenticated: false,
          user: null,
          isGuest: false
        });
      }
    } catch (error) {
      console.error('Error refreshing auth state:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        isGuest: false
      });
    }
  }, []);

  // Function to refresh auth state after username setup
  const handleUsernameSet = useCallback(async () => {
    try {
      const { AuthService } = await import('./services/authService');
      
      // Get current user
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        // Fetch updated user data from API
        const result = await ApiService.getCurrentUser();
        const userData = result.user;
        
        if (userData) {
          setAuthState({
            isAuthenticated: true,
            user: {
              id: userData.uid,
              username: userData.username,
              email: userData.email,
              stats: {
                gamesPlayed: userData['total games played'],
                wins: userData.no?.['of wins'] || 0,
                losses: userData.no?.['of loss'] || 0
              }
            },
            isGuest: false
          });
          console.log('🔄 Auth state refreshed after username setup');
        }
      } else {
        // User is signed out (e.g., for email verification)
        // Reset auth state to reflect that user is not authenticated
        setAuthState({
          isAuthenticated: false,
          user: null,
          isGuest: false
        });
        console.log('🔄 Auth state reset - user signed out');
      }
    } catch (error) {
      console.error('❌ Error refreshing auth state:', error);
    }
  }, []);

  // Handle stats button click - show modal
  const handleViewStatsWrapper = async () => {
    if (!authState.isAuthenticated || !authState.user) {
      setShowSettings(false);
      showToast('Please sign in to view your statistics');
      setAuthModalMode('signin');
      setAuthModalOpen(true);
      return;
    }

    setShowSettings(false);
    // Show statistics modal
    setShowStatisticsModal(true);
  };
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const makeLocalMove = useCallback((row: number, col: number) => {
    // Check basic validity first
    if (!isValidMove(gameState.board, row, col, gameState.currentPlayer)) {
    if (wouldCreateLineTooLong(gameState.board, row, col, gameState.currentPlayer)) {
      showToast("Illegal move. You may not create a line longer than 4 of your own color");
      
      // Highlight the illegal move cell
      setIllegalMoveCell({ row, col });
      setTimeout(() => {
        setIllegalMoveCell(null);
      }, 1000); // Clear highlight after 1 second
      
      // Play illegal move sound (only if game sounds are enabled)
      if (gameSoundsEnabled) {
        try {
          const preloadedAudio = preloadedSounds.current.illegalMove;
          const isReady = audioReady.current.illegalMove;
          if (preloadedAudio && (isReady || preloadedAudio.readyState >= 2)) {
            const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
            audio.volume = preloadedAudio.volume;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          } else if (preloadedAudio) {
            const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
            audio.volume = preloadedAudio.volume;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          } else {
            const audio = new Audio('/sounds/illegal-move.mp3');
            audio.volume = 0.5;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          }
        } catch (e) {
          // Silently fail - sound errors shouldn't break gameplay
        }
      }
      }
      return;
    }
    
    // Store board state before move for undo functionality (only for local/AI games)
    if (gameMode === 'local' || gameMode.startsWith('ai-')) {
      setBoardHistory(prev => [...prev, copyBoard(gameState.board)]);
    }
    
    const currentPlayer = gameState.currentPlayer;
    const newBoard = copyBoard(gameState.board);
    
    // Place the migo
    newBoard[row][col] = { color: currentPlayer, isYugo: false };
    
    // Check for yugos
    const yugos = checkForYugos(newBoard, row, col, currentPlayer);
    const { yugoFormed, removedCells } = processYugos(newBoard, yugos, row, col);
    
            // Trigger fade-out animation for removed dots
    if (removedCells.length > 0) {
      addFadeOutAnimation(removedCells);
    }
    
    // Trigger bounce-in animation for newly placed dot
    addNewDotAnimation(row, col);
    
    // If yugos were formed, make this cell a yugo
    if (yugoFormed) {
      newBoard[row][col] = { color: currentPlayer, isYugo: true };
    }
    
    // Update scores
    const newScores = {
      white: countYugos(newBoard, 'white'),
      black: countYugos(newBoard, 'black')
    };
    
    // Check for igo (winning condition)
    const igo = checkForIgo(newBoard, row, col, currentPlayer);
    let gameOver = false;
    let winner: 'white' | 'black' | 'draw' | null = null;
    
    // Play appropriate sound based on what happened
    if (igo) {
      gameOver = true;
      winner = currentPlayer;
      playSound('igo'); // Igo sound takes priority
    } else if (yugoFormed) {
      playSound('yugo'); // Yugo sound if no igo
    } else {
      playSound('migo'); // Regular migo placement
    }
    
    if (!igo) {
      // Check if next player has legal moves
      const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
      if (!hasLegalMoves(newBoard, nextPlayer)) {
        gameOver = true;
        if (newScores.white > newScores.black) winner = 'white';
        else if (newScores.black > newScores.white) winner = 'black';
        else winner = 'draw';
      }
    }
    
    // Add to move history
    setMoveHistory(prev => [
      ...prev,
      {
        row,
        col,
        player: currentPlayer,
        yugos: yugos.length,
        moveNumber: prev.length + 1
      }
    ]);
    
    if (gameOver) {
      setGameState(prev => ({
        ...prev,
        board: newBoard,
        scores: newScores,
        lastMove: { row, col, player: currentPlayer },
        gameStatus: 'finished',
        igoLine: igo || null
      }));
      
      let message = '';
      if (winner === 'draw') {
        // Check if it's a Wego draw (no legal moves, equal yugos)
        if (!hasLegalMoves(newBoard, currentPlayer)) {
          playSound('wego'); // Wego occurred
          const whiteYugos = newScores.white;
          const blackYugos = newScores.black;
          message = `Wego! No legal moves remain.\nDraw - Yugos tied at ${whiteYugos}`;
        } else {
          message = 'Game ended in a draw!';
        }
      } else if (igo) {
        const winnerName = getCurrentWinnerName(winner as 'white' | 'black');
        message = `${winnerName} wins with an Igo!`;
      } else {
        const winnerName = getCurrentWinnerName(winner as 'white' | 'black');
        // Check if it's a Wego win (game ended because next player has no legal moves)
        const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
        if (!hasLegalMoves(newBoard, nextPlayer)) {
          playSound('wego'); // Wego occurred
          const whiteYugos = newScores.white;
          const blackYugos = newScores.black;
          const winnerYugos = winner === 'white' ? whiteYugos : blackYugos;
          const loserYugos = winner === 'white' ? blackYugos : whiteYugos;
          message = `Wego! No legal moves remain.\n${winnerName} wins ${winnerYugos} Yugos to ${loserYugos}`;
        } else {
          message = `${winnerName} wins by yugo count!`;
        }
      }
      
      // Record game result using centralized function (only for local/AI games)
      // Online games are recorded via socket events in recordGameEnd
      if (gameMode === 'local' || gameMode.startsWith('ai-')) {
        recordGameEnd(winner as 'white' | 'black' | 'draw', 'local game over');
      }
      
      // Add 1 second delay for players to see the final move
      setTimeout(() => {
      setNotification({
        title: 'Game Over',
        message,
        show: true
      });
      }, 1000);
      setActiveTimer(null);
    } else {
      // Switch to next player
      const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
      // Note: increment is applied in handleTimerStopped when GameTimerDisplay reports remaining time

      setGameState(prev => ({
        ...prev,
        board: newBoard,
        currentPlayer: nextPlayer,
        scores: newScores,
        lastMove: { row, col, player: currentPlayer },
        gameStatus: 'active',
        igoLine: null
      }));
      // Note: activeTimer will be updated automatically by useEffect at line 2363
    }
  }, [gameState.board, gameState.currentPlayer, setMoveHistory, setGameState, setNotification, setActiveTimer, addFadeOutAnimation, addNewDotAnimation, playSound, showToast, timerEnabled, incrementSeconds, getCurrentWinnerName, gameMode, recordGameEnd]);

  // AI move logic with human-like thinking time (Level 1 & 2 only; Level 3 uses engine)
  useEffect(() => {
    const aiTurnInStandardMode = isGameStarted &&
      gameState.gameStatus === 'active' &&
      (gameMode === 'ai-1' || gameMode === 'ai-2') &&
      playerColor &&
      gameState.currentPlayer !== playerColor;

    const aiTurnInSelfPlay = false;

    if (aiTurnInStandardMode || aiTurnInSelfPlay) {
      // AI's turn
      let minThinkTime, maxThinkTime;
      const currentEngine = gameMode as 'ai-1' | 'ai-2' | 'ai-3';

      if (currentEngine === 'ai-1') {
        minThinkTime = 1000;
        maxThinkTime = 2000;
      } else if (currentEngine === 'ai-2') {
        minThinkTime = 1500;
        maxThinkTime = 2500;
      } else {
        minThinkTime = 2000;
        maxThinkTime = 4000;
      }
      const thinkTime = Math.floor(Math.random() * (maxThinkTime - minThinkTime + 1)) + minThinkTime;
      console.log(`${currentEngine.toUpperCase()} thinking for ${thinkTime}ms...`);
      const timeout = setTimeout(async () => {
        // Determine AI color - it's the current player when it's AI's turn
        const aiColor = gameState.currentPlayer;
        // Human color is the opposite of AI color when AI is playing
        const humanColor: 'white' | 'black' = (gameState.currentPlayer === 'white' ? 'black' : 'white');
        
        let aiMove: { row: number; col: number } | null = null;

        if (!aiMove) {
          // AI-1, AI-2, AI-3 logic
          const validMoves: { row: number; col: number; score: number }[] = [];
          for (let row = 0; row < 8; row++) {
            for (let col = 0; col < 8; col++) {
              if (isValidMove(gameState.board, row, col, aiColor)) {
                const isOpeningPhase = aiMoveCount < 5;
                const isFirstThreeMoves = aiMoveCount < 3;
                const isEdgeSquare = row === 0 || row === 7 || col === 0 || col === 7;
                if (isOpeningPhase && isFirstThreeMoves && isEdgeSquare) continue;
                const score = evaluateMove(gameState.board, row, col, aiColor, currentEngine as 'ai-1' | 'ai-2' | 'ai-3');
                validMoves.push({ row, col, score });
              }
            }
          }
          if (validMoves.length > 0) {
            validMoves.sort((a, b) => b.score - a.score);
            const isOpeningPhase = aiMoveCount < 5;
            if (isOpeningPhase && currentEngine !== 'ai-3') {
              const topMoves = validMoves.slice(0, Math.min(3, validMoves.length));
              aiMove = topMoves[Math.floor(Math.random() * topMoves.length)];
            } else {
              if (currentEngine === 'ai-1') {
                const topMoves = validMoves.slice(0, Math.min(3, validMoves.length));
                aiMove = topMoves[Math.floor(Math.random() * topMoves.length)];
              } else if (currentEngine === 'ai-2') {
                const criticalMoves = validMoves.filter(move => move.score >= 800);
                if (criticalMoves.length > 0) aiMove = criticalMoves[0];
                else {
                  const topMoves = validMoves.slice(0, Math.min(5, validMoves.length));
                  aiMove = topMoves[Math.floor(Math.random() * topMoves.length)];
                }
              } else {
                const criticalMoves = validMoves.filter(move => move.score >= 9000);
                if (criticalMoves.length > 0) aiMove = criticalMoves[0];
                else {
                  const winningMoves = validMoves.filter(move => move.score >= 1000);
                  aiMove = winningMoves.length > 0 ? winningMoves[0] : validMoves[0];
                }
              }
            }
          }
        }

        if (aiMove) {
          if (!isValidMove(gameState.board, aiMove.row, aiMove.col, aiColor)) {
            const fallbackMoves = getAllValidMoves(gameState.board, aiColor);
            if (fallbackMoves.length > 0) aiMove = fallbackMoves[0];
            else {
              setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
              showGameOverNotification('Game Over', `${getCurrentWinnerName(humanColor)} wins - AI has no legal moves!`);
              setActiveTimer(null);
              return;
            }
          }
          setAiMoveCount(prev => prev + 1);
          makeLocalMove(aiMove.row, aiMove.col);
        } else {
          const fallbackMoves = getAllValidMoves(gameState.board, aiColor);
          if (fallbackMoves.length > 0) makeLocalMove(fallbackMoves[0].row, fallbackMoves[0].col);
          else {
            setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
            showGameOverNotification('Game Over', `${getCurrentWinnerName(humanColor)} wins - AI has no legal moves!`);
            setActiveTimer(null);
          }
        }
      }, thinkTime);
      return () => clearTimeout(timeout);
    }
  }, [gameState.currentPlayer, gameState.gameStatus, isGameStarted, gameMode, gameState.board, makeLocalMove, playerColor, getCurrentWinnerName, showGameOverNotification, showToast, setGameState, setActiveTimer, aiMoveCount]);

  // Rust Engine AI move logic
  // Intermediate: depth 2, Advanced: depth 4, Expert: 500ms full depth, Master: 5000ms full depth
  useEffect(() => {
    const engineTurn = isGameStarted &&
      gameState.gameStatus === 'active' &&
      (gameMode === 'ai-challenger' || gameMode === 'ai-skilled' || gameMode === 'ai-3' || gameMode === 'ai-engine') &&
      playerColor &&
      gameState.currentPlayer !== playerColor;

    if (!engineTurn) return;

    let cancelled = false;
    const timeLimitMs = gameMode === 'ai-3' ? 500 : 5000;
    const maxDepth = gameMode === 'ai-challenger' ? 2 : gameMode === 'ai-skilled' ? 4 : undefined;

    const fetchEngineMove = async () => {
      const startTime = Date.now();
      const aiColor = gameState.currentPlayer;
      const humanColor: 'white' | 'black' = aiColor === 'white' ? 'black' : 'white';

      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/engine/move`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            board: gameState.board,
            player_color: aiColor,
            time_limit: timeLimitMs,
            ...(maxDepth !== undefined && { max_depth: maxDepth }),
          }),
        });

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(`Engine returned ${response.status}`);
        }

        const result = await response.json();
        if (cancelled) return;

        // Minimum think time: 2-3 seconds so AI feels like it's considering
        const minThinkTime = 2000 + Math.random() * 1000;
        const elapsed = Date.now() - startTime;
        if (elapsed < minThinkTime) {
          await new Promise(resolve => setTimeout(resolve, minThinkTime - elapsed));
        }
        if (cancelled) return;

        if (result.row !== undefined && result.col !== undefined) {
          console.log(`[Engine] Move: (${result.row}, ${result.col}) depth=${result.depth} nodes=${result.nodes} time=${result.time}ms`);
          if (isValidMove(gameState.board, result.row, result.col, aiColor)) {
            setAiMoveCount(prev => prev + 1);
            makeLocalMove(result.row, result.col);
          } else {
            console.error('[Engine] Returned invalid move, using fallback');
            const fallbackMoves = getAllValidMoves(gameState.board, aiColor);
            if (fallbackMoves.length > 0) {
              setAiMoveCount(prev => prev + 1);
              makeLocalMove(fallbackMoves[0].row, fallbackMoves[0].col);
            } else {
              setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
              showGameOverNotification('Game Over', `${getCurrentWinnerName(humanColor)} wins - Engine has no legal moves!`);
              setActiveTimer(null);
            }
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[Engine] Error:', error);
        showToast('Engine unavailable, using fallback move');
        // Minimum think time even for fallback
        const minThinkTime = 2000 + Math.random() * 1000;
        const elapsed = Date.now() - startTime;
        if (elapsed < minThinkTime) {
          await new Promise(resolve => setTimeout(resolve, minThinkTime - elapsed));
        }
        if (cancelled) return;
        const fallbackMoves = getAllValidMoves(gameState.board, aiColor);
        if (fallbackMoves.length > 0) {
          setAiMoveCount(prev => prev + 1);
          makeLocalMove(fallbackMoves[0].row, fallbackMoves[0].col);
        } else {
          setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
          showGameOverNotification('Game Over', `${getCurrentWinnerName(humanColor)} wins - No legal moves!`);
          setActiveTimer(null);
        }
      }
    };

    fetchEngineMove();
    return () => { cancelled = true; };
  }, [gameState.currentPlayer, gameState.gameStatus, isGameStarted, gameMode, gameState.board, makeLocalMove, playerColor, getCurrentWinnerName, showGameOverNotification, showToast, setGameState, setActiveTimer, aiMoveCount]);

  const handleCellClick = (row: number, col: number) => {
    // Disable cell clicks when in spectator mode
    if (spectatorMode) {
      return;
    }
    
    if (!isGameStarted || gameState.gameStatus !== 'active' || isReviewMode || isProcessingMove) return;

    if (gameMode === 'online') {
      if (!playerColor || gameState.currentPlayer !== playerColor) return;
      if (gameState.board[row][col] !== null) return;
      if (wouldCreateLineTooLong(gameState.board, row, col, playerColor)) {
        showToast("Illegal move. You may not create a line longer than 4 of your own color");
        
      // Highlight the illegal move cell
      setIllegalMoveCell({ row, col });
      setTimeout(() => {
        setIllegalMoveCell(null);
      }, 1000); // Clear highlight after 1 second
      
      // Play illegal move sound (only if game sounds are enabled)
      if (gameSoundsEnabled) {
        try {
          const preloadedAudio = preloadedSounds.current.illegalMove;
          const isReady = audioReady.current.illegalMove;
          if (preloadedAudio && (isReady || preloadedAudio.readyState >= 2)) {
            const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
            audio.volume = preloadedAudio.volume;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          } else if (preloadedAudio) {
            const audio = preloadedAudio.cloneNode() as HTMLAudioElement;
            audio.volume = preloadedAudio.volume;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          } else {
            const audio = new Audio('/sounds/illegal-move.mp3');
            audio.volume = 0.5;
            audio.currentTime = 0;
            audio.play().catch(() => {});
          }
        } catch (e) {
          // Silently fail - sound errors shouldn't break gameplay
        }
      }
      
      return;
      }
      socket?.emit('makeMove', { gameId, row, col });
    } else {
      // Local game (human vs human or vs AI)
      if (
        (gameMode === 'ai-1' || gameMode === 'ai-2' || gameMode === 'ai-challenger' || gameMode === 'ai-skilled' || gameMode === 'ai-3' || gameMode === 'ai-engine') &&
        playerColor &&
        gameState.currentPlayer !== playerColor
      ) {
        // Not human's turn
        return;
      }
      makeLocalMove(row, col);
    }
  };
//working
  const startGame = (customBoard?: (Cell | null)[][], startingPlayer?: 'white' | 'black') => {
    console.log('===== START GAME CLICKED =====');
    // Use ref to avoid race conditions with async state updates (e.g., when importing position strings)
    const currentGameMode = gameModeRef.current;
    console.log('Game mode:', currentGameMode);
    console.log('Auth state:', {
      isAuthenticated: authState.isAuthenticated,
      isGuest: authState.isGuest,
      user: authState.user,
    });
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Current URL:', window.location.href);
    
    // Exit review mode immediately if currently in review - completely move on from previous game
    setIsReviewMode(false);
    setCurrentReviewMove(0);
    setOriginalGameState(null);
    
    // Close end game modal when starting any game
    hideNotification({ resetContent: true });
    
    // Close mobile controls modal when starting any game
    setShowMobileControls(false);
    
    if (currentGameMode === 'online') {
      console.log('📡 ONLINE MODE SELECTED');
      // Check authentication status for online play
      if (!authState.isAuthenticated && !authState.isGuest) {
        console.log('🔐 User not authenticated - showing login modal');
        console.log('Setting showLogin to true...');
        // Show login modal directly for a more streamlined experience
        setShowLogin(true);
        console.log('Login modal should now be visible');
        return;
      }
      
      // Full verification: Check if user has verified their email
      if (authState.isAuthenticated && authState.user) {
        const user = AuthService.getCurrentUser();
        const isEmailVerified = user?.emailVerified || user?.email_verified || false;
        
        if (!isEmailVerified) {
          console.log('⚠️ User not verified - blocking online game');
          showToast('Please verify your email address before playing online games. Check your inbox for the verification email.', 8000);
          // Optionally open auth modal to show verification status
          setAuthModalMode('signin');
          setAuthModalOpen(true);
          return;
        }
      }
      
      console.log('✅ User authenticated and verified - showing matchmaking modal');
      console.log('Setting showMatchmaking to true...');
      setShowMatchmaking(true);
      console.log('Matchmaking modal should now be visible');
    } else {
      // Local game start
      if (currentGameMode === 'ai-1' || currentGameMode === 'ai-2' || currentGameMode === 'ai-challenger' || currentGameMode === 'ai-skilled' || currentGameMode === 'ai-3' || currentGameMode === 'ai-engine') {
        startAIGame();
        return;
      }
      
      // Check for saved sandbox position first
      let boardToUse = customBoard;
      let playerToUse = startingPlayer;
      
      if (!boardToUse) {
        const savedPosition = localStorage.getItem('sandboxSavedPosition');
        if (savedPosition) {
          try {
            const parsed = JSON.parse(savedPosition);
            boardToUse = parsed.board;
            playerToUse = parsed.startingPlayer || 'white';
            localStorage.removeItem('sandboxSavedPosition'); // Clear after loading
          } catch (e) {
            console.error('Error loading saved sandbox position:', e);
          }
        }
      }
      
      const newBoard = boardToUse ? copyBoard(boardToUse) : copyBoard(INITIAL_BOARD);
      const firstPlayer = playerToUse || 'white';
      
      // Calculate initial scores from Yugos on the board
      let whiteScore = 0;
      let blackScore = 0;
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const cell = newBoard[row][col];
          if (cell && cell.isYugo) {
            if (cell.color === 'white') whiteScore++;
            else blackScore++;
          }
        }
      }
      
      setGameState({
        board: newBoard,
        currentPlayer: firstPlayer,
        scores: { white: whiteScore, black: blackScore },
        gameStatus: 'active',
        lastMove: null,
        players: { 
          white: 'White', 
          black: 'Black'
        },
        igoLine: null
      });
      setIsGameStarted(true);
      isGameStartedRef.current = true;
      setStartingPlayer(firstPlayer); // Track who started the game
      setMoveHistory([]);
      setBoardHistory([]);
      
      if (timerEnabled) {
        initializeTimers();
        setActiveTimer(firstPlayer);
      }
    }
  };

  const findMatch = async () => {
    console.log('findMatch called', {
      hasSocket: !!socket,
      socketConnected: socket?.connected,
      socketId: socket?.id
    });
    
    // Check if user is already in a game on another device/tab
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      // Check if user is already in a game (ApiService doesn't have this method, so we'll skip the check)
      const isAlreadyInGame = false;
      
      if (isAlreadyInGame) {
        showToast('You are already in a game on another device or tab');
        return;
      }
      
      // Mark user as in matchmaking/game
      await ApiService.setInGame(true);
    }
    
    if (socket) {
      console.log('Emitting findMatch to server...');
      // Use selected timer preference for matchmaking
      // If 'any', we'll match with both 5 and 10 minute players
      const timerPreference = matchmakingTimerPreference === 'any' ? 'any' : matchmakingTimerPreference;
      const matchmakingSettings = {
        timerEnabled: true,
        minutesPerPlayer: timerPreference,
        incrementSeconds: 0,
        isRated: matchmakingIsRated
      };
      console.log('Using matchmaking timer preference:', matchmakingSettings);
      socket.emit('findMatch', matchmakingSettings);
      setIsSearchingMatch(true);
    } else {
      console.error('No socket available for findMatch');
      // Clear inGame flag if socket not available
      if (currentUser) {
        await ApiService.setInGame(false);
      }
    }
  };

  const cancelMatchmaking = async () => {
    if (socket) {
      socket.emit('cancelMatchmaking');
    }
    
    // Clear inGame flag when canceling matchmaking
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      await ApiService.setInGame(false);
    }
    
    setShowMatchmaking(false);
    setIsSearchingMatch(false);
  };


  const handlePrivateRoomClick = () => {
    // Check if user is authenticated (not guest, not unauthenticated)
    if (!authState.isAuthenticated || authState.isGuest) {
      // Close matchmaking modal first
      setShowMatchmaking(false);
      // Show toast message and open sidebar auth modal
      showToast('Please sign in to play in private rooms');
      setAuthModalMode('signin');
      setAuthModalOpen(true);
      return;
    }
    // User is authenticated, show room modal
    setShowRoomModal(true);
  };

  const requestRematch = () => {
    console.log('Request rematch clicked!', {
      hasSocket: !!socket,
      gameId,
      socketConnected: socket?.connected,
      gameStatus: gameState.gameStatus,
      socketId: socket?.id
    });

    if (isTournamentGameRef.current || isTournamentGame) {
      showToast('Rematch is not available in tournament games.');
      return;
    }

    if (!socket || !gameId || gameState.gameStatus !== 'finished') {
      console.log('Cannot request rematch - missing requirements');
      return;
    }

    if (rematchState.waitingForResponse) {
      showToast('Already waiting for your opponent to respond.');
      return;
    }

    console.log('Emitting requestRematch to server...', {
      gameId,
      socketId: socket.id,
      connected: socket.connected
    });

    socket.emit('requestRematch', { gameId });
  };

  const respondToRematch = (accept: boolean) => {
    if (isTournamentGameRef.current || isTournamentGame) {
      return;
    }
    if (socket && gameId) {
      socket.emit('respondToRematch', { gameId, accept });
    }
    
    if (accept) {
      // Exit review mode immediately when accepting rematch
      setIsReviewMode(false);
      setCurrentReviewMove(0);
      setOriginalGameState(null);
    } else {
      // Reset rematch state and close modal on decline
      setRematchState({
        requested: false,
        fromPlayer: null,
        requestedBy: '',
        waitingForResponse: false
      });
      pendingResetForfeitureRef.current = null;
    hideNotification({ resetContent: true, preserveBoard: true });
    }
  };

  // Spectator mode emit functions
  const watchGame = (gameIdToWatch: string) => {
    if (socket && socket.connected) {
      // Do not abandon an active online/tournament game to spectate another
      if (
        isGameStartedRef.current &&
        gameModeRef.current === 'online' &&
        playerColorRef.current &&
        gameIdRef.current &&
        gameIdRef.current !== gameIdToWatch
      ) {
        showToast('Finish or leave your current game before watching another.');
        return;
      }

      // Leave previous spectated game so its timer ticks stop fighting the new clocks
      const previousSpectateId = spectatingGameIdRef.current;
      if (previousSpectateId && previousSpectateId !== gameIdToWatch) {
        console.log('Leaving previous spectated game before switch:', previousSpectateId);
        socket.emit('stopWatching', { gameId: previousSpectateId });
      }

      // Clear any existing game state before watching a new game
      // This ensures we're not in an active game when trying to spectate
      if (gameId && gameId !== gameIdToWatch) {
        // Leave current game room if we're in one
        socket.emit('leaveGame', { gameId });
      }
      
      // Mark user as not in game on backend (required for spectating)
      ApiService.setInGame(false);
      
      // Clear local game/timer state so old clocks don't flash over the new game
      setGameId(null);
      gameIdRef.current = null;
      setIsGameStarted(false);
      isGameStartedRef.current = false;
      setMoveHistory([]);
      setBoardHistory([]);
      setIsReviewMode(false);
      setIsImportReviewMode(false);
      setTimers({ white: 600, black: 600 });
      setActiveTimer(null);
      // Mark spectator immediately so a guest Watch click keeps the socket
      // after currentPage becomes 'game' (ack arrives later).
      spectatorModeRef.current = true;
      spectatingGameIdRef.current = gameIdToWatch;
      setSpectatorMode(true);
      setSpectatingGameId(gameIdToWatch);
      
      console.log('Emitting watchGame:', gameIdToWatch);
      socket.emit('watchGame', { gameId: gameIdToWatch });
    } else {
      console.error('Cannot watch game - socket not connected');
      showToast('Connection error. Please refresh the page.');
    }
  };

  const stopWatching = () => {
    const currentGameId = spectatingGameIdRef.current;
    if (socket && currentGameId) {
      console.log('Emitting stopWatching:', currentGameId);
      socket.emit('stopWatching', { gameId: currentGameId });
    }
    
    // Clear spectator state (refs sync so socket handlers see it immediately)
    spectatorModeRef.current = false;
    spectatingGameIdRef.current = null;
    setSpectatorMode(false);
    setSpectatingGameId(null);
    setViewerCount(0);
    setSpectatorTournamentCtx(null);
    
    // Clear game state
    setGameId(null);
    gameIdRef.current = null;
    setIsReviewMode(false);
    setIsImportReviewMode(false);
    setMoveHistory([]);
    setGameState({
      board: copyBoard(INITIAL_BOARD),
      currentPlayer: 'white',
      scores: { white: 0, black: 0 },
      gameStatus: 'waiting',
      lastMove: null,
      players: { white: 'Player 1', black: 'Player 2' },
      igoLine: null
    });
    setTimers({ white: 600, black: 600 });
    setActiveTimer(null);
  };

  const joinSpectatorLobby = () => {
    if (socket && socket.connected) {
      console.log('Joining spectator lobby');
      socket.emit('joinSpectatorLobby');
    } else {
      console.error('Cannot join spectator lobby - socket not connected');
    }
  };

  const leaveSpectatorLobby = () => {
    if (socket && socket.connected) {
      console.log('Leaving spectator lobby');
      socket.emit('leaveSpectatorLobby');
    }
  };

  const resignGame = () => {
    // Show confirmation modal instead of immediately resigning
    setShowResignConfirmation(true);
  };

  const confirmResignation = () => {
    setShowResignConfirmation(false);
    
    if (gameMode === 'online' && socket && gameId) {
      // Online game - send resign to server
      socket.emit('resign', { gameId });
    } else {
      // Local game - handle resignation locally
      const winner = gameState.currentPlayer === 'white' ? 'black' : 'white';
      const winnerName = getCurrentWinnerName(winner);
      setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
      setNotification({
        title: 'Game Over',
        message: `${winnerName} wins by resignation!`,
        show: true
      });
      setIsGameStarted(false);
      isGameStartedRef.current = false;
      setActiveTimer(null);
    }
  };

  const cancelResignation = () => {
    setShowResignConfirmation(false);
  };

  // Desktop: Show resign/draw modal
  const showResignDrawOptions = () => {
    setShowResignDrawModal(true);
  };

  const handleResignFromModal = () => {
    setShowResignDrawModal(false);
    setShowResignConfirmation(true);
  };

  const handleDrawFromModal = () => {
    setShowResignDrawModal(false);
    offerDraw();
  };

  const cancelResignDrawModal = () => {
    setShowResignDrawModal(false);
  };

  // Mobile: Direct draw offer
  const offerDraw = () => {
    // Only allow draw offers in online multiplayer games (not tournaments)
    if (gameMode !== 'online') {
      showToast('Draw offers are only available in online multiplayer games');
      return;
    }
    if (isTournamentGameRef.current || isTournamentGame) {
      showToast('Draw offers are not allowed in tournament games');
      return;
    }
    
    if (gameMode === 'online' && socket && gameId) {
      // Online game - send draw offer to server
      socket.emit('draw-offer', { gameId });
      showToast('Draw offer sent');
    }
  };

  const respondToDrawOffer = (accept: boolean) => {
    setShowDrawOffer(false);
    setPendingDrawFrom(null);
    
    if (accept) {
      if (gameMode === 'online' && socket && gameId) {
        // Online game - send draw acceptance to server
        socket.emit('draw-accept', { gameId });
      } else {
        // Local game - end game as draw
        setGameState(prev => ({ ...prev, gameStatus: 'finished' }));
        setIsGameStarted(false);
        isGameStartedRef.current = false;
        // Add 1 second delay for players to see the final position
        setTimeout(() => {
          setNotification({
            title: 'Game Drawn',
            message: 'The game has ended in a draw by mutual agreement.',
            show: true
          });
        }, 1000);
        setActiveTimer(null);
      }
    } else {
      if (gameMode === 'online' && socket && gameId) {
        // Online game - send draw rejection to server
        socket.emit('draw-decline', { gameId });
      } else {
        // Local game - just close the modal
        showToast('Draw offer declined');
      }
    }
  };

  // Room-based multiplayer functions
  const createRoom = async () => {
    // Check if user is already in a game
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      // Check if user is already in a game (ApiService doesn't have this method, so we'll skip the check)
      const isAlreadyInGame = false;
      
      if (isAlreadyInGame) {
        showToast('You are already in a game on another device or tab');
        return;
      }
      
      // Mark user as in game
      await ApiService.setInGame(true);
    }
    
    if (socket) {
      const timerSettings = {
        minutesPerPlayer: roomTimerPreference,
        incrementSeconds: 0
      };
      socket.emit('createRoom', { timerSettings });
    }
  };

  const joinRoom = async () => {
    if (socket && roomCodeInput.trim()) {
      // Check if user is already in a game
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        // Check if user is already in a game (ApiService doesn't have this method, so we'll skip the check)
      const isAlreadyInGame = false;
        
        if (isAlreadyInGame) {
          showToast('You are already in a game on another device or tab');
          return;
        }
        
        // Mark user as in game
        await ApiService.setInGame(true);
      }
      
      socket.emit('joinRoom', { roomCode: roomCodeInput.trim() });
      setRoomCodeInput('');
    }
  };

  const leaveRoom = async () => {
    if (socket && currentRoom) {
      socket.emit('leaveRoom', { roomCode: currentRoom.code });
      setCurrentRoom(null);
      
      // Clear inGame flag when leaving room
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        await ApiService.setInGame(false);
      }
    }
  };

  const startRoomGame = () => {
    console.log('startRoomGame called', { socket: !!socket, currentRoom, isHost: currentRoom?.isHost });
    if (socket && currentRoom && currentRoom.isHost) {
      console.log('Emitting startRoomGame for room:', currentRoom.code);
      socket.emit('startRoomGame', { 
        roomCode: currentRoom.code,
        timerSettings: {
          timerEnabled: timerEnabled,
          minutesPerPlayer: minutesPerPlayer,
          incrementSeconds: incrementSeconds
        }
      });
    } else {
      console.log('Cannot start room game - missing requirements');
    }
  };

  const emitResetGameToServer = async (activeGameId: string | null) => {
    if (!socket || !activeGameId) {
      return;
    }

    await new Promise<void>((resolve) => {
      let completed = false;
      const timeout = setTimeout(() => {
        if (!completed) {
          console.warn('resetGame acknowledgement timed out');
          completed = true;
          resolve();
        }
      }, 1500);

      try {
        socket.emit('resetGame', { gameId: activeGameId }, () => {
          if (!completed) {
            completed = true;
            clearTimeout(timeout);
            resolve();
          }
        });
      } catch (error) {
        console.error('Failed to emit resetGame event', error);
        if (!completed) {
          completed = true;
          clearTimeout(timeout);
          resolve();
        }
      }
    });
  };

  const resetGame = async () => {
    const activeGameId = gameIdRef.current;
    const currentStatus = gameStateRef.current?.gameStatus;

    // Clear end game modal when resetting game
    hideNotification({ resetContent: true });

    // Clear disconnect modal state when resetting game
    setOpponentDisconnected(false);
    setDisconnectedPlayerColor(null);
    setGracePeriodSeconds(30);
    if (gracePeriodIntervalRef.current) {
      clearInterval(gracePeriodIntervalRef.current);
      gracePeriodIntervalRef.current = null;
    }

    if (!(gameMode === 'online' && currentStatus && currentStatus !== 'active')) {
      await emitResetGameToServer(activeGameId);
      if (gameMode === 'online') {
        pendingResetForfeitureRef.current = 'self';
      }
    } else {
      console.log('Skipping server reset for finished online game');
      pendingResetForfeitureRef.current = null;
    }

    // Clear inGame flag when resetting game
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      await ApiService.setInGame(false);
    }
    
    setGameState({
      board: INITIAL_BOARD,
      currentPlayer: 'white',
      scores: { white: 0, black: 0 },
      gameStatus: 'waiting',
      lastMove: null,
      players: { white: 'White', black: 'Black' },
      igoLine: null
    });
    setIsGameStarted(false);
    isGameStartedRef.current = false;
    setMoveHistory([]);
    setBoardHistory([]);
    setActiveTimer(null);
    setPlayerColor(null);
    playerColorRef.current = null;
    setOpponentName('');
    opponentNameRef.current = ''; // Clear ref too
    setGameId(null);
    gameIdRef.current = null;
    // Reset AI move count for opening phase
    setAiMoveCount(0);
    // Exit review mode if currently in review
    setIsReviewMode(false);
    setIsImportReviewMode(false);
    setCurrentReviewMove(0);
    // Reset game result recording flag for new game
    setGameResultRecorded(false);
    gameResultRecordedRef.current = false;
    setOriginalGameState(null);
    // Update both state and ref synchronously
    isProcessingMoveRef.current = false;
    setIsProcessingMove(false);
    setRematchState({
      requested: false,
      fromPlayer: null,
      requestedBy: '',
      waitingForResponse: false
    });
    if (timerEnabled) {
      initializeTimers();
    }
    
    // Clear any active animations
    setNewlyPlacedDots(new Set());
    setFadingDots(new Set());
  };

  const undoMove = useCallback(() => {
    // Only allow undo for local/AI games
    if (gameMode !== 'local' && !gameMode.startsWith('ai-')) {
      return;
    }
    
    if (boardHistory.length === 0 || moveHistory.length === 0) {
      return;
    }

    const isAIGame = gameMode.startsWith('ai-');
    let movesToUndo = 1;

    // vs AI: take back your last move. If the AI already answered, take that reply too.
    if (isAIGame && playerColor) {
      const latestMove = moveHistory[moveHistory.length - 1];
      if (latestMove.player !== playerColor) {
        movesToUndo = moveHistory.length >= 2 ? 2 : 1;
      }
    }
    
    if (boardHistory.length < movesToUndo || moveHistory.length < movesToUndo) {
      return;
    }
    
    // Get the board state to restore
    const targetBoardIndex = boardHistory.length - movesToUndo;
    const restoredBoard = copyBoard(boardHistory[targetBoardIndex]);
    
    // Calculate scores from restored board
    const restoredScores = {
      white: countYugos(restoredBoard, 'white'),
      black: countYugos(restoredBoard, 'black')
    };
    
    // Side to move is the earliest ply being undone (the turn the player is replaying).
    // Using the latest ply would hand the turn to the AI after a 2-move takeback.
    const earliestUndoneMove = moveHistory[moveHistory.length - movesToUndo];
    const restoredPlayer = earliestUndoneMove ? earliestUndoneMove.player : startingPlayer;
    
    // Get last move from move history (if any moves remain)
    const remainingMoves = moveHistory.length - movesToUndo;
    const lastMove = remainingMoves > 0 && moveHistory[remainingMoves - 1]
      ? { row: moveHistory[remainingMoves - 1].row, col: moveHistory[remainingMoves - 1].col, player: moveHistory[remainingMoves - 1].player }
      : null;

    if (isAIGame && playerColor) {
      const undoneMoves = moveHistory.slice(remainingMoves);
      const aiMovesUndone = undoneMoves.filter(m => m.player !== playerColor).length;
      if (aiMovesUndone > 0) {
        setAiMoveCount(prev => Math.max(0, prev - aiMovesUndone));
      }
    }
    
    // Update board history (remove undone moves)
    setBoardHistory(prev => prev.slice(0, targetBoardIndex));
    
    // Update move history (remove undone moves)
    setMoveHistory(prev => prev.slice(0, remainingMoves));
    
    // Restore game state. Human to move after a takeback, so the AI effect waits.
    setGameState(prev => ({
      ...prev,
      board: restoredBoard,
      currentPlayer: restoredPlayer,
      scores: restoredScores,
      lastMove,
      gameStatus: 'active',
      igoLine: null
    }));

    isProcessingMoveRef.current = false;
    setIsProcessingMove(false);
    
    // Update active timer if timer is enabled
    if (timerEnabled) {
      setActiveTimer(restoredPlayer);
    }
    
    showToast(movesToUndo === 2 ? 'Undone: Player and AI moves' : 'Move undone');
  }, [gameMode, boardHistory, moveHistory, playerColor, startingPlayer, timerEnabled, showToast, countYugos, copyBoard]);

  const getNotation = (col: number, row: number): string => {
    const colLetter = String.fromCharCode(97 + col); // a-h
    const rowNumber = 8 - row; // 8-1
    return colLetter + rowNumber;
  };

  const parseNotation = (notation: string): { col: number; row: number } | null => {
    // Parse notation like "a1", "b2", etc.
    const match = notation.toLowerCase().match(/^([a-h])([1-8])$/);
    if (!match) return null;
    
    const colLetter = match[1];
    const rowNumber = parseInt(match[2], 10);
    
    const col = colLetter.charCodeAt(0) - 97; // a=0, b=1, ..., h=7
    const row = 8 - rowNumber; // rowNumber 1-8 -> row 7-0
    
    if (col < 0 || col > 7 || row < 0 || row > 7) return null;
    
    return { col, row };
  };

  const parseMoveHistory = (text: string): MoveHistoryEntry[] => {
    const moves: MoveHistoryEntry[] = [];
    const cleaned = text.replace(/●/g, '*');
    const lines = cleaned.split('\n').filter(line => line.trim());
    
    for (const line of lines) {
      const match = line.match(/^\s*(\d+)\.\s*([a-h][1-8])(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*([a-h][1-8])?(\*{0,4})?(?:\s*\(\d+\s*yugos?\))?\s*$/i);
      
      if (match) {
        const moveNumber = parseInt(match[1], 10);
        const whiteNotation = match[2];
        const whiteYugos = match[3] ? match[3].length : 0;
        const blackNotation = match[4];
        const blackYugos = match[5] ? match[5].length : 0;
        
        const whiteMove = parseNotation(whiteNotation);
        if (whiteMove) {
          moves.push({
            row: whiteMove.row,
            col: whiteMove.col,
            player: 'white',
            yugos: whiteYugos,
            moveNumber: moveNumber * 2 - 1
          });
        }
        
        if (blackNotation) {
          const blackMove = parseNotation(blackNotation);
          if (blackMove) {
            moves.push({
              row: blackMove.row,
              col: blackMove.col,
              player: 'black',
              yugos: blackYugos,
              moveNumber: moveNumber * 2
            });
          }
        }
      }
    }
    
    return moves;
  };

  // Parse position string format: WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white
  const parsePositionString = (text: string): { board: (Cell | null)[][], startingPlayer: 'white' | 'black' } | null => {
    const trimmed = text.trim();
    if (!trimmed.includes('WM:') && !trimmed.includes('WY:') && !trimmed.includes('BM:') && !trimmed.includes('BY:')) {
      return null; // Doesn't look like a position string
    }

    const board: (Cell | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
    let startingPlayer: 'white' | 'black' = 'white';

    const parts = trimmed.split(';');
    for (const part of parts) {
      if (part.startsWith('START:')) {
        const player = part.split(':')[1].trim().toLowerCase();
        if (player === 'white' || player === 'black') {
          startingPlayer = player;
        }
        continue;
      }

      const [type, squares] = part.split(':');
      if (!squares) continue;

      const squareList = squares.split(',');
      for (const square of squareList) {
        const trimmedSquare = square.trim();
        if (trimmedSquare.length !== 2) continue;
        const col = trimmedSquare.charCodeAt(0) - 97;
        const row = 8 - parseInt(trimmedSquare[1]);
        if (row >= 0 && row < 8 && col >= 0 && col < 8) {
          if (type === 'WM') {
            board[row][col] = { color: 'white', isYugo: false };
          } else if (type === 'WY') {
            board[row][col] = { color: 'white', isYugo: true };
          } else if (type === 'BM') {
            board[row][col] = { color: 'black', isYugo: false };
          } else if (type === 'BY') {
            board[row][col] = { color: 'black', isYugo: true };
          }
        }
      }
    }

    return { board, startingPlayer };
  };

  // Import a position string and start a game from that position
  const importPositionString = (text: string) => {
    const parsed = parsePositionString(text);
    
    if (!parsed) {
      showToast('Invalid position string format. Expected format: WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white');
      return;
    }

    const { board, startingPlayer } = parsed;

    // Disable timer for imported positions (for analysis, not timed play)
    setTimerEnabled(false);
    localStorage.setItem('migoyugoTimerEnabled', 'false');

    // Set game mode to local for imported positions (similar to sandbox)
    setGameMode('local');
    gameModeRef.current = 'local';
    localStorage.setItem('migoyugoGameMode', 'local');

    // Start game with this position
    startGame(board, startingPlayer);
    
    showToast(`Imported position. Starting player: ${startingPlayer === 'white' ? 'White' : 'Black'}`);
  };

  const importMoveHistory = (text: string) => {
    if (spectatorMode) {
      showToast('Cannot import moves while spectating');
      return;
    }
    const parsedMoves = parseMoveHistory(text);
    
    if (parsedMoves.length === 0) {
      showToast('Invalid move history format. Please paste the copied move history.');
      return;
    }
    
    // Replay moves from blank board to build full board history
    const initialBoard = copyBoard(INITIAL_BOARD);
    const history: (Cell | null)[][][] = [];
    let currentBoard = initialBoard;
    let currentPlayer: 'white' | 'black' = 'white';
    let gameOver = false;
    let igoLine: { row: number; col: number }[] | null = null;
    const replayedMoves: MoveHistoryEntry[] = [];

    for (const move of parsedMoves) {
      if (gameOver) break;
      const { row, col } = move;
      if (row < 0 || row > 7 || col < 0 || col > 7) continue;
      if (currentBoard[row][col] !== null) continue;

      history.push(copyBoard(currentBoard));

      const newBoard = copyBoard(currentBoard);
      newBoard[row][col] = { color: currentPlayer, isYugo: false };

      const yugos = checkForYugos(newBoard, row, col, currentPlayer);
      processYugos(newBoard, yugos, row, col);
      if (yugos.length > 0) {
        newBoard[row][col] = { color: currentPlayer, isYugo: true };
      }

      const igo = checkForIgo(newBoard, row, col, currentPlayer);
      if (igo) {
        gameOver = true;
        igoLine = igo;
      } else {
        const nextPlayer = currentPlayer === 'white' ? 'black' : 'white';
        if (!hasLegalMoves(newBoard, nextPlayer)) {
          gameOver = true;
        }
      }

      replayedMoves.push({
        row, col, player: currentPlayer,
        yugos: yugos.length,
        moveNumber: replayedMoves.length + 1
      });

      currentBoard = newBoard;
      if (!gameOver) {
        currentPlayer = currentPlayer === 'white' ? 'black' : 'white';
      }
    }

    const scores = {
      white: countYugos(currentBoard, 'white'),
      black: countYugos(currentBoard, 'black')
    };

    const lastMove = replayedMoves.length > 0
      ? { row: replayedMoves[replayedMoves.length - 1].row, col: replayedMoves[replayedMoves.length - 1].col, player: replayedMoves[replayedMoves.length - 1].player }
      : null;

    // Disable timer and set local mode
    setTimerEnabled(false);
    localStorage.setItem('migoyugoTimerEnabled', 'false');
    setGameMode('local');
    gameModeRef.current = 'local';
    localStorage.setItem('migoyugoGameMode', 'local');

    setGameState({
      board: currentBoard,
      currentPlayer,
      scores,
      gameStatus: gameOver ? 'finished' : 'active',
      lastMove,
      players: { white: 'White', black: 'Black' },
      igoLine
    });

    setMoveHistory(replayedMoves);
    setBoardHistory(history);
    setStartingPlayer('white');
    setIsGameStarted(true);
    isGameStartedRef.current = true;

    // Not review mode — live game
    setIsReviewMode(false);
    setOriginalGameState(null);

    setNewlyPlacedDots(new Set());
    setFadingDots(new Set());
    
    showToast(`Imported ${replayedMoves.length} moves. You can play, undo, or review from here.`);
  };

  // Smart import that detects position string vs move history
  const handleImport = (text: string) => {
    const trimmed = text.trim();
    
    // Check if it's a position string (contains WM:, WY:, BM:, or BY:)
    if (trimmed.includes('WM:') || trimmed.includes('WY:') || trimmed.includes('BM:') || trimmed.includes('BY:')) {
      importPositionString(trimmed);
    } else {
      // Assume it's move history
      importMoveHistory(trimmed);
    }
  };

  const formatMoveHistoryAsText = (): string => {
    if (moveHistory.length === 0) return '';
    
    const totalPairs = Math.ceil(moveHistory.length / 2);
    const numWidth = String(totalPairs).length;
    
    const lines: string[] = [];
    for (let i = 0; i < moveHistory.length; i += 2) {
      const moveNumber = Math.floor(i / 2) + 1;
      const whiteMove = moveHistory[i];
      const blackMove = moveHistory[i + 1];
      
      const numStr = String(moveNumber).padStart(numWidth);
      const whiteStr = (getNotation(whiteMove.col, whiteMove.row) + '*'.repeat(whiteMove.yugos)).padEnd(6);
      
      let line = `${numStr}. ${whiteStr}`;
      if (blackMove) {
        const blackStr = getNotation(blackMove.col, blackMove.row) + '*'.repeat(blackMove.yugos);
        line += `  ${blackStr}`;
      }
      
      lines.push(line);
    }
    
    return lines.join('\n');
  };

  const copyMoveHistory = async () => {
    try {
      const moveHistoryText = formatMoveHistoryAsText();
      if (!moveHistoryText) {
        showToast('No moves to copy');
        return;
      }
      
      await navigator.clipboard.writeText(moveHistoryText);
      showToast('Move history copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      try {
        const moveHistoryText = formatMoveHistoryAsText();
        const textArea = document.createElement('textarea');
        textArea.value = moveHistoryText;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showToast('Move history copied to clipboard!');
      } catch (fallbackErr) {
        showToast('Failed to copy. Please try selecting the text manually.');
      }
    }
  };

  // Tutorial steps configuration
  const tutorialSteps = [
    {
      title: "Basic Gameplay",
      message: "<span style=\"color: red; font-weight: bold;\">Migoyugo</span> is played on an 8×8 board.<br>Players alternate turns,<br>white moves first, then black,<br>placing pieces called <span style=\"color: red; font-weight: bold;\">Migos</span> on empty squares.",
      demo: "board"
    },
    {
      title: "Building a Yugo",
      message: "Your first tactical step is to create a <span style=\"color: red; font-weight: bold;\">Yugo</span>. A Yugo is created when you build an unbroken line of exactly 4 pieces of your own color, horizontal, vertical, or diagonal.<br><br>When a <span style=\"color: red; font-weight: bold;\">Yugo</span> is created, it is identified with a red mark in the middle of the piece. At that same moment, all Migos in the line of 4 disappear, while any Yugos already in that line remain. Yugos can never be moved or removed from the board.",
      demo: "yugo-formation"
    },
    {
      title: "No Long Lines",
      message: "At no time may either player create a line of more than 4 in a row of any combination of Migos and/or Yugos <span style=\"color: red; font-weight: bold;\">of the same color</span>.",
      demo: "long-line"
    },
    {
      title: "The Winning Goal",
      message: "Win by forming an <span style=\"color: red; font-weight: bold;\">Igo</span>. An Igo is an unbroken line of 4 Yugos of one color, horizontal, vertical or diagonal.",
      demo: "igo"
    },
    {
      title: "Alternative Win",
      message: "<b>No legal moves:</b><br>If at any time either player is unable to play a legal move, or all 64 squares are covered, the game ends with a <span style=\"color: red; font-weight: bold;\">Wego</span>, and the player with the most Yugos wins. If both players have the same number of Yugos, the game is a draw by Wego.<br><br><b>Timer expiry:</b><br>If players have chosen to play using a timer, the game will end immediately if one player runs out of time, and the opponent will be awarded the win.<br><br><b>Resignation:</b><br>A player may choose to resign a game at any point and this will award the win to their opponent.",
      demo: null
    },
    {
      title: "How to Start a Multiplayer Game",
      message: `
        <b>Select 'Opponent':</b><br>
        On the main menu, find 'Opponent', and select 'Online Multiplayer'.<br><br>
        <b>Choose How to Play:</b><br>
        <ul style='margin:0 0 0 1.2em;padding:0;'>
          <li><b>Quick Match:</b> Click "Quick Match" to be paired with a random online opponent. The game will start automatically when a match is found.</li>
          <li><b>Create a Room:</b> Click "Create Room" to start a private game. You'll get a unique room code to share with a friend.</li>
          <li><b>Join a Room:</b> If your friend has already created a room, enter the room code they give you and click "Join Room."</li>
          <li><b>Play as Guest:</b> If you don't want to sign in, you can choose "Play as Guest" to join multiplayer games without creating an account.</li>
        </ul>
        <br>
        <b>Wait for Your Opponent:</b><br>
        Once both players are in the room (or a match is found), the game will begin automatically.
      `,
      demo: null
    },
    {
      title: "Ready to Play!",
      message: "You have two options - play against a human opponent or try your luck against one of the AI levels.<br><br>You can play with a timer or without.<br>Choose from a 3-minute game or up to an hour on the clock.<br>You can even choose increments from 2 to 10 seconds which add time to your clock after every move.<br>Once you run out of time, it's game over.<br><br>Is it better to build your own Yugos or block your opponent?<br>Will you go for an Igo or fill the board and see who ends up with the most Yugos?<br>The options are endless.<br><br>That's all you need to know!<br>Click 'Start' and enjoy playing <span style=\"color: red; font-weight: bold;\">Migoyugo</span>!",
      demo: null
    }
  ];

  // Tutorial navigation functions
  const nextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      closeTutorial();
    }
  };

  const prevTutorialStep = () => {
    if (tutorialStep > 0) {
      setTutorialStep(tutorialStep - 1);
    }
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialStep(0);
  };

  // Review mode functions
  const enterReviewMode = () => {
    console.log('🔍 [REVIEW] enterReviewMode called', {
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: moveHistoryRef.current.length,
      isProcessingMove,
      isReviewMode
    });
    
    if (moveHistory.length === 0) {
      console.log('❌ [REVIEW] enterReviewMode blocked: moveHistory.length === 0');
      return;
    }
    
    // Clear any pending setTimeout from goToMove to prevent blocking
    if (reviewModeProcessingTimeoutRef.current) {
      console.log('🧹 [REVIEW] Clearing pending timeout');
      clearTimeout(reviewModeProcessingTimeoutRef.current);
      reviewModeProcessingTimeoutRef.current = null;
    }
    
    // Reset processing state to ensure navigation works (update both state and ref synchronously)
    isProcessingMoveRef.current = false;
    setIsProcessingMove(false);
    
    setOriginalGameState({ ...gameState });
    setIsReviewMode(true);
    
    // Clear any active animations
    setNewlyPlacedDots(new Set());
    setFadingDots(new Set());
    
    // Start at the last move (final position) instead of move 0
    const finalMoveIndex = moveHistory.length;
    console.log('✅ [REVIEW] enterReviewMode calling goToMove with index:', finalMoveIndex);
    goToMove(finalMoveIndex);
  };

  const exitReviewMode = () => {
    // Clear any pending setTimeout from goToMove to prevent race conditions
    if (reviewModeProcessingTimeoutRef.current) {
      clearTimeout(reviewModeProcessingTimeoutRef.current);
      reviewModeProcessingTimeoutRef.current = null;
    }
    
    // Reset processing state immediately (synchronously - update both state and ref)
    isProcessingMoveRef.current = false;
    setIsProcessingMove(false);
    
    if (originalGameState) {
      setGameState(originalGameState);
      setOriginalGameState(null);
    }
    setIsReviewMode(false);
    setIsImportReviewMode(false);
    setCurrentReviewMove(0);
    setOriginalGameState(null);
  };

  const goToMove = useCallback((moveIndex: number) => {
    // Use refs to get current values (avoid stale closures)
    const currentMoveHistory = moveHistoryRef.current;
    const currentlyProcessing = isProcessingMoveRef.current;
    
    console.log('🔍 [REVIEW] goToMove called', {
      moveIndex,
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: currentMoveHistory.length,
      isProcessingMove,
      isProcessingMoveRef: currentlyProcessing,
      currentReviewMove: currentReviewMoveRef.current,
      check1: moveIndex < 0,
      check2: moveIndex > currentMoveHistory.length,
      check3: currentlyProcessing
    });
    
    if (moveIndex < 0) {
      console.log('❌ [REVIEW] goToMove blocked: moveIndex < 0');
      return;
    }
    if (moveIndex > currentMoveHistory.length) {
      console.log('❌ [REVIEW] goToMove blocked: moveIndex > moveHistory.length', {
        moveIndex,
        moveHistoryLength: moveHistory.length,
        moveHistoryRefLength: currentMoveHistory.length
      });
      return;
    }
    if (currentlyProcessing) {
      console.log('❌ [REVIEW] goToMove blocked: isProcessingMoveRef.current === true');
      return;
    }
    
    console.log('✅ [REVIEW] goToMove proceeding with moveIndex:', moveIndex);
    // Update both state and ref synchronously
    isProcessingMoveRef.current = true;
    setIsProcessingMove(true);
    
    try {
      setCurrentReviewMove(moveIndex);
      currentReviewMoveRef.current = moveIndex;
      
      // Reconstruct board state up to this move
      const board = copyBoard(INITIAL_BOARD);
      let currentPlayer: 'white' | 'black' = 'white';
      
      for (let i = 0; i < moveIndex; i++) {
        const move = currentMoveHistory[i];
        if (!move) {
          console.error('❌ [REVIEW] goToMove: moveHistory[' + i + '] is undefined!', {
            moveIndex,
            i,
            moveHistoryLength: moveHistory.length,
            moveHistoryRefLength: currentMoveHistory.length,
            moveHistory: moveHistory,
            currentMoveHistory: currentMoveHistory
          });
          break;
        }
        const { row, col, player } = move;
        
        // Place the migo
        board[row][col] = { color: player, isYugo: false };
        
        // Check for yugos and process them
        const yugos = checkForYugos(board, row, col, player);
        const { yugoFormed } = processYugos(board, yugos, row, col);
        
        // If yugos were formed, make this cell a yugo
        if (yugoFormed) {
          board[row][col] = { color: player, isYugo: true };
        }
        
        currentPlayer = player === 'white' ? 'black' : 'white';
      }
      
      const scores = {
        white: countYugos(board, 'white'),
        black: countYugos(board, 'black')
      };
      
      const lastMove = moveIndex > 0 ? {
        row: currentMoveHistory[moveIndex - 1].row,
        col: currentMoveHistory[moveIndex - 1].col,
        player: currentMoveHistory[moveIndex - 1].player
      } : null;
      
      // Check for igo at the final position if this is the last move
      let igoLine: { row: number; col: number }[] | null = null;
      if (moveIndex === currentMoveHistory.length && lastMove) {
        igoLine = checkForIgo(board, lastMove.row, lastMove.col, lastMove.player);
      }
      
      console.log('✅ [REVIEW] goToMove updating gameState', {
        moveIndex,
        scores,
        lastMove,
        currentPlayer
      });
      
      setGameState(prev => ({
        ...prev,
        board,
        currentPlayer,
        scores,
        lastMove,
        igoLine
      }));
    } catch (error) {
      console.error('❌ [REVIEW] goToMove error:', error);
    } finally {
      // Reset processing state synchronously (no setTimeout delay needed)
      // Update both state and ref immediately to prevent blocking next navigation
      console.log('✅ [REVIEW] goToMove finished: setting isProcessingMove to false');
      isProcessingMoveRef.current = false;
      setIsProcessingMove(false);
      // Clear any pending timeout (shouldn't exist, but safety check)
      if (reviewModeProcessingTimeoutRef.current) {
        clearTimeout(reviewModeProcessingTimeoutRef.current);
        reviewModeProcessingTimeoutRef.current = null;
      }
    }
  }, []); // Empty dependency array - uses refs for current values

  // Auto-scroll active move into view in review mode
  useEffect(() => {
    if (isReviewMode && activeReviewMoveRef.current) {
      activeReviewMoveRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [currentReviewMove, isReviewMode]);

  // Cleanup: Clear review mode processing timeout when exiting review mode or component unmounts
  useEffect(() => {
    if (!isReviewMode && reviewModeProcessingTimeoutRef.current) {
      clearTimeout(reviewModeProcessingTimeoutRef.current);
      reviewModeProcessingTimeoutRef.current = null;
      // Update both state and ref synchronously
      isProcessingMoveRef.current = false;
      setIsProcessingMove(false);
    }
    
    return () => {
      // Cleanup on unmount
      if (reviewModeProcessingTimeoutRef.current) {
        clearTimeout(reviewModeProcessingTimeoutRef.current);
        reviewModeProcessingTimeoutRef.current = null;
      }
    };
  }, [isReviewMode]);

  const firstMove = () => {
    console.log('🔍 [REVIEW] firstMove called', {
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: moveHistoryRef.current.length
    });
    goToMove(0);
  };

  const lastMove = () => {
    console.log('🔍 [REVIEW] lastMove called', {
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: moveHistoryRef.current.length
    });
    goToMove(moveHistory.length);
  };

  const previousMove = useCallback(() => {
    console.log('🔍 [REVIEW] previousMove called', {
      currentReviewMove,
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: moveHistoryRef.current.length
    });
    if (currentReviewMove > 0) {
      console.log('✅ [REVIEW] previousMove calling goToMove with:', currentReviewMove - 1);
      goToMove(currentReviewMove - 1);
    } else {
      console.log('❌ [REVIEW] previousMove blocked: currentReviewMove <= 0');
    }
  }, [currentReviewMove, goToMove, moveHistory.length]);

  const nextMove = useCallback(() => {
    console.log('🔍 [REVIEW] nextMove called', {
      currentReviewMove,
      moveHistoryLength: moveHistory.length,
      moveHistoryRefLength: moveHistoryRef.current.length,
      comparison: currentReviewMove < moveHistory.length
    });
    if (currentReviewMove < moveHistory.length) {
      console.log('✅ [REVIEW] nextMove calling goToMove with:', currentReviewMove + 1);
      goToMove(currentReviewMove + 1);
    } else {
      console.log('❌ [REVIEW] nextMove blocked: currentReviewMove >= moveHistory.length');
    }
  }, [currentReviewMove, moveHistory.length, goToMove]);

  // Keyboard navigation for review mode with hold-to-scroll
  useEffect(() => {
    if (!isReviewMode) return; // Only active when in review mode

    let keyHoldInterval: NodeJS.Timeout | null = null;
    let keyHoldTimeout: NodeJS.Timeout | null = null;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when review mode is active
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); // Prevent default scrolling
        
        // Clear any existing intervals
        if (keyHoldInterval) {
          clearInterval(keyHoldInterval);
          keyHoldInterval = null;
        }
        if (keyHoldTimeout) {
          clearTimeout(keyHoldTimeout);
          keyHoldTimeout = null;
        }

        const direction = event.key === 'ArrowLeft' ? 'prev' : 'next';

        // Immediate first move
        if (direction === 'prev' && currentReviewMove > 0) {
          goToMove(currentReviewMove - 1);
        } else if (direction === 'next' && currentReviewMove < moveHistory.length) {
          goToMove(currentReviewMove + 1);
        }

        // Wait 0.5 seconds before starting continuous scroll
        keyHoldTimeout = setTimeout(() => {
          // Then continue scrolling every 333ms (3 moves per second)
          keyHoldInterval = setInterval(() => {
            // Use ref to get current value (avoids closure issues)
            const currentMove = currentReviewMoveRef.current;
            if (direction === 'prev') {
              if (currentMove > 0) {
                goToMove(currentMove - 1);
              } else {
                if (keyHoldInterval) {
                  clearInterval(keyHoldInterval);
                  keyHoldInterval = null;
                }
              }
            } else {
              if (currentMove < moveHistory.length) {
                goToMove(currentMove + 1);
              } else {
                if (keyHoldInterval) {
                  clearInterval(keyHoldInterval);
                  keyHoldInterval = null;
                }
              }
            }
          }, 333);
        }, 500);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Clear intervals when key is released
        if (keyHoldInterval) {
          clearInterval(keyHoldInterval);
          keyHoldInterval = null;
        }
        if (keyHoldTimeout) {
          clearTimeout(keyHoldTimeout);
          keyHoldTimeout = null;
        }
      }
    };

    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (keyHoldInterval) {
        clearInterval(keyHoldInterval);
      }
      if (keyHoldTimeout) {
        clearTimeout(keyHoldTimeout);
      }
    };
  }, [isReviewMode, currentReviewMove, moveHistory.length, goToMove]); // Dependencies

  // Hold-to-scroll functionality
  const startHoldScroll = (direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    console.log('🔍 [REVIEW] startHoldScroll called', {
      direction,
      currentReviewMove,
      moveHistoryLength: moveHistory.length
    });
    event?.preventDefault();
    
    // Clear any existing timeouts/intervals
    if (holdScrollInterval) {
      clearTimeout(holdScrollInterval);
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    
    // Record start time
    holdStartTimeRef.current = Date.now();
    
    const scrollFunction = direction === 'prev' ? previousMove : nextMove;
    console.log('✅ [REVIEW] startHoldScroll setting timeout, will call:', direction === 'prev' ? 'previousMove' : 'nextMove');
    
    // Wait 0.5 seconds before executing first move and starting continuous scroll
    holdScrollTimeoutRef.current = setTimeout(() => {
      // Execute first move after 0.5 seconds
      console.log('⏰ [REVIEW] startHoldScroll timeout fired, calling scrollFunction');
      scrollFunction();
      
      // Then continue scrolling every 333ms (3 moves per second)
      const interval = setInterval(() => {
        scrollFunction();
      }, 333);
      
      setHoldScrollInterval(interval);
    }, 500);
  };

  const stopHoldScroll = (direction: 'prev' | 'next', event?: React.MouseEvent | React.TouchEvent) => {
    console.log('🔍 [REVIEW] stopHoldScroll called', {
      direction,
      holdStartTime: holdStartTimeRef.current,
      currentReviewMove,
      moveHistoryLength: moveHistory.length
    });
    event?.preventDefault();
    
    const scrollFunction = direction === 'prev' ? previousMove : nextMove;
    
    // Check if this was a quick click (< 0.5 seconds)
    if (holdStartTimeRef.current !== null) {
      const holdDuration = Date.now() - holdStartTimeRef.current;
      console.log('🔍 [REVIEW] stopHoldScroll holdDuration:', holdDuration);
      
      if (holdDuration < 500) {
        // Quick click: execute ONE move
        console.log('✅ [REVIEW] stopHoldScroll quick click detected, calling scrollFunction');
        if (holdScrollTimeoutRef.current) {
          clearTimeout(holdScrollTimeoutRef.current);
          holdScrollTimeoutRef.current = null;
        }
        scrollFunction();
      }
      
      holdStartTimeRef.current = null;
    }
    
    // Clear any existing timeout or interval
    if (holdScrollTimeoutRef.current) {
      clearTimeout(holdScrollTimeoutRef.current);
      holdScrollTimeoutRef.current = null;
    }
    if (holdScrollInterval) {
      clearInterval(holdScrollInterval);
      setHoldScrollInterval(null);
    }
  };

  // Cleanup hold timeout and interval when component unmounts or review mode exits
  useEffect(() => {
    return () => {
      if (holdScrollInterval) {
        clearTimeout(holdScrollInterval);
        clearInterval(holdScrollInterval);
      }
      if (holdScrollTimeoutRef.current) {
        clearTimeout(holdScrollTimeoutRef.current);
      }
    };
  }, [holdScrollInterval]);

  useEffect(() => {
    if (!isReviewMode) {
      if (holdScrollInterval) {
        clearTimeout(holdScrollInterval);
        clearInterval(holdScrollInterval);
        setHoldScrollInterval(null);
      }
      if (holdScrollTimeoutRef.current) {
        clearTimeout(holdScrollTimeoutRef.current);
        holdScrollTimeoutRef.current = null;
      }
      holdStartTimeRef.current = null;
    }
  }, [isReviewMode, holdScrollInterval]);



  const renderMoveHistory = () => {
    return (
      <>
        <div 
          ref={moveHistoryScrollRef}
          className={`game-log ${spectatorMode ? 'spectator-mode' : ''}`}
          id="game-log"
          onScroll={handleMoveHistoryScroll}
          onMouseLeave={() => setHistoryHoverIndex(null)}
          style={spectatorMode ? { height: '250px', minHeight: '250px', maxHeight: '250px' } : undefined}
        >
          {Array.from({ length: Math.ceil(moveHistory.length / 2) }, (_, pairIndex) => {
            let whiteMove: MoveHistoryEntry | undefined;
            let blackMove: MoveHistoryEntry | undefined;
            let whiteMoveIndex: number;
            let blackMoveIndex: number;
            
            if (startingPlayer === 'black' && pairIndex === 0) {
              // First pair when black starts: show "--" for white, black's first move
              whiteMove = undefined;
              blackMove = moveHistory[0];
              whiteMoveIndex = -1; // No white move
              blackMoveIndex = 0;
            } else if (startingPlayer === 'black') {
              // Subsequent pairs when black starts: offset by 1
              whiteMove = moveHistory[pairIndex * 2 - 1];
              blackMove = moveHistory[pairIndex * 2];
              whiteMoveIndex = pairIndex * 2 - 1;
              blackMoveIndex = pairIndex * 2;
            } else {
              // Normal case: white starts first
              whiteMove = moveHistory[pairIndex * 2];
              blackMove = moveHistory[pairIndex * 2 + 1];
              whiteMoveIndex = pairIndex * 2;
              blackMoveIndex = pairIndex * 2 + 1;
            }
            
            const moveNumber = pairIndex + 1;
            
            // Adjust highlighting logic for black starting first
            let isWhiteActive = false;
            let isBlackActive = false;
            
            if (isReviewMode) {
              if (startingPlayer === 'black' && pairIndex === 0) {
                // First pair when black starts: highlight black move at index 0
                isBlackActive = currentReviewMove - 1 === 0;
              } else if (startingPlayer === 'black') {
                // Subsequent pairs: adjust indices
                isWhiteActive = whiteMove !== undefined && currentReviewMove - 1 === whiteMoveIndex;
                isBlackActive = blackMove !== undefined && currentReviewMove - 1 === blackMoveIndex;
              } else {
                // Normal case
                isWhiteActive = whiteMove !== undefined && currentReviewMove - 1 === whiteMoveIndex;
                isBlackActive = blackMove !== undefined && currentReviewMove - 1 === blackMoveIndex;
              }
            }
            
            const isActive = isWhiteActive || isBlackActive;
            
            return (
              <div 
                key={pairIndex} 
                ref={isActive ? activeReviewMoveRef : null}
                className="log-entry"
              >
                <span className="move-number">{moveNumber}.</span>
                <span 
                  className={`white-move ${isWhiteActive ? 'highlighted-move' : ''} ${historyHoverIndex === whiteMoveIndex ? 'history-hover-move' : ''} ${!whiteMove && startingPlayer === 'black' && pairIndex === 0 ? 'no-move' : ''}`}
                  onClick={() => {
                    if (whiteMove && isReviewMode) {
                      goToMove(whiteMoveIndex + 1);
                    }
                  }}
                  onMouseEnter={() => {
                    if (whiteMove) setHistoryHoverIndex(whiteMoveIndex);
                  }}
                  onMouseLeave={() => setHistoryHoverIndex(null)}
                  style={{ cursor: isReviewMode && whiteMove ? 'pointer' : 'default' }}
                >
                  {startingPlayer === 'black' && pairIndex === 0 && !whiteMove ? (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>--</span>
                  ) : whiteMove ? (
                    <span>
                      {getNotation(whiteMove.col, whiteMove.row)}
                      {whiteMove.yugos > 0 && <span className="yugo-indicator">{'*'.repeat(whiteMove.yugos)}</span>}
                    </span>
                  ) : ''}
                </span>
                <span 
                  className={`black-move ${isBlackActive ? 'highlighted-move' : ''} ${historyHoverIndex === blackMoveIndex ? 'history-hover-move' : ''}`}
                  onClick={() => {
                    if (blackMove && isReviewMode) {
                      goToMove(blackMoveIndex + 1);
                    }
                  }}
                  onMouseEnter={() => {
                    if (blackMove) setHistoryHoverIndex(blackMoveIndex);
                  }}
                  onMouseLeave={() => setHistoryHoverIndex(null)}
                  style={{ cursor: isReviewMode && blackMove ? 'pointer' : 'default' }}
                >
                  {blackMove ? (
                    <span>
                      {getNotation(blackMove.col, blackMove.row)}
                      {blackMove.yugos > 0 && <span className="yugo-indicator">{'*'.repeat(blackMove.yugos)}</span>}
                    </span>
                  ) : ''}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Review mode controls - NOW OUTSIDE game-log */}
        {/* Show review controls when in review mode */}
        {isReviewMode && (
          <div id="review-section">
            <div className="review-controls">
              <button 
                className="btn" 
                id="first-move-btn"
                onClick={() => firstMove()}
                disabled={currentReviewMove <= 0}
                title="First Move"
              >
                <span className="arrow-icon">⏮</span>
              </button>
              <button 
                className="btn" 
                id="prev-move-btn"
                onMouseDown={(e) => startHoldScroll('prev', e)}
                onMouseUp={(e) => stopHoldScroll('prev', e)}
                onMouseLeave={(e) => stopHoldScroll('prev', e)}
                onTouchStart={(e) => startHoldScroll('prev', e)}
                onTouchEnd={(e) => stopHoldScroll('prev', e)}
                disabled={currentReviewMove <= 0}
                title="Previous Move (Hold to scroll)"
              >
                <span className="arrow-icon">◀</span>
              </button>
              <button 
                className="btn" 
                id="next-move-btn"
                onMouseDown={(e) => startHoldScroll('next', e)}
                onMouseUp={(e) => stopHoldScroll('next', e)}
                onMouseLeave={(e) => stopHoldScroll('next', e)}
                onTouchStart={(e) => startHoldScroll('next', e)}
                onTouchEnd={(e) => stopHoldScroll('next', e)}
                disabled={currentReviewMove >= moveHistory.length}
                title="Next Move (Hold to scroll)"
              >
                <span className="arrow-icon">▶</span>
              </button>
              <button 
                className="btn" 
                id="last-move-btn"
                onClick={() => lastMove()}
                disabled={currentReviewMove >= moveHistory.length}
                title="Last Move / Live"
              >
                <span className="arrow-icon">⏭</span>
              </button>
              {moveHistory.length > 0 && (
                <button
                  className="btn"
                  id="copy-move-history-btn"
                  onClick={copyMoveHistory}
                  title="Copy move history to clipboard"
                  style={{
                    width: 'auto',
                    minWidth: '36px',
                    maxWidth: 'none',
                    fontSize: '9px',
                    padding: '0 4px',
                    height: '24px',
                    lineHeight: '1',
                    margin: '0 2px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📋 Copy
                </button>
              )}
            </div>
            <div style={{ textAlign: 'center', marginTop: '5px' }}>
                <button 
                className="btn" 
                onClick={() => exitReviewMode()}
                style={{ fontSize: '10px', padding: '2px 8px', height: '20px', lineHeight: '1' }}
              >
                Exit Review
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // Add state for player color choice
  const [playerColorChoice, setPlayerColorChoice] = useState<'white' | 'black' | 'random'>('white');

  // Save game settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('migoyugoGameMode', gameMode);
  }, [gameMode]);

  useEffect(() => {
    localStorage.setItem('migoyugoTimerEnabled', timerEnabled.toString());
  }, [timerEnabled]);

  useEffect(() => {
    localStorage.setItem('migoyugoMinutesPerPlayer', minutesPerPlayer.toString());
  }, [minutesPerPlayer]);

  useEffect(() => {
    localStorage.setItem('migoyugoIncrementSeconds', incrementSeconds.toString());
  }, [incrementSeconds]);

  useEffect(() => {
    localStorage.setItem('migoyugoPlayerColorChoice', playerColorChoice);
  }, [playerColorChoice]);

  // When starting a new AI game, determine playerColor based on playerColorChoice
  const startAIGame = () => {
    const mode = gameModeRef.current;
    // Exit review mode immediately if currently in review - completely move on from previous game
    setIsReviewMode(false);
    setCurrentReviewMove(0);
    setOriginalGameState(null);
    
    // Reset AI move count for opening phase
    setAiMoveCount(0);
    botFinishTrackedRef.current = false;
    
    // Defensive: default to 'white' if playerColorChoice is null/undefined
    let chosenColor: 'white' | 'black';
    if (playerColorChoice === 'random') {
      chosenColor = Math.random() < 0.5 ? 'white' : 'black';
    } else if (playerColorChoice === 'white' || playerColorChoice === 'black') {
      chosenColor = playerColorChoice;
    } else {
      chosenColor = 'white'; // fallback default
    }
    setPlayerColor(chosenColor);

    // Get player name - use username if authenticated, otherwise 'Guest'
    const humanPlayerName = authState.isAuthenticated && authState.user?.username 
      ? authState.user.username 
      : 'Guest';
    const aiPlayerName =
      mode === 'ai-1'
        ? 'AI - Beginner'
        : mode === 'ai-2'
          ? 'AI - Novice'
          : mode === 'ai-challenger'
            ? 'AI - Intermediate'
            : mode === 'ai-skilled'
              ? 'AI - Advanced'
              : mode === 'ai-3'
                ? 'AI - Expert'
                : mode === 'ai-engine'
                  ? 'AI - Master'
                  : 'AI';

    // Initialize the game state - White always goes first
    const newBoard = copyBoard(INITIAL_BOARD);
    setGameState({
      board: newBoard,
      currentPlayer: 'white',
      scores: { white: 0, black: 0 },
      gameStatus: 'active',
      lastMove: null,
      players: {
        white: chosenColor === 'white' ? humanPlayerName : aiPlayerName,
        black: chosenColor === 'black' ? humanPlayerName : aiPlayerName
      },
      igoLine: null
    });
    setIsGameStarted(true);
    isGameStartedRef.current = true;
    setStartingPlayer('white'); // AI games always start with white
    setMoveHistory([]);
    setBoardHistory([]);

    if (timerEnabled) {
      initializeTimers();
      setActiveTimer(chosenColor);
    }

    trackBotStart(mode);
  };

  const handleHumanAvailableDecline = () => {
    if (!humanAvailablePrompt) return;
    waitlistPlayerCooldownRef.current.set(humanAvailablePrompt.id, Date.now() + 30 * 60 * 1000);
    setHumanAvailablePrompt(null);
  };

  const handleHumanAvailableAccept = async () => {
    if (!humanAvailablePrompt) return;
    setHumanAvailableBusy(true);
    try {
      const gm = gameModeRef.current;
      if (typeof gm === 'string' && gm.startsWith('ai-') && isGameStartedRef.current) {
        await resetGame();
      }
      const result = await ApiService.sendChallenge(humanAvailablePrompt.id, { minutesPerPlayer: 10, incrementSeconds: 0 }, true);
      if (result.success) {
        showToast(`Challenge sent to ${humanAvailablePrompt.username}`);
        waitlistPlayerCooldownRef.current.set(humanAvailablePrompt.id, Date.now() + 30 * 60 * 1000);
      } else {
        showToast(result.error || 'Could not send challenge');
      }
    } finally {
      setHumanAvailableBusy(false);
      setHumanAvailablePrompt(null);
    }
  };

  // Add this helper function near the minimax definition:
  function iterativeDeepeningMinimax(
    board: (Cell | null)[][],
    maxTimeMs: number,
    playerColor: 'white' | 'black',
    transTable: TranspositionTable,
    maxDepth: number = 3 // Add a default max depth cap
  ): { row: number; col: number } | null {
    const start = Date.now();
    let bestMove: { row: number; col: number } | null = null;
    let depth = 1;
    while (depth <= maxDepth) { // Only go up to maxDepth
      const now = Date.now();
      if (now - start > maxTimeMs) break;
      const result = minimax(board, depth, -Infinity, Infinity, true, playerColor, transTable, quiescence);
      if (result.bestMove) {
        bestMove = result.bestMove;
      }
      depth++;
    }
    return bestMove;
  }

  // AI move selection for different difficulty levels doing for es lint
  // const getAIMove = (
  //   board: (Cell | null)[][],
  //   difficulty: 'ai-1' | 'ai-2' | 'ai-3'
  // ): { row: number; col: number } | null => {
  //   const validMoves: { row: number; col: number; score: number }[] = [];
  //   for (let row = 0; row < 8; row++) {
  //     for (let col = 0; col < 8; col++) {
  //       if (isValidMove(board, row, col, 'black')) {
  //         const score = evaluateMove(board, row, col, 'black', difficulty);
  //         validMoves.push({ row, col, score });
  //       }
  //     }
  //   }
  //   if (validMoves.length === 0) return null;
  //   if (difficulty === 'ai-1') {
  //     validMoves.sort((a, b) => b.score - a.score);
  //     const topMoves = validMoves.slice(0, Math.min(3, validMoves.length));
  //     return topMoves[Math.floor(Math.random() * topMoves.length)];
  //   } else if (difficulty === 'ai-2') {
  //     validMoves.sort((a, b) => b.score - a.score);
  //     const criticalMoves = validMoves.filter(move => move.score >= 800);
  //     if (criticalMoves.length > 0) {
  //       return criticalMoves[0];
  //     }
  //     const topMoves = validMoves.slice(0, Math.min(5, validMoves.length));
  //     return topMoves[Math.floor(Math.random() * topMoves.length)];
  //   } else { // AI-3 now uses MCTS
  //     return mcts(board, 'black', 200, 8);
  //   }
  // };

  /** Tournament banners/modals on every route (socket events are global). */
  const globalTournamentOverlaysEl = (
    <GlobalTournamentOverlays
      tournamentBanner={tournamentBanner}
      onBannerGoToTournament={() => {
        handleTournamentsClick();
        setTournamentBanner(null);
      }}
      onBannerDismiss={() => setTournamentBanner(null)}
      tournamentReadyCheck={tournamentReadyCheck}
      tournamentRCCountdown={tournamentRCCountdown}
      tournamentRCAccepted={tournamentRCAccepted}
      opponentPeerReady={tournamentOpponentPeerReady}
      myUsername={authState.user?.username}
      onAcceptTournamentReady={handleAcceptTournamentReady}
      championCelebration={tournamentChampionCelebration}
      onDismissChampionCelebration={() => setTournamentChampionCelebration(null)}
    />
  );

  const welcomeAndHumanModalsEl = (
    <HumanAvailableModal
      isOpen={!!humanAvailablePrompt}
      username={humanAvailablePrompt?.username ?? ''}
      onAccept={handleHumanAvailableAccept}
      onDecline={handleHumanAvailableDecline}
      busy={humanAvailableBusy}
    />
  );

  const sharedNavOverlaysEl = (
    <>
      {/* Settings popup */}
      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          currentTheme={currentTheme}
          customColors={customColors}
          gameSoundsEnabled={gameSoundsEnabled}
          notificationsEnabled={notificationsEnabled}
          illegalHoverEnabled={illegalHoverEnabled}
          dmPrivacy={dmPrivacy}
          isAuthenticated={authState.isAuthenticated && !authState.isGuest}
          onClose={() => setShowSettings(false)}
          onThemeChange={handleThemeChange}
          onCustomColorChange={handleCustomColorChange}
          onGameSoundsToggle={handleGameSoundsToggle}
          onNotificationsToggle={handleNotificationsToggle}
          onIllegalHoverToggle={handleIllegalHoverToggle}
          onDmPrivacyChange={handleDmPrivacyChange}
          onResetSettings={resetSettings}
        />
      )}

      {/* Login modal */}
      {showLogin && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowLogin(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>Play Online</h2>
            <div className="notification-buttons">
              <button type="button" className="btn" onClick={() => { setShowLogin(false); handlePlayAsGuestWrapper(); }}>
                Play as Guest
              </button>
              <button type="button" className="btn" onClick={() => setShowLogin(false)}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* Signup modal */}
      {showSignup && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowSignup(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>Play Online</h2>
            <div className="notification-buttons">
              <button type="button" className="btn" onClick={() => { setShowSignup(false); handlePlayAsGuestWrapper(); }}>
                Play as Guest
              </button>
              <button type="button" className="btn" onClick={() => setShowSignup(false)}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* Stats authentication modal */}
      {showStatsAuth && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowStatsAuth(false)} />
          <div className="notification stats-auth-modal" style={{ display: 'block' }}>
            <h2>Track Your Stats</h2>
            <p style={{ marginBottom: '20px', lineHeight: '1.5' }}>
              To view and track your game statistics, you need a player account. 
              Create an account to keep track of your wins, losses, and game history!
            </p>
            <div className="notification-buttons">
              <button 
                type="button" 
                className="btn" 
                onClick={() => { 
                  setShowStatsAuth(false); 
                  setShowLogin(true); 
                }}
              >
                Log In
              </button>
              <button 
                type="button" 
                className="btn" 
                onClick={() => { 
                  setShowStatsAuth(false); 
                  setShowSignup(true); 
                }}
              >
                Sign Up
              </button>
              <button type="button" className="btn" onClick={() => setShowStatsAuth(false)}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Matchmaking modal */}
      {showMatchmaking && !currentRoom && (
        <>
          <div className="overlay" style={{ display: 'block', zIndex: 10001 }} onClick={() => setShowMatchmaking(false)} />
          <div className="notification matchmaking-modal" style={{ display: 'block', zIndex: 10002 }}>
            <h2>Online Multiplayer</h2>
            <div style={{ marginBottom: '15px', fontSize: '0.9rem', color: '#666' }}>
              Playing as: <strong>
                {authState.isAuthenticated ? authState.user?.username : 
                 authState.isGuest ? `Guest${Math.floor(Math.random() * 9000) + 1000}` : 'Anonymous'}
              </strong>
            </div>
            
            {/* Guest mode limitation notice */}
            {authState.isGuest && (
              <div style={{ 
                margin: '15px 0', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '6px',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#856404' }}>
                  👤 Guest Mode Limitations
                </div>
                <div style={{ color: '#856404', marginBottom: '10px' }}>
                  As a guest, you can only be matched with other guests in Quick Match.
                  To play against all users and access full features, please sign up or sign in.
                </div>
                <div className="notification-buttons" style={{ marginTop: '10px', gap: '8px' }}>
                  <button 
                    className="btn" 
                    onClick={() => { 
                      setShowMatchmaking(false); 
                      setAuthModalMode('signup');
                      setAuthModalOpen(true);
                    }}
                    style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                  >
                    Sign Up
                  </button>
                  <button 
                    className="btn" 
                    onClick={() => { 
                      setShowMatchmaking(false); 
                      setAuthModalMode('signin');
                      setAuthModalOpen(true);
                    }}
                    style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                  >
                    Sign In
                  </button>
                </div>
              </div>
            )}
            
            {/* Timer preference selection */}
            <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <div style={{ fontSize: '0.9rem', color: '#495057', textAlign: 'center', marginBottom: '10px' }}>
                <strong>⏱️ Select Time Control</strong>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setMatchmakingTimerPreference(10)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: `2px solid ${matchmakingTimerPreference === 10 ? '#007bff' : '#ccc'}`,
                    background: matchmakingTimerPreference === 10 ? '#007bff' : 'white',
                    color: matchmakingTimerPreference === 10 ? 'white' : '#333',
                    fontWeight: matchmakingTimerPreference === 10 ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  10 minutes
                </button>
                <button
                  type="button"
                  onClick={() => setMatchmakingTimerPreference(5)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: `2px solid ${matchmakingTimerPreference === 5 ? '#007bff' : '#ccc'}`,
                    background: matchmakingTimerPreference === 5 ? '#007bff' : 'white',
                    color: matchmakingTimerPreference === 5 ? 'white' : '#333',
                    fontWeight: matchmakingTimerPreference === 5 ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  5 minutes
                </button>
                <button
                  type="button"
                  onClick={() => setMatchmakingTimerPreference('any')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: `2px solid ${matchmakingTimerPreference === 'any' ? '#007bff' : '#ccc'}`,
                    background: matchmakingTimerPreference === 'any' ? '#007bff' : 'white',
                    color: matchmakingTimerPreference === 'any' ? 'white' : '#333',
                    fontWeight: matchmakingTimerPreference === 'any' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Any
                </button>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center', marginTop: '8px' }}>
                {matchmakingTimerPreference === 'any' 
                  ? 'Will match with 5 or 10 minute games'
                  : `Will match with ${matchmakingTimerPreference} minute games only`
                }
              </div>
            </div>

            {/* Rated/Unrated toggle - only for authenticated non-guest users */}
            {ELO_VISIBLE && authState.isAuthenticated && !authState.isGuest && (
              <div style={{ margin: '12px 0', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.9rem', color: '#495057', marginBottom: '8px' }}>
                  <strong>Game Type</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <button
                    className={`btn ${matchmakingIsRated ? 'btn-active' : ''}`}
                    onClick={() => setMatchmakingIsRated(true)}
                    style={{ padding: '6px 16px', fontSize: '0.85rem', backgroundColor: matchmakingIsRated ? '#667eea' : '#e9ecef', color: matchmakingIsRated ? 'white' : '#495057', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Rated
                  </button>
                  <button
                    className={`btn ${!matchmakingIsRated ? 'btn-active' : ''}`}
                    onClick={() => setMatchmakingIsRated(false)}
                    style={{ padding: '6px 16px', fontSize: '0.85rem', backgroundColor: !matchmakingIsRated ? '#667eea' : '#e9ecef', color: !matchmakingIsRated ? 'white' : '#495057', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Casual
                  </button>
                </div>
              </div>
            )}
            
            {isSearchingMatch ? (
              <>
                <p>Searching for a match...</p>
            <div className="notification-buttons">
                  <button className="btn" onClick={cancelMatchmaking}>Cancel Search</button>
                </div>
              </>
            ) : (
                <>
                <p>Choose how to play:</p>
                <div className="notification-buttons">
                  <button className="btn" onClick={findMatch}>🎯 Quick Match</button>
                  <button className="btn" onClick={handlePrivateRoomClick}>🏠 Private Room</button>
                  <button className="btn" onClick={() => setShowMatchmaking(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {/* Mobile Controls Modal */}
      {showMobileControls && (
        <>
          <div className="overlay" style={{ display: 'block', zIndex: 14999 }} onClick={() => setShowMobileControls(false)} />
          <div className="notification mobile-controls-modal" style={{ display: 'block', position: 'relative', zIndex: 15000 }}>
            <PageCloseButton
              variant="dialog"
              ariaLabel="Close opponent settings"
              onClick={() => setShowMobileControls(false)}
            />
            <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Opponent</h2>
            
            {/* Opponent selection */}
            <div className="option-row" style={{ marginBottom: '20px' }}>
              <label htmlFor="mobile-game-mode-select" style={{ fontWeight: 'bold', fontSize: '16px' }}>Opponent:</label>
              <select 
                id="mobile-game-mode-select" 
                className="control-select"
                value={gameMode}
                onChange={(e) => setGameMode(e.target.value as any)}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  fontSize: '16px',
                  border: '2px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: '#fff'
                }}
              >
                <option value="local">Local Play</option>
                <option value="ai-1">AI - Beginner</option>
                <option value="ai-2">AI - Novice</option>
                <option value="ai-challenger">AI - Intermediate</option>
                <option value="ai-skilled">AI - Advanced</option>
                <option value="ai-3">AI - Expert</option>
                <option value="ai-engine">AI - Master</option>
                <option value="online">Online Multiplayer</option>
              </select>
            </div>

            {/* Color selection for AI games */}
            {(gameMode.startsWith('ai-')) && (
              <div className="option-row" style={{ marginBottom: '20px', justifyContent: 'center' }}>
                <span style={{ fontWeight: 'bold', fontSize: '16px', marginRight: '12px' }}>Play as:</span>
                <div style={{ display: 'inline-flex', gap: '8px' }}>
                  <button
                    type="button"
                    className={`color-choice-btn${playerColorChoice === 'white' ? ' selected' : ''}`}
                    aria-label="Play as White"
                    onClick={() => setPlayerColorChoice('white')}
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: '6px', 
                      border: '2px solid #ccc', 
                      background: playerColorChoice === 'white' ? '#ecf0f1' : 'white', 
                      fontWeight: playerColorChoice === 'white' ? 'bold' : 'normal',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    ⚪
                  </button>
                  <button
                    type="button"
                    className={`color-choice-btn${playerColorChoice === 'black' ? ' selected' : ''}`}
                    aria-label="Play as Black"
                    onClick={() => setPlayerColorChoice('black')}
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: '6px', 
                      border: '2px solid #ccc', 
                      background: playerColorChoice === 'black' ? '#2c3e50' : 'white', 
                      color: playerColorChoice === 'black' ? 'white' : '#2c3e50', 
                      fontWeight: playerColorChoice === 'black' ? 'bold' : 'normal',
                      fontSize: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    ⚫
                  </button>
                </div>
              </div>
            )}

            {/* Timer toggle */}
            <div className="option-row" style={{ marginBottom: '15px' }}>
              <label htmlFor="mobile-timer-toggle" style={{ fontWeight: 'bold', fontSize: '16px' }}>Game Timer:</label>
              <div className="toggle-container">
                <span className={`toggle-label ${!timerEnabled ? 'active' : ''}`}>Off</span>
                <label className="toggle">
                  <input 
                    type="checkbox" 
                    id="mobile-timer-toggle" 
                    checked={timerEnabled}
                    onChange={(e) => setTimerEnabled(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className={`toggle-label ${timerEnabled ? 'active' : ''}`}>On</span>
              </div>
            </div>

            {/* Timer settings */}
            {timerEnabled && (
              <div className="timer-settings" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div className="timer-row" style={{ marginBottom: '15px' }}>
                  <div className="option-cell" style={{ flex: 1, marginRight: '10px' }}>
                    <label htmlFor="mobile-minutes-per-player" style={{ fontWeight: 'bold', fontSize: '14px' }}>Minutes:</label>
                    <select 
                      id="mobile-minutes-per-player" 
                      className="control-select"
                      value={minutesPerPlayer}
                      onChange={(e) => setMinutesPerPlayer(parseInt(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="15">15</option>
                      <option value="10">10</option>
                      <option value="5">5</option>
                      <option value="3">3</option>
                      <option value="2">2</option>
                      <option value="1">1</option>
                    </select>
                  </div>
                  <div className="option-cell" style={{ flex: 1, marginLeft: '10px' }}>
                    <label htmlFor="mobile-increment-seconds" style={{ fontWeight: 'bold', fontSize: '14px' }}>Increment:</label>
                    <select 
                      id="mobile-increment-seconds" 
                      className="control-select"
                      value={incrementSeconds}
                      onChange={(e) => setIncrementSeconds(parseInt(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="5">5 sec</option>
                      <option value="2">2 sec</option>
                      <option value="1">1 sec</option>
                      <option value="0">0 sec</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="notification-buttons">
              <button 
                className="btn" 
                onClick={() => {
                  setShowMobileControls(false);
                  // Small delay to ensure modal closes before starting game
                  setTimeout(() => {
                    startGame();
                  }, 100);
                }} 
                style={{ width: '100%', padding: '12px', fontSize: '16px', backgroundColor: '#28a745', color: 'white' }}
              >
                Start Game
              </button>
            </div>
          </div>
        </>
      )}

      {/* Statistics modal — available on all pages */}
      {showStatisticsModal && authState.isAuthenticated && authState.user && (
        <>
          <div
            className="overlay"
            style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
            onClick={() => setShowStatisticsModal(false)}
          />
          <div className="notification settings-dialog" style={{ display: 'block', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: 860, width: '92%', maxHeight: '90vh', overflow: 'hidden', padding: 0, zIndex: 10001 }}>
            <PageCloseButton variant="dialog" onClick={() => setShowStatisticsModal(false)} />
            <div style={{ clear: 'both' }} />
            <StatisticsPage
              user={authState.user}
              onBack={() => setShowStatisticsModal(false)}
              onPlayClick={handlePlayClick}
              onHowToPlayClick={handleHowToPlayClick}
              onAboutClick={handleAboutClick}
              onPlayersClick={handlePlayersClick}
              onStatsClick={handleStatsClick}
              authState={authState}
              onLogout={handleLogoutWrapper}
              onSignInClick={() => {
                setAuthModalMode('signin');
                setAuthModalOpen(true);
              }}
              onSignUpClick={() => {
                setAuthModalMode('signup');
                setAuthModalOpen(true);
              }}
              isModal
            />
          </div>
        </>
      )}

      {/* Rules popup - Available on all pages */}
      {showRules && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowRules(false)} />
          <div className="notification settings-dialog" style={{ display: 'block' }}>
            <PageCloseButton variant="dialog" onClick={() => setShowRules(false)} />
            <h2><span style={{color: 'red', fontWeight: 'bold'}}>Migoyugo</span> Game Rules</h2>
            
            <br />
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 10, textAlign: 'left' }}>
              <h3 style={{ color: 'red' }}>The Game</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '30px' }}>
                <li><strong>Migoyugo</strong> is an abstract board game for two players, played on an 8x8 grid of 64 squares</li>
              </ul>
              
              <h3 style={{ color: 'red' }}>The Migo</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '30px' }}>
                <li>White always moves first by placing a basic, unmarked piece called a <strong>Migo</strong> on any open square on the board* (see No Long Lines) and players always alternate turns</li>
              </ul>
              
              <h3 style={{ color: 'red' }}>The Yugo</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '30px' }}>
                <li>When you form one unbroken line or multiple intersecting lines of exactly 4 pieces of your own color (horizontal, vertical or diagonal), the last piece placed becomes a permanent <strong>Yugo</strong>, represented by a dot in the center</li>
                <li>When a Yugo is created, all Migos in the line(s) of exactly 4 are removed, leaving behind only the Yugo created and any other Yugos. Yugos can never be moved or removed from the board</li>
              </ul>
              
              <h3 style={{ color: 'red' }}>No Long Lines</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '30px' }}>
                <li>At no time may either player create an unbroken line of more than 4 in a row of any combination of Migos and/or Yugos of their own color (multiple intersecting unbroken lines of exactly 4 are permitted)</li>
              </ul>
              
              <h3 style={{ color: 'red' }}>Winning</h3>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                <li>There are two distinct ways to win in Migoyugo – an <strong>Igo</strong> and a <strong>Wego</strong></li>
                <li><strong>Igo:</strong> Form an unbroken line (horizontal, vertical or diagonal) of exactly 4 Yugos of your own color and you win instantly</li>
                <li><strong>Wego:</strong> If on your turn, you are unable to make a legal move, or if all 64 squares are filled, the game ends instantly, and the player with the most Yugos is the winner. If both players have the same number of Yugos, the game is drawn</li>
              </ul>
            </div>

            <div className="notification-buttons">
              <button 
                className="btn" 
                onClick={async () => {
                  try {
                    const rulesText = `
Migoyugo Game Rules

The Game
• Migoyugo is an abstract board game for two players, played on an 8x8 grid of 64 squares

The Migo
• White moves first by placing a basic, unmarked piece called a Migo on any open square on the board* (see No Long Lines) and players always alternate turns

The Yugo
• When you form one unbroken line or multiple intersecting lines of exactly 4 pieces of your own color (horizontal, vertical or diagonal), the last piece placed becomes a permanent Yugo, represented by a dot in the center
• When a Yugo is created, all Migos in the line(s) of exactly 4 are removed, leaving behind only the Yugo created and any other Yugos. Yugos can never be moved or removed from the board

No Long Lines
• At no time may either player create an unbroken line of more than 4 in a row of any combination of Migos and/or Yugos of their own color (multiple intersecting unbroken lines of exactly 4 are permitted)

Winning
• There are two distinct ways to win in Migoyugo – an Igo and a Wego
Igo: Form an unbroken line (horizontal, vertical or diagonal) of exactly 4 Yugos of your own color and you win instantly
Wego: If on your turn, you are unable to make a legal move, or if all 64 squares are filled, the game ends instantly, and the player with the most Yugos is the winner. If both players have the same number of Yugos, the game is drawn
`.trim();
                    await navigator.clipboard.writeText(rulesText);
                    showToast('Rules copied to clipboard!');
                  } catch (err) {
                    showToast('Failed to copy rules. Please try selecting the text manually.');
                  }
                }}
                style={{ marginRight: '10px' }}
              >
                📋 Copy Rules
              </button>
              <button className="btn" onClick={() => setShowRules(false)}>Close</button>
            </div>
          </div>
        </>
      )}

      {/* Players Modal - Available on all pages */}
      {showPlayersModal && authState.isAuthenticated && authState.user && (
        <>
          <div
            className="overlay"
            style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
                onClick={() => setShowPlayersModal(false)}
          />
          <div className="notification settings-dialog players-modal" style={{ display: 'block', maxWidth: 860, width: '92%', height: '90vh', maxHeight: '90vh', overflow: 'hidden', padding: 0, zIndex: 10001 }}>
            <PageCloseButton variant="dialog" onClick={() => setShowPlayersModal(false)} />
            <div className="players-modal-content" style={{ height: '100%', overflow: 'hidden', paddingTop: '10px' }}>
              <PlayersPage 
                onBack={() => setShowPlayersModal(false)}
                authState={authState}
                socket={socket}
                onViewPlayerStats={(userId, username) => {
                  setViewingOtherPlayerStats({ userId, username });
                }}
                onWatchGame={(gameIdToWatch) => {
                  setShowPlayersModal(false);
                  watchGame(gameIdToWatch);
                  setCurrentPage('game');
                }}
                onMessagePlayer={GLOBAL_CHAT_UI_ENABLED ? (recipientId, recipientUsername) => {
                  setShowPlayersModal(false);
                  setMessagePlayerTarget({ recipientId, recipientUsername });
                  setShowGlobalChat(true);
                } : undefined}
              />
            </div>
          </div>
        </>
      )}

      {/* Import Moves/Position Modal */}
      {showImportMovesModal && (
        <>
          <div
            className="overlay"
            style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
            onClick={() => {
              setShowImportMovesModal(false);
              setImportMovesText('');
            }}
          />
          <div className="notification settings-dialog" style={{ display: 'block', maxWidth: 600, width: '90%', maxHeight: '80vh', overflow: 'hidden', padding: 20, zIndex: 10001 }}>
            <h2 style={{ marginTop: 0 }}>Import Move History or Position</h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
              Paste either move history or a position string below. The system will automatically detect the format.
            </p>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontFamily: 'monospace' }}>
              <strong style={{ fontFamily: 'inherit' }}>Move History Format:</strong><br/>
              1. a1&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;b2<br/>
              2. c3&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;d4*<br/>
              3. e5**&nbsp;&nbsp;&nbsp;b6<br/><br/>
              <strong style={{ fontFamily: 'inherit' }}>Position String Format (from Test Lab):</strong><br/>
              WM:a1,b2;WY:c3;BM:d4;BY:e5;START:white
            </div>
            <textarea
              value={importMovesText}
              onChange={(e) => setImportMovesText(e.target.value)}
              placeholder={"Paste move history or position string here...\n\nMove history example:\n1. a1      b2\n2. c3      d4*\n3. e5**    b6\n\nPosition string example:\nWM:a1,b2;WY:c3;BM:d4;BY:e5;START:white"}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '15px' }}>
              <button
                className="btn"
                onClick={() => {
                  setShowImportMovesModal(false);
                  setImportMovesText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => {
                  if (importMovesText.trim()) {
                    handleImport(importMovesText);
                    setShowImportMovesModal(false);
                    setImportMovesText('');
                  } else {
                    showToast('Please paste move history or position string first');
                  }
                }}
                style={{ backgroundColor: '#28a745', color: 'white' }}
              >
                Import
              </button>
            </div>
          </div>
        </>
      )}

      {/* About/Q&A Modal - Available on all pages */}
      {showAboutModal && (
        <>
          <div
            className="overlay"
            style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
            onClick={() => setShowAboutModal(false)}
          />
          <div className="notification settings-dialog" style={{ display: 'block', position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: 860, width: '92%', maxHeight: '90vh', overflow: 'hidden', padding: 0, zIndex: 10001 }}>
            <PageCloseButton variant="dialog" onClick={() => setShowAboutModal(false)} />
            <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', maxHeight: '90vh', paddingTop: '10px' }}>
              <AboutPage
                onBack={() => setShowAboutModal(false)}
                onBuyBoardClick={() => {
                  setShowAboutModal(false);
                  handleShopClick();
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Statistics Modal for Viewing Other Players */}
      {viewingOtherPlayerStats && authState.isAuthenticated && authState.user && (
        <>
          <div
            className="overlay"
            style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
            onClick={() => setViewingOtherPlayerStats(null)}
          />
          <div className="notification settings-dialog" style={{ display: 'block', maxWidth: 860, width: '92%', maxHeight: '90vh', overflow: 'hidden', padding: 0, zIndex: 10001 }}>
            <PageCloseButton variant="dialog" onClick={() => setViewingOtherPlayerStats(null)} />
            <div style={{ clear: 'both' }} />
            <StatisticsPage 
              user={authState.user}
              onBack={() => setViewingOtherPlayerStats(null)}
              onPlayClick={handlePlayClick}
              onHowToPlayClick={handleHowToPlayClick}
              onAboutClick={handleAboutClick}
              onPlayersClick={handlePlayersClick}
              onStatsClick={handleStatsClick}
              authState={authState}
              onLogout={handleLogoutWrapper}
              onSignInClick={() => {
                setAuthModalMode('signin');
                setAuthModalOpen(true);
              }}
              onSignUpClick={() => {
                setAuthModalMode('signup');
                setAuthModalOpen(true);
              }}
              isModal
              viewingUserId={viewingOtherPlayerStats.userId}
              viewingUsername={viewingOtherPlayerStats.username}
            />
          </div>
        </>
      )}

      {/* Tutorial popup - Available on all pages */}
      {showTutorial && (
        <>
          <div className="overlay tutorial-overlay" style={{ display: 'block' }} onClick={closeTutorial} />
          <div className="tutorial-popup" id="tutorial-popup" style={{ display: 'block' }}>
            <div id="tutorial-content">
              <h2 id="tutorial-title">{tutorialSteps[tutorialStep]?.title}</h2>
              <div id="tutorial-message">
                <div dangerouslySetInnerHTML={{ __html: tutorialSteps[tutorialStep]?.message || '' }} />
              </div>
              <div id="tutorial-demo">
                {tutorialSteps[tutorialStep]?.demo && (
                  <TutorialDemo demoType={tutorialSteps[tutorialStep].demo as "board" | "yugo" | "yugo" | "long-line" | "igo"} />
                )}
              </div>
              <div className="tutorial-navigation">
                {tutorialStep > 0 ? (
                  <button className="btn" onClick={prevTutorialStep}>
                    Previous
                  </button>
                ) : (
                  <button className="btn" style={{ visibility: 'hidden' }}>
                    Previous
                  </button>
                )}
                {tutorialStep < tutorialSteps.length - 1 && (
                  <button className="btn" onClick={nextTutorialStep}>
                    Next
                  </button>
                )}
                {tutorialStep === tutorialSteps.length - 1 && (
                  <button className="btn" onClick={nextTutorialStep}>
                    Finish
                  </button>
                )}
                <button className="btn" onClick={closeTutorial}>
                  Close
                </button>
              </div>
            </div>
            <button
              id="mobile-tutorial-close-x"
              onClick={closeTutorial}
              style={{ display: 'block' }}
            >
              ×
            </button>
          </div>
        </>
      )}
    </>
  );


  // Show admin portal if on admin page
  if (currentPage === 'admin') {
    console.log('📄 Rendering ADMIN page, currentChallenge:', currentChallenge);
    return (
      <div style={{ minHeight: '100vh', width: '100%', maxWidth: '100%', margin: 0, padding: 0 }}>
        {adminAuthenticated ? (
          <AdminDashboard socket={socket} />
        ) : (
          <AdminLogin
            onLoginSuccess={() => {
              setAdminAuthenticated(true);
            }}
          />
        )}
        {/* Challenge Toast */}
        {currentChallenge && (
          <ChallengeToast
            challengeId={currentChallenge.challengeId}
            challengerUsername={currentChallenge.challengerUsername}
            timerSettings={currentChallenge.timerSettings}
            isRated={currentChallenge?.isRated}
            isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
            onAccept={handleChallengeAccept}
            onDecline={handleChallengeDecline}
            onTimeout={handleChallengeTimeout}
          />
        )}

        {/* Challenge Declined Toast */}
        {challengeDeclinedToast && (
          <ChallengeDeclinedToast
            challengedUsername={challengeDeclinedToast.challengedUsername}
            reason={challengeDeclinedToast.reason}
            onClose={() => setChallengeDeclinedToast(null)}
          />
        )}
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  if (currentPage === 'greedy') {
    return (
      <div className="App greedy-app-shell" style={{ minHeight: '100vh' }}>
        <Sidebar
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setAuthModalOpen(true);
          }}
          onSignUpClick={() => {
            setAuthModalMode('signup');
            setAuthModalOpen(true);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
        />
        <GreedyGame
          authState={authState}
          onBack={() => setCurrentPage('game')}
        />
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  // Show email verification page if token is present
  // Show password reset page if token is present
  if (resetPasswordToken) {
    return (
      <ResetPassword 
        token={resetPasswordToken} 
        onSuccess={() => {
          setResetPasswordToken(null);
          // Clear URL parameters
          window.history.replaceState({}, '', '/');
          // Show sign in modal
          setAuthModalOpen(true);
          setAuthModalMode('signin');
        }}
      />
    );
  }

  if (verificationToken) {
    return (
      <EmailVerification 
        token={verificationToken} 
        onVerified={() => {
          setVerificationToken(null);
          // Clear URL parameters
          window.history.replaceState({}, '', '/');
          // Redirect to game page
          setCurrentPage('game');
        }} 
      />
    );
  }


  // Show Leaderboard page
  if (currentPage === 'leaderboard') {
    console.log('📄 Rendering LEADERBOARD page, currentChallenge:', currentChallenge);
    return (
      <div className="App">
        <Sidebar 
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setAuthModalOpen(true);
          }}
          onSignUpClick={() => {
            setAuthModalMode('signup');
            setAuthModalOpen(true);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
        />
        <div className="main-content">
          <LeaderboardPage
            onBack={() => setCurrentPage('game')}
            authState={authState}
            onViewPlayerStats={(userId, username) => {
              setViewingOtherPlayerStats({ userId, username });
            }}
            onWatchGame={(gameIdToWatch) => {
              watchGame(gameIdToWatch);
              setCurrentPage('game');
            }}
            socket={socket}
          />
        </div>
        {/* Auth Modal */}
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authModalMode}
          onModeChange={setAuthModalMode}
          onUsernameSet={refreshAuthState}
        />

        {/* Challenge Toast */}
        {currentChallenge && (
          <ChallengeToast
            challengeId={currentChallenge.challengeId}
            challengerUsername={currentChallenge.challengerUsername}
            timerSettings={currentChallenge.timerSettings}
            isRated={currentChallenge?.isRated}
            isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
            onAccept={handleChallengeAccept}
            onDecline={handleChallengeDecline}
            onTimeout={handleChallengeTimeout}
          />
        )}

        {/* Challenge Declined Toast */}
        {challengeDeclinedToast && (
          <ChallengeDeclinedToast
            challengedUsername={challengeDeclinedToast.challengedUsername}
            reason={challengeDeclinedToast.reason}
            onClose={() => setChallengeDeclinedToast(null)}
          />
        )}

        {/* Statistics Modal for Viewing Other Players (from Leaderboard) */}
        {viewingOtherPlayerStats && authState.isAuthenticated && authState.user && (
          <>
            <div
              className="overlay"
              style={{ display: 'block', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
              onClick={() => setViewingOtherPlayerStats(null)}
            />
            <div className="notification settings-dialog" style={{ display: 'block', maxWidth: 860, width: '92%', maxHeight: '90vh', overflow: 'hidden', padding: 0, zIndex: 10001 }}>
              <PageCloseButton variant="dialog" onClick={() => setViewingOtherPlayerStats(null)} />
              <div style={{ clear: 'both' }} />
              <StatisticsPage
                user={authState.user}
                onBack={() => setViewingOtherPlayerStats(null)}
                onPlayClick={handlePlayClick}
                onHowToPlayClick={handleHowToPlayClick}
                onAboutClick={handleAboutClick}
                onPlayersClick={handlePlayersClick}
                onStatsClick={handleStatsClick}
                authState={authState}
                onLogout={handleLogoutWrapper}
                onSignInClick={() => {
                  setAuthModalMode('signin');
                  setAuthModalOpen(true);
                }}
                onSignUpClick={() => {
                  setAuthModalMode('signup');
                  setAuthModalOpen(true);
                }}
                isModal
                viewingUserId={viewingOtherPlayerStats.userId}
                viewingUsername={viewingOtherPlayerStats.username}
              />
            </div>
          </>
        )}

        <GlobalChat
          isOpen={GLOBAL_CHAT_UI_ENABLED && showGlobalChat}
          onClose={() => { setShowGlobalChat(false); setMessagePlayerTarget(null); }}
          socket={socket}
          authState={authState}
          openConversationWith={messagePlayerTarget}
        />
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  // Show Shop page
  if (currentPage === 'shop') {
    return (
      <div className="App">
        <Sidebar 
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setAuthModalOpen(true);
          }}
          onSignUpClick={() => {
            setAuthModalMode('signup');
            setAuthModalOpen(true);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
        />
        <div className="main-content">
          <ShopPage
            onBack={() => {
              setCurrentPage('game');
              localStorage.setItem('mig_current_page', 'game');
            }}
          />
        </div>
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authModalMode}
          onModeChange={setAuthModalMode}
          onUsernameSet={refreshAuthState}
        />
        {sharedNavOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  // Show Puzzles page
  if (currentPage === 'puzzles') {
    return (
      <div className="App">
        <Sidebar 
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setAuthModalOpen(true);
          }}
          onSignUpClick={() => {
            setAuthModalMode('signup');
            setAuthModalOpen(true);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
        />
        <div className="main-content">
          <PuzzlesPage
            onBack={() => {
              setCurrentPage('game');
              localStorage.setItem('mig_current_page', 'game');
            }}
            onStartGameFromSandbox={handleStartGameFromSandbox}
            authState={authState}
          />
        </div>
        {/* Auth Modal */}
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authModalMode}
          onModeChange={setAuthModalMode}
          onUsernameSet={refreshAuthState}
        />

        {/* Challenge Toast */}
        {currentChallenge && (
          <ChallengeToast
            challengeId={currentChallenge.challengeId}
            challengerUsername={currentChallenge.challengerUsername}
            timerSettings={currentChallenge.timerSettings}
            isRated={currentChallenge?.isRated}
            isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
            onAccept={handleChallengeAccept}
            onDecline={handleChallengeDecline}
            onTimeout={handleChallengeTimeout}
          />
        )}

        {/* Challenge Declined Toast */}
        {challengeDeclinedToast && (
          <ChallengeDeclinedToast
            challengedUsername={challengeDeclinedToast.challengedUsername}
            reason={challengeDeclinedToast.reason}
            onClose={() => setChallengeDeclinedToast(null)}
          />
        )}

        <GlobalChat
          isOpen={GLOBAL_CHAT_UI_ENABLED && showGlobalChat}
          onClose={() => { setShowGlobalChat(false); setMessagePlayerTarget(null); }}
          socket={socket}
          authState={authState}
          openConversationWith={messagePlayerTarget}
        />
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  // Show Spectator page
  if (currentPage === 'spectator') {
    return (
      <div className="App">
        <Sidebar 
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => {
            setAuthModalMode('signin');
            setAuthModalOpen(true);
          }}
          onSignUpClick={() => {
            setAuthModalMode('signup');
            setAuthModalOpen(true);
          }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
        />
        <div className="main-content">
          <SpectatorListPage
            onGameClick={(gameId) => {
              watchGame(gameId);
              setCurrentPage('game');
            }}
            onBack={() => setCurrentPage('game')}
            spectatorTimers={spectatorTimers}
            authState={authState}
          />
        </div>
        {/* Auth Modal */}
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authModalMode}
          onModeChange={setAuthModalMode}
          onUsernameSet={refreshAuthState}
        />

        {/* Challenge Toast */}
        {currentChallenge && (
          <ChallengeToast
            challengeId={currentChallenge.challengeId}
            challengerUsername={currentChallenge.challengerUsername}
            timerSettings={currentChallenge.timerSettings}
            isRated={currentChallenge?.isRated}
            isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
            onAccept={handleChallengeAccept}
            onDecline={handleChallengeDecline}
            onTimeout={handleChallengeTimeout}
          />
        )}

        {/* Challenge Declined Toast */}
        {challengeDeclinedToast && (
          <ChallengeDeclinedToast
            challengedUsername={challengeDeclinedToast.challengedUsername}
            reason={challengeDeclinedToast.reason}
            onClose={() => setChallengeDeclinedToast(null)}
          />
        )}

        <GlobalChat
          isOpen={GLOBAL_CHAT_UI_ENABLED && showGlobalChat}
          onClose={() => { setShowGlobalChat(false); setMessagePlayerTarget(null); }}
          socket={socket}
          authState={authState}
          openConversationWith={messagePlayerTarget}
        />
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  // Show Tournaments page
  if (currentPage === 'tournaments') {
    return (
      <div className="App">
        <Sidebar
          onPlayClick={handlePlayClick}
          onHowToPlayClick={handleHowToPlayClick}
          onAboutClick={handleAboutClick}
          onPlayersClick={handlePlayersClick}
          onStatsClick={handleStatsClick}
          onLeaderboardClick={handleLeaderboardClick}
          onRulesClick={handleRulesClick}
          onSettingsClick={handleSettingsClick}
          onPuzzlesClick={handlePuzzlesClick}
          onShopClick={handleShopClick}
          onSpectatorClick={handleSpectatorClick}
          onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
          onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
          authState={authState}
          onLogout={handleLogoutWrapper}
          onSignInClick={() => { setAuthModalMode('signin'); setAuthModalOpen(true); }}
          onSignUpClick={() => { setAuthModalMode('signup'); setAuthModalOpen(true); }}
          isMobileMenuOpen={isMobileMenuOpen}
          toggleMobileMenu={toggleMobileMenu}
          closeMobileMenu={closeMobileMenu}
          onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
        />
        <div className="main-content">
          <TournamentPage
            socket={socket}
            userId={authState.user?.id ?? null}
            username={authState.user?.username ?? null}
            isGuest={!authState.isAuthenticated || authState.isGuest}
            onCreateAccount={() => { setAuthModalMode('signup'); setAuthModalOpen(true); }}
            onClose={handlePlayClick}
            onWatchGame={(gameId, ctx) => { watchGame(gameId); setCurrentPage('game'); if (ctx) setSpectatorTournamentCtx({ name: ctx.tournamentName, round: ctx.round }); else setSpectatorTournamentCtx(null); }}
          />
        </div>
        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authModalMode}
          onModeChange={setAuthModalMode}
          onUsernameSet={refreshAuthState}
        />
        {currentChallenge && (
          <ChallengeToast
            challengeId={currentChallenge.challengeId}
            challengerUsername={currentChallenge.challengerUsername}
            timerSettings={currentChallenge.timerSettings}
            isRated={currentChallenge?.isRated}
            isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
            onAccept={handleChallengeAccept}
            onDecline={handleChallengeDecline}
            onTimeout={handleChallengeTimeout}
          />
        )}
        {challengeDeclinedToast && (
          <ChallengeDeclinedToast
            challengedUsername={challengeDeclinedToast.challengedUsername}
            reason={challengeDeclinedToast.reason}
            onClose={() => setChallengeDeclinedToast(null)}
          />
        )}
        <GlobalChat
          isOpen={GLOBAL_CHAT_UI_ENABLED && showGlobalChat}
          onClose={() => { setShowGlobalChat(false); setMessagePlayerTarget(null); }}
          socket={socket}
          authState={authState}
          openConversationWith={messagePlayerTarget}
        />
        {sharedNavOverlaysEl}
        {globalTournamentOverlaysEl}
        {welcomeAndHumanModalsEl}
      </div>
    );
  }

  console.log('📄 Rendering GAME page, currentChallenge:', currentChallenge);
  return (
    <div className="App">
      <Sidebar 
        onPlayClick={handlePlayClick}
        onHowToPlayClick={handleHowToPlayClick}
        onAboutClick={handleAboutClick}
        onPlayersClick={handlePlayersClick}
        onStatsClick={handleStatsClick}
        onLeaderboardClick={handleLeaderboardClick}
        onRulesClick={handleRulesClick}
        onSettingsClick={handleSettingsClick}
        onPuzzlesClick={handlePuzzlesClick}
        onShopClick={handleShopClick}
        onSpectatorClick={handleSpectatorClick}
        onChatClick={GLOBAL_CHAT_UI_ENABLED ? handleGlobalChatClick : undefined}
        onTournamentsClick={TOURNAMENTS_NAV_UI_ENABLED ? handleTournamentsClick : undefined}
        authState={authState}
        onLogout={handleLogoutWrapper}
        onSignInClick={() => {
          setAuthModalMode('signin');
          setAuthModalOpen(true);
        }}
        onSignUpClick={() => {
          setAuthModalMode('signup');
          setAuthModalOpen(true);
        }}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={toggleMobileMenu}
        closeMobileMenu={closeMobileMenu}
        onGreedyClick={GREEDY_NAV_UI_ENABLED ? handleGreedyNavClick : undefined}
      />
      <div className="main-content">
      <header>
        <img 
          src="/migoyugo-logo.png" 
          alt="migoyugo" 
          style={{
            height: '60px',
            maxWidth: '300px',
            objectFit: 'contain',
            margin: '10px auto 10px auto',
            display: 'block'
          }}
        />
      </header>

      <div className="game-container">
        <div className="game-board-area">
          {/* 
            OPTION 2: Disconnect Modal at top of game-board-area (above everything)
            Uncomment this block and remove Option 1 to use this placement instead:
            
            {gameMode === 'online' && opponentDisconnected && (
              <div style={{
                width: 'calc(var(--board-size) + 4px)',
                margin: '0 auto 15px auto',
                background: 'rgba(255, 193, 7, 0.95)',
                color: '#856404',
                padding: '12px 20px',
                borderRadius: '8px',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                fontWeight: 'bold',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}>
                <div>⚠️ Opponent Disconnected</div>
                <div style={{ marginTop: '6px', fontSize: '14px' }}>
                  Grace period: {gracePeriodSeconds} seconds
                </div>
                <div style={{ marginTop: '4px', fontSize: '12px', fontWeight: 'normal' }}>
                  Timer continues running. Game will end if opponent doesn't reconnect.
                </div>
              </div>
            )}
          */}
          
          {/* Top player info - Always shows OPPONENT */}
          {(() => {
            // Determine which color is "you" and which is "opponent"
            let yourColor: 'white' | 'black' | null = null;
            let opponentColor: 'white' | 'black' | null = null;
            let topName = '';
            let topScore = 0;
            let topTimer = 0;
            let topIsActive = false;
            
            if (spectatorMode) {
              // Spectator mode - always show actual game players' names
              topName = gameState.players.white;
              topScore = gameState.scores.white;
              topTimer = timers.white;
              topIsActive = gameState.currentPlayer === 'white';
            } else if (!isGameStarted) {
              // Before game starts, show default labels
              topName = 'White';
              topScore = gameState.scores.white;
              topTimer = timers.white;
              topIsActive = false;
            } else if (gameMode === 'online') {
              // Online game - determine your color from gameState.players
              // Check both authenticated users and guests
              const currentUsername = authState.isAuthenticated ? authState.user?.username : null;
              const isGuest = authState.isGuest || (!authState.isAuthenticated);
              
              if (currentUsername) {
                // Authenticated member - match by username
                if (gameState.players.white === currentUsername) {
                  yourColor = 'white';
                  opponentColor = 'black';
                } else if (gameState.players.black === currentUsername) {
                  yourColor = 'black';
                  opponentColor = 'white';
                }
              }
              // If we couldn't determine, fall back to playerColor
              if (!yourColor && playerColor) {
                yourColor = playerColor;
                opponentColor = playerColor === 'white' ? 'black' : 'white';
              }
              // Top always shows opponent
              // For guests: show "Opponent"; for members: show opponent's actual username
              if (opponentColor) {
                if (isGuest) {
                  // Guest vs Guest - show "Opponent"
                  topName = 'Opponent';
                } else {
                  // Member vs Member - show opponent's actual username
                  topName = gameState.players[opponentColor];
                }
                topScore = gameState.scores[opponentColor];
                topTimer = timers[opponentColor];
                topIsActive = gameState.currentPlayer === opponentColor;
              } else {
                // Fallback - show white at top
                topName = isGuest ? 'Opponent' : gameState.players.white;
                topScore = gameState.scores.white;
                topTimer = timers.white;
                topIsActive = gameState.currentPlayer === 'white';
              }
            } else if (gameMode === 'ai-1' || gameMode === 'ai-2' || gameMode === 'ai-challenger' || gameMode === 'ai-skilled' || gameMode === 'ai-3' || gameMode === 'ai-engine') {
              // AI game - use playerColor (works for both authenticated and guest)
              yourColor = playerColor;
              opponentColor = playerColor === 'white' ? 'black' : 'white';
              // Top always shows opponent (AI)
              topName = opponentColor === 'white' ? gameState.players.white : gameState.players.black;
              topScore = gameState.scores[opponentColor];
              topTimer = timers[opponentColor];
              topIsActive = gameState.currentPlayer === opponentColor;
            } else {
              // Local human vs human or unauthenticated
              // For local games, we can't determine "you" vs "opponent" easily
              // So we'll show white at top, black at bottom (default behavior)
              topName = gameState.players.white;
              topScore = gameState.scores.white;
              topTimer = timers.white;
              topIsActive = gameState.currentPlayer === 'white';
            }
            
            return (
              <div className="player-info" style={{ width: 'calc(var(--board-size) + 4px)', boxSizing: 'border-box' }}>
                <div className={`player ${topIsActive ? 'active' : ''}`} id={`player-${opponentColor || 'white'}`}>
                  <div className={`player-color ${opponentColor || 'white'}`}></div>
                  <span>{topName}</span>
                  <span>Yugos: <span id={`${opponentColor || 'white'}-score`}>{topScore}</span></span>
                </div>
                {timerEnabled && (
                  <GameTimerDisplay
                    seconds={topTimer}
                    isActive={topIsActive}
                    playerColor={opponentColor || 'white'}
                    timerEnabled={timerEnabled}
                    gameMode={spectatorMode || gameMode === 'online' ? 'online' : gameMode}
                    isGameStarted={isGameStarted}
                    gameStatus={gameState.gameStatus}
                    onTimeout={handleTimerTimeout}
                    onTimerWarning={handleTimerWarning}
                    onTimerStopped={handleTimerStopped}
                    timerWarningShown={timerWarningShown[opponentColor || 'white']}
                  />
                )}
              </div>
            );
          })()}

          {/* Disconnect Modal - Overlays player-info area (top or bottom based on which player disconnected) */}
          {gameMode === 'online' && opponentDisconnected && (
            <>
              {/* Show on top if white player disconnected OR if regular player (opponent is always top) */}
              {(!spectatorMode && disconnectedPlayerColor === null) || disconnectedPlayerColor === 'white' ? (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'calc(var(--board-size) + 4px)',
                  height: '40px',
                  background: 'rgba(255, 193, 7, 0.95)',
                  color: '#856404',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '5px 5px 0 0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  zIndex: 1000,
                  boxSizing: 'border-box',
                  padding: '0 10px'
                }}>
                  ⚠️ {spectatorMode ? 'White' : 'Opponent'} Disconnected • {gracePeriodSeconds}s remaining
                </div>
              ) : null}
              
              {/* Show on bottom if black player disconnected (spectator mode only) */}
              {spectatorMode && disconnectedPlayerColor === 'black' ? (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 'calc(var(--board-size) + 4px)',
                  height: '40px',
                  background: 'rgba(255, 193, 7, 0.95)',
                  color: '#856404',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0 0 5px 5px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  zIndex: 1000,
                  boxSizing: 'border-box',
                  padding: '0 10px'
                }}>
                  ⚠️ Black Disconnected • {gracePeriodSeconds}s remaining
                </div>
              ) : null}
            </>
          )}

          {/* Game board */}
          <div className="board-with-labels">
            <div style={{ position: 'relative' }}>
              <GameBoard
                gameState={gameState}
                isReviewMode={isReviewMode}
                newlyPlacedDots={newlyPlacedDots}
                fadingDots={fadingDots}
                onCellClick={handleCellClick}
                illegalCell={illegalMoveCell}
                currentTheme={currentTheme}
                illegalHoverEnabled={illegalHoverEnabled}
                historyHoverSquare={
                  historyHoverIndex !== null && moveHistory[historyHoverIndex]
                    ? { row: moveHistory[historyHoverIndex].row, col: moveHistory[historyHoverIndex].col }
                    : null
                }
              />
            </div>
          </div>

          {/* Bottom player info - Always shows YOU */}
          {(() => {
            // Determine which color is "you" and which is "opponent"
            let yourColor: 'white' | 'black' | null = null;
            let opponentColor: 'white' | 'black' | null = null;
            let bottomName = '';
            let bottomScore = 0;
            let bottomTimer = 0;
            let bottomIsActive = false;
            
            if (spectatorMode) {
              // Spectator mode - always show actual game players' names
              bottomName = gameState.players.black;
              bottomScore = gameState.scores.black;
              bottomTimer = timers.black;
              bottomIsActive = gameState.currentPlayer === 'black';
            } else if (!isGameStarted) {
              // Before game starts, show default labels
              bottomName = 'Black';
              bottomScore = gameState.scores.black;
              bottomTimer = timers.black;
              bottomIsActive = false;
            } else if (gameMode === 'online') {
              // Online game - determine your color from gameState.players
              const currentUsername = authState.isAuthenticated ? authState.user?.username : null;
              if (currentUsername) {
                if (gameState.players.white === currentUsername) {
                  yourColor = 'white';
                  opponentColor = 'black';
                } else if (gameState.players.black === currentUsername) {
                  yourColor = 'black';
                  opponentColor = 'white';
                }
              }
              // If we couldn't determine, fall back to playerColor
              if (!yourColor && playerColor) {
                yourColor = playerColor;
                opponentColor = playerColor === 'white' ? 'black' : 'white';
              }
              // Bottom always shows you
              // For guests: show "Guest"; for members: show their username
              if (yourColor) {
                const isGuest = authState.isGuest || (!authState.isAuthenticated);
                if (isGuest) {
                  bottomName = 'Guest';
                } else {
                  // Member - show their actual username
                  bottomName = authState.user?.username || gameState.players[yourColor];
                }
                bottomScore = gameState.scores[yourColor];
                bottomTimer = timers[yourColor];
                bottomIsActive = gameState.currentPlayer === yourColor;
              } else {
                // Fallback - show black at bottom
                const isGuest = authState.isGuest || (!authState.isAuthenticated);
                if (isGuest) {
                  bottomName = 'Guest';
                } else {
                  bottomName = authState.user?.username || gameState.players.black;
                }
                bottomScore = gameState.scores.black;
                bottomTimer = timers.black;
                bottomIsActive = gameState.currentPlayer === 'black';
              }
            } else if (gameMode === 'ai-1' || gameMode === 'ai-2' || gameMode === 'ai-challenger' || gameMode === 'ai-skilled' || gameMode === 'ai-3' || gameMode === 'ai-engine') {
              // AI game - use playerColor (works for both authenticated and guest)
              yourColor = playerColor;
              opponentColor = playerColor === 'white' ? 'black' : 'white';
              // Bottom always shows you - "Guest" for guests, username for authenticated users
              if (yourColor) {
                const isGuest = authState.isGuest || (!authState.isAuthenticated);
                bottomName = isGuest ? 'Guest' : (authState.user?.username || (yourColor === 'white' ? gameState.players.white : gameState.players.black));
                bottomScore = gameState.scores[yourColor];
                bottomTimer = timers[yourColor];
                bottomIsActive = gameState.currentPlayer === yourColor;
              } else {
                // Fallback - show black at bottom
                const isGuest = authState.isGuest || (!authState.isAuthenticated);
                bottomName = isGuest ? 'Guest' : (authState.user?.username || gameState.players.black);
                bottomScore = gameState.scores.black;
                bottomTimer = timers.black;
                bottomIsActive = gameState.currentPlayer === 'black';
              }
            } else {
              // Local human vs human
              // For local games, show "Guest" at bottom if not authenticated, otherwise show player name
              const isGuest = authState.isGuest || (!authState.isAuthenticated);
              bottomName = isGuest ? 'Guest' : (authState.user?.username || gameState.players.black);
              bottomScore = gameState.scores.black;
              bottomTimer = timers.black;
              bottomIsActive = gameState.currentPlayer === 'black';
            }
            
            return (
              <div className="player-info bottom" style={{ width: 'calc(var(--board-size) + 4px)', boxSizing: 'border-box' }}>
                <div className={`player ${bottomIsActive ? 'active' : ''}`} id={`player-${yourColor || 'black'}`}>
                  <div className={`player-color ${yourColor || 'black'}`}></div>
                  <span>{bottomName}</span>
                  <span>Yugos: <span id={`${yourColor || 'black'}-score`}>{bottomScore}</span></span>
                </div>
                {timerEnabled && (
                  <GameTimerDisplay
                    seconds={bottomTimer}
                    isActive={bottomIsActive}
                    playerColor={yourColor || 'black'}
                    timerEnabled={timerEnabled}
                    gameMode={spectatorMode || gameMode === 'online' ? 'online' : gameMode}
                    isGameStarted={isGameStarted}
                    gameStatus={gameState.gameStatus}
                    onTimeout={handleTimerTimeout}
                    onTimerWarning={handleTimerWarning}
                    onTimerStopped={handleTimerStopped}
                    timerWarningShown={timerWarningShown[yourColor || 'black']}
                  />
                )}
              </div>
            );
          })()}
        </div>

        
        {/* Game controls */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {/* Action buttons - hidden when spectating */}
          {!spectatorMode && (
            <div className="player-buttons" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '256px', marginBottom: '0' }}>
              {isImportReviewMode ? (
                // Import review mode - only show Reset button
                <button 
                  className="btn action-btn" 
                  onClick={() => {
                    exitReviewMode();
                    resetGame();
                  }}
                  style={{ 
                    height: '40px', 
                    padding: '0 24px',
                    width: '100%',
                    backgroundColor: '#dc3545',
                    color: 'white'
                  }}
                >
                  Reset
                </button>
              ) : (
                <>
                  {(
                    <>
                      <button 
                        className="btn action-btn" 
                        onClick={isGameStarted && gameState.gameStatus === 'active' ? 
                          (gameMode === 'online' ? showResignDrawOptions : resignGame) : () => startGame()}
                        style={{ 
                          height: '40px', 
                          padding: '0 24px',
                          backgroundColor: !isGameStarted ? '#28a745' : undefined,
                          color: !isGameStarted ? 'white' : undefined
                        }}
                      >
                        {isGameStarted && gameState.gameStatus === 'active' ? 
                          (gameMode === 'online' ? 'Resign/Draw' : 'Resign') : 'Start'}
                      </button>
                      <button 
                        className="btn action-btn" 
                        onClick={resetGame}
                        style={{ height: '40px', padding: '0 24px' }}
                      >
                        Reset
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Mobile controls button - only show on mobile when game is not started */}
          {!isGameStarted && !spectatorMode && (
            <div className="mobile-controls-button" style={{ width: '256px', marginBottom: '10px' }}>
              <button 
                className="btn mobile-only" 
                onClick={openMobileControls}
                style={{ width: '100%', height: '40px' }}
              >
                Game Settings
              </button>
            </div>
          )}

          {/* Game controls area */}
          <div 
            className="game-controls-area" 
            style={{ 
              height: spectatorMode ? 'calc(var(--board-size) + 80px)' : undefined, 
              width: '256px', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-start', 
              overflowY: 'auto' 
            }}
            ref={(el) => {
              if (el && spectatorMode) {
                const computedHeight = window.getComputedStyle(el).height;
                console.log('[Spectator Debug] game-controls-area computed height:', computedHeight, 'expected: calc(var(--board-size) + 80px)');
              }
            }}
          >
            {/* Spectator indicator and Back to List button - shown when spectating */}
            {spectatorMode && (
              <div style={{ width: '100%', marginBottom: '0' }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  marginBottom: '8px',
                  padding: '8px 10px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '4px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    <span 
                      style={{ 
                        fontSize: '20px',
                        display: 'inline-block',
                        animation: eyeBlink ? 'eyeBlink 0.3s ease-in-out' : 'none'
                      }}
                    >
                      👁️
                    </span>
                    <span style={{ fontWeight: 'bold', color: '#1976d2', fontSize: '24px', textAlign: 'center' }}>Spectating</span>
                  </div>
                  {spectatorTournamentCtx && (
                    <div style={{ color: '#1976d2', fontSize: '12px', fontWeight: 700, textAlign: 'center', marginTop: 2 }}>
                      🏆 {spectatorTournamentCtx.name} — Round {spectatorTournamentCtx.round}
                    </div>
                  )}
                  <span style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
                    {viewerCount} {viewerCount === 1 ? 'viewer' : 'viewers'}
                  </span>
                </div>
                <button 
                  className="btn action-btn" 
                  onClick={() => {
                    stopWatching();
                    // Update ref immediately to ensure timer updates are processed correctly
                    currentPageRef.current = 'spectator';
                    setCurrentPage('spectator');
                    localStorage.setItem('mig_current_page', 'spectator');
                    // Rejoin spectator lobby to ensure we receive timer updates
                    if (socket && socket.connected) {
                      setTimeout(() => {
                        socket.emit('joinSpectatorLobby');
                      }, 100);
                    }
                  }}
                  style={{ 
                    height: '40px', 
                    padding: '0 24px',
                    width: '100%',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  ← Back to List
                </button>
              </div>
            )}
            
            {!isGameStarted && !spectatorMode && (
              <div id="pregame-controls">
                <div className="option-row">
                  <label htmlFor="game-mode-select">Opponent:</label>
                  <select 
                    id="game-mode-select" 
                    className="control-select"
                    value={gameMode}
                    onChange={(e) => setGameMode(e.target.value as any)}
                  >
                    <option value="local">Local Play</option>
                    <option value="ai-1">AI - Beginner</option>
                    <option value="ai-2">AI - Novice</option>
                    <option value="ai-challenger">AI - Intermediate</option>
                    <option value="ai-skilled">AI - Advanced</option>
                    <option value="ai-3">AI - Expert</option>
                    <option value="ai-engine">AI - Master</option>
                    <option value="online">Online Multiplayer</option>
                  </select>
                </div>

                {/* Color selection for AI games - White or Black only */}
                {(gameMode.startsWith('ai-')) && (
                  <div className="option-row" style={{ marginTop: 8, marginBottom: 8, justifyContent: 'center' }}>
                    <span style={{ marginRight: 8 }}>Play as:</span>
                    <div style={{ display: 'inline-flex', gap: 4 }}>
                      <button
                        type="button"
                        className={`color-choice-btn${playerColorChoice === 'white' ? ' selected' : ''}`}
                        aria-label="Play as White"
                        onClick={() => setPlayerColorChoice('white')}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc', background: playerColorChoice === 'white' ? '#ecf0f1' : 'white', fontWeight: playerColorChoice === 'white' ? 'bold' : 'normal' }}
                      >
                        ⚪
                      </button>
                      <button
                        type="button"
                        className={`color-choice-btn${playerColorChoice === 'black' ? ' selected' : ''}`}
                        aria-label="Play as Black"
                        onClick={() => setPlayerColorChoice('black')}
                        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc', background: playerColorChoice === 'black' ? '#2c3e50' : 'white', color: playerColorChoice === 'black' ? 'white' : '#2c3e50', fontWeight: playerColorChoice === 'black' ? 'bold' : 'normal' }}
                      >
                        ⚫
                      </button>
                    </div>
                  </div>
                )}

                <div className="option-row">
                  <label htmlFor="timer-toggle">Game Timer:</label>
                  <div className="toggle-container">
                    <span className="toggle-label">Off</span>
                    <label className="toggle small">
                      <input 
                        type="checkbox" 
                        id="timer-toggle" 
                        checked={timerEnabled}
                        onChange={(e) => setTimerEnabled(e.target.checked)}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span className={`toggle-label ${timerEnabled ? 'active' : ''}`}>On</span>
                  </div>
                </div>

                {timerEnabled && (
                  <div className="timer-settings" id="timer-settings">
                    <div className="timer-row">
                      <div className="option-cell">
                        <label htmlFor="minutes-per-player">Minutes:</label>
                        <select 
                          id="minutes-per-player" 
                          className="control-select"
                          value={minutesPerPlayer}
                          onChange={(e) => setMinutesPerPlayer(parseInt(e.target.value))}
                        >
                          <option value="15">15</option>
                          <option value="10">10</option>
                          <option value="5">5</option>
                          <option value="3">3</option>
                          <option value="2">2</option>
                          <option value="1">1</option>
                        </select>
                      </div>
                      <div className="option-cell">
                        <label htmlFor="increment-seconds">Increment:</label>
                        <select 
                          id="increment-seconds" 
                          className="control-select"
                          value={incrementSeconds}
                          onChange={(e) => setIncrementSeconds(parseInt(e.target.value))}
                        >
                          <option value="5">5 sec</option>
                          <option value="2">2 sec</option>
                          <option value="1">1 sec</option>
                          <option value="0">0 sec</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Game log - show after game starts (including spectator mode) */}
            {isGameStarted && (
              <div id="game-log-container">
                <div className="review-button-container" style={{ width: '236px', margin: '15px auto 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ fontSize: '1.2em', margin: '0' }}>Move History</h2>
                </div>
                {renderMoveHistory()}
                {/* Review and Undo buttons - Review available for all, Undo only for players */}
                {/* Debug: Check if conditions are met */}
                {(() => {
                  if (spectatorMode) {
                    console.log('[Spectator Debug] moveHistory.length:', moveHistory.length, 'isReviewMode:', isReviewMode, 'isGameStarted:', isGameStarted);
                  }
                  return moveHistory.length > 0 && !isReviewMode;
                })() && (
                  <div style={{ width: '100%', margin: '12px 0', display: 'flex', gap: '8px', justifyContent: 'center', padding: '0 10px', boxSizing: 'border-box', flexShrink: 0 }}>
                    {/* Review button - available for spectators and players */}
                    <button 
                      className="review-button" 
                      onClick={enterReviewMode}
                      style={{ flex: 1, maxWidth: '110px' }}
                    >
                      Review
                    </button>
                    {/* Undo button - only for local/AI games, not spectators */}
                    {!spectatorMode && (gameMode === 'local' || gameMode.startsWith('ai-')) && boardHistory.length > 0 && (
                      <button
                        className="review-button"
                        onClick={undoMove}
                        title="Undo last move"
                        style={{ flex: 1, maxWidth: '110px' }}
                      >
                        Undo
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Import Moves/Position button - available even when no game started, but hidden for spectators */}
            {!isGameStarted && !isReviewMode && !spectatorMode && (
              <div style={{ width: '100%', margin: '20px 0', display: 'flex', justifyContent: 'center' }}>
                <button
                  className="btn"
                  onClick={() => setShowImportMovesModal(true)}
                  title="Import move history or position string from Test Lab"
                  style={{
                    fontSize: '0.9rem',
                    padding: '10px 20px',
                    backgroundColor: '#17a2b8',
                    color: 'white'
                  }}
                >
                  📥 Import Move History / Position
                </button>
              </div>
            )}

            {/* Chat Component - show during online games and spectator mode */}
            {isGameStarted && (gameMode === 'online' || spectatorMode) && (
              <div style={{ width: '100%', alignSelf: 'stretch' }}>
                <GameChat
                  gameId={gameId}
                  currentPlayerName={authState.isAuthenticated ? (authState.user?.username || 'Guest') : 'Guest'}
                  currentPlayerColor={spectatorMode ? null : playerColor}
                  socket={socket}
                  isOnline={true}
                  soundEnabled={notificationsEnabled}
                  playChatSound={playChatSound}
                />
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Room modal */}
      {showRoomModal && (
        <>
          <div className="overlay" style={{ display: 'block', zIndex: 10001 }} onClick={() => setShowRoomModal(false)} />
          <div className="notification" style={{ display: 'block', zIndex: 10002 }}>
            <h2>Private Room</h2>
            <p>Create a room to invite a friend, or join an existing room:</p>
            
            {/* Timer Selection for Room Creation */}
            <div style={{ margin: '15px 0', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}>
              <div style={{ fontSize: '0.9rem', color: '#495057', marginBottom: '8px', textAlign: 'center' }}>
                <strong>⏱️ Time Control</strong>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setRoomTimerPreference(10)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: `2px solid ${roomTimerPreference === 10 ? '#007bff' : '#ccc'}`,
                    background: roomTimerPreference === 10 ? '#007bff' : 'white',
                    color: roomTimerPreference === 10 ? 'white' : '#333',
                    fontWeight: roomTimerPreference === 10 ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  10 minutes
                </button>
                <button
                  type="button"
                  onClick={() => setRoomTimerPreference(5)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: `2px solid ${roomTimerPreference === 5 ? '#007bff' : '#ccc'}`,
                    background: roomTimerPreference === 5 ? '#007bff' : 'white',
                    color: roomTimerPreference === 5 ? 'white' : '#333',
                    fontWeight: roomTimerPreference === 5 ? 'bold' : 'normal',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  5 minutes
                </button>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', textAlign: 'center', marginTop: '6px' }}>
                (Only applies when creating a room)
              </div>
            </div>
            
            <div style={{ margin: '20px 0' }}>
              <div style={{ marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Enter room code (e.g. ABC123)"
                  value={roomCodeInput}
                  onChange={(e) => setRoomCodeInput(e.target.value.toUpperCase())}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '16px',
                    textAlign: 'center',
                    letterSpacing: '2px',
                    fontFamily: 'monospace'
                  }}
                  maxLength={6}
                />
              </div>
            </div>
            
            <div className="notification-buttons">
              <button className="btn" onClick={createRoom}>Create Room</button>
              <button 
                className="btn" 
                onClick={joinRoom}
                disabled={!roomCodeInput.trim()}
                style={{ 
                  opacity: roomCodeInput.trim() ? 1 : 0.5,
                  cursor: roomCodeInput.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Join Room
              </button>
              <button className="btn" onClick={() => setShowRoomModal(false)}>Cancel</button>
            </div>
          </div>
        </>
      )}

      {/* Current room display */}
      {currentRoom && (
        <>
          <div className="overlay" style={{ display: 'block', zIndex: 10001 }} />
          <div className="notification" style={{ display: 'block', zIndex: 10002 }}>
            <h2>Room {currentRoom.code}</h2>
            
            <div style={{ margin: '20px 0', textAlign: 'center' }}>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8f9fa', 
                border: '1px solid #dee2e6', 
                borderRadius: '8px',
                marginBottom: '15px'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '10px' }}>
                  Share this code with your friend:
                </div>
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  letterSpacing: '4px',
                  fontFamily: 'monospace',
                  color: '#007bff',
                  userSelect: 'text',
                  cursor: 'text'
                }}>
                  {currentRoom.code}
                </div>
                <button
                  className="btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(currentRoom.code);
                      showToast('Room code copied to clipboard!');
                    } catch (err) {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = currentRoom.code;
                      textArea.style.position = 'fixed';
                      textArea.style.opacity = '0';
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand('copy');
                        showToast('Room code copied to clipboard!');
                      } catch (fallbackErr) {
                        showToast('Failed to copy. Please select and copy manually.');
                      }
                      document.body.removeChild(textArea);
                    }
                  }}
                  style={{
                    marginTop: '10px',
                    padding: '8px 16px',
                    fontSize: '0.9rem'
                  }}
                >
                  📋 Copy Room Code
                </button>
              </div>
              
              <div style={{ textAlign: 'left' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Host:</strong> {currentRoom.hostName} {currentRoom.isHost && '(You)'}
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <strong>Guest:</strong> {currentRoom.guestName || 'Waiting...'}
                </div>
                
                {currentRoom.status === 'waiting' && (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    color: '#856404'
                  }}>
                    ⏳ Waiting for opponent to join...
                  </div>
                )}
                
                {currentRoom.status === 'ready' && (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    color: '#155724'
                  }}>
                    ✅ Both players ready! {currentRoom.isHost ? 'You can start the game.' : 'Waiting for host to start...'}
                  </div>
                )}
              </div>
            </div>
            
            <div className="notification-buttons">
              {currentRoom.isHost && currentRoom.status === 'ready' && (
                <button 
                  className="btn" 
                  onClick={startRoomGame}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  Start Game
                </button>
              )}
              <button className="btn" onClick={leaveRoom}>Leave Room</button>
            </div>
          </div>
        </>
      )}

      {/* Game notification */}
      {notification.show && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => hideNotification()} />
          <div className={`notification ${notification.title === 'Game Drawn' ? 'game-drawn-modal' : ''}`} style={{ display: 'block' }}>
            <h2>{notification.title}</h2>
            {notification.title === 'Play Online' && !authState.isAuthenticated && !authState.isGuest ? (
              <div className="notification-buttons">
                <button className="btn" onClick={() => { hideNotification({ preserveBoard: true }); handlePlayAsGuestWrapper(); }}>
                  Play as Guest
                </button>
                <button className="btn" onClick={() => hideNotification({ preserveBoard: true })}>
                  Cancel
                </button>
              </div>
            ) : (notification.title === 'Game Over' || notification.title === 'Game Drawn' || notification.title === 'Time Out' || notification.title === 'Opponent Disconnected') && gameMode === 'online' && gameState.gameStatus === 'finished' && !spectatorMode && isTournamentGame ? (
              <>
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>{notification.message}</p>
                <div className="notification-buttons">
                  {moveHistory.length > 0 && (
                    <button
                      className="btn"
                      onClick={() => {
                        hideNotification({ preserveBoard: true });
                        enterReviewMode();
                      }}
                      style={{ backgroundColor: '#17a2b8', color: 'white' }}
                    >
                      📋 Review Game
                    </button>
                  )}
                  <button className="btn" onClick={() => hideNotification()}>
                    Close
                  </button>
                </div>
              </>
            ) : (notification.title === 'Game Over' || notification.title === 'Game Drawn' || notification.title === 'Time Out' || notification.title === 'Opponent Disconnected') && gameMode === 'online' && gameState.gameStatus === 'finished' && !spectatorMode ? (
              <>
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>{notification.message}</p>
                {rematchState.requested ? (
                  <>
                    <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#f0f8ff', border: '1px solid #007bff', borderRadius: '4px' }}>
                      <p style={{ margin: '0', fontWeight: 'bold', color: '#007bff' }}>
                        🎯 {rematchState.requestedBy} has challenged you to a rematch!
                      </p>
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                        Do you accept the challenge?
                      </p>
                    </div>
                    <div className="notification-buttons">
                      {/* Show Review Game button for online games with move history */}
                      {moveHistory.length > 0 && (
                        <button 
                          className="btn" 
                          onClick={() => {
                            hideNotification({ preserveBoard: true });
                            enterReviewMode();
                          }}
                          style={{ backgroundColor: '#17a2b8', color: 'white' }}
                        >
                          📋 Review Game
                        </button>
                      )}
                      <button className="btn" onClick={() => respondToRematch(true)} style={{ backgroundColor: '#28a745', color: 'white' }}>
                        ⚔️ Accept Rematch
                      </button>
                      <button className="btn" onClick={() => respondToRematch(false)} style={{ backgroundColor: '#dc3545', color: 'white' }}>
                        ❌ Decline
                      </button>
                      <button className="btn" onClick={() => {
                        // Auto-decline rematch when closing modal if a challenge is pending
                        respondToRematch(false);
                      }}>
                        Close
                      </button>
                    </div>
                  </>
                ) : rematchState.waitingForResponse ? (
                  <>
                    <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                      <p style={{ margin: '0', fontWeight: 'bold', color: '#856404' }}>
                        ⏳ Waiting for opponent's response...
                      </p>
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                        Your rematch challenge has been sent!
                      </p>
                    </div>
                    <div className="notification-buttons">
                      {/* Show Review Game button for online games with move history */}
                      {moveHistory.length > 0 && (
                        <button 
                          className="btn" 
                          onClick={() => {
                            hideNotification({ preserveBoard: true });
                            enterReviewMode();
                          }}
                          style={{ backgroundColor: '#17a2b8', color: 'white' }}
                        >
                          📋 Review Game
                        </button>
                      )}
                      <button className="btn" onClick={() => hideNotification({ preserveBoard: true })}>
                        Continue Waiting
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                      <p style={{ margin: '0', fontWeight: 'bold', color: '#495057' }}>
                        🔄 Want to play again?
                      </p>
                      <p style={{ margin: '5px 0 0 0', fontSize: '0.9em', color: '#666' }}>
                        Challenge your opponent to a rematch! Colors will be swapped.
                      </p>
                    </div>
                    <div className="notification-buttons">
                      {/* Show Review Game button for online games with move history */}
                      {moveHistory.length > 0 && (
                        <button 
                          className="btn" 
                          onClick={() => {
                            hideNotification({ preserveBoard: true });
                            enterReviewMode();
                          }}
                          style={{ backgroundColor: '#28a745', color: 'white' }}
                        >
                          📋 Review Game
                        </button>
                      )}
                      <button 
                        className="btn" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Button clicked - event fired!');
                          requestRematch();
                        }}
                        style={{ 
                          backgroundColor: '#007bff', 
                          color: 'white',
                          zIndex: 1002,
                          position: 'relative',
                          pointerEvents: 'auto',
                          cursor: 'pointer'
                        }}
                      >
                        🎯 Request Rematch
                      </button>

                      <button className="btn" onClick={() => hideNotification()}>
                        Close
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (notification.title === 'Game Over' || notification.title === 'Game Drawn' || notification.title === 'Time Out') ? (
              <>
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>{notification.message}</p>
                <div className="notification-buttons">
                  {/* Spectator mode buttons */}
                  {spectatorMode ? (
                    <>
                      {notification.primaryButton && (
                        <button 
                          className="btn" 
                          onClick={notification.onPrimary}
                          style={{ backgroundColor: '#17a2b8', color: 'white' }}
                        >
                          📋 {notification.primaryButton}
                        </button>
                      )}
                      {notification.secondaryButton && (
                        <button 
                          className="btn" 
                          onClick={notification.onSecondary}
                          style={{ backgroundColor: '#1976d2', color: 'white' }}
                        >
                          ← {notification.secondaryButton}
                        </button>
                      )}
                      <button className="btn" onClick={() => hideNotification()}>
                        Close
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Player mode buttons - show Review Game if move history exists */}
                      {moveHistory.length > 0 && (
                        <button 
                          className="btn" 
                          onClick={() => {
                            hideNotification({ preserveBoard: true });
                            enterReviewMode();
                          }}
                          style={{ backgroundColor: '#28a745', color: 'white' }}
                        >
                          📋 Review Game
                        </button>
                      )}
                      <button className="btn" onClick={() => hideNotification()}>
                        Close
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5' }}>{notification.message}</p>
                <div className="notification-buttons">
                  {notification.primaryButton && notification.onPrimary && (
                    <button 
                      className="btn" 
                      onClick={notification.onPrimary}
                      style={{ backgroundColor: '#28a745', color: 'white' }}
                    >
                      {notification.primaryButton}
                    </button>
                  )}
                  {notification.secondaryButton && notification.onSecondary && (
                    <button 
                      className="btn" 
                      onClick={notification.onSecondary}
                    >
                      {notification.secondaryButton}
                    </button>
                  )}
                  <button className="btn" onClick={() => hideNotification()}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}


      {/* Resign Confirmation Modal */}
      {showResignConfirmation && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowResignConfirmation(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>⚠️ Confirm Resignation</h2>
            <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5', margin: '20px 0' }}>
              Are you sure you want to resign this game?
              {'\n\n'}Your opponent will be declared the winner.
            </p>
            <div className="notification-buttons">
              <button 
                className="btn" 
                onClick={confirmResignation}
                style={{ 
                  backgroundColor: '#dc3545', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                🏳️ Yes, Resign
              </button>
              <button 
                className="btn" 
                onClick={cancelResignation}
                style={{ 
                  backgroundColor: '#28a745', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                ⚔️ Continue Playing
              </button>
            </div>
          </div>
        </>
      )}

      {/* Desktop Resign/Draw Modal */}
      {showResignDrawModal && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowResignDrawModal(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>🎯 Choose Your Action</h2>
            <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5', margin: '20px 0' }}>
              What would you like to do?
            </p>
            <div className="notification-buttons">
              <button 
                className="btn" 
                onClick={handleResignFromModal}
                style={{ 
                  backgroundColor: '#dc3545', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                🏳️ Resign Game
              </button>
              {gameMode === 'online' && !isTournamentGame && (
                <button 
                  className="btn" 
                  onClick={handleDrawFromModal}
                  style={{ 
                    backgroundColor: '#17a2b8', 
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  🤝 Offer Draw
                </button>
              )}
              <button 
                className="btn" 
                onClick={cancelResignDrawModal}
                style={{ 
                  backgroundColor: '#28a745', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                ⚔️ Continue Playing
              </button>
            </div>
          </div>
        </>
      )}

      {/* Draw Offer Modal */}
      {showDrawOffer && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowDrawOffer(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>🤝 Draw Offer</h2>
            <p style={{ whiteSpace: 'pre-line', lineHeight: '1.5', margin: '20px 0' }}>
              {pendingDrawFrom === 'white' ? 'White' : 'Black'} player has offered a draw.
              {'\n\n'}Do you accept?
            </p>
            <div className="notification-buttons">
              <button 
                className="btn" 
                onClick={() => respondToDrawOffer(true)}
                style={{ 
                  backgroundColor: '#28a745', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                ✅ Accept Draw
              </button>
              <button 
                className="btn" 
                onClick={() => respondToDrawOffer(false)}
                style={{ 
                  backgroundColor: '#dc3545', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                ❌ Decline Draw
              </button>
            </div>
          </div>
        </>
      )}

      {/* Stats Modal */}
      {showStats && (
        <>
          <div className="overlay" style={{ display: 'block' }} onClick={() => setShowStats(false)} />
          <div className="notification" style={{ display: 'block' }}>
            <h2>📊 Player Statistics</h2>
            {statsLoading ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading statistics...</p>
              </div>
            ) : userStats ? (
              <div style={{ padding: '20px 0' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '20px',
                  marginBottom: '20px' 
                }}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '15px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '2px solid #e9ecef'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50' }}>
                      {userStats.gamesPlayed || 0}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>Games Played</div>
                  </div>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '15px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px',
                    border: '2px solid #e9ecef'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                      {userStats.winRate || 0}%
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>Win Rate</div>
                  </div>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr 1fr', 
                  gap: '15px',
                  marginBottom: '20px' 
                }}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: '6px',
                    border: '1px solid #c3e6cb'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#155724' }}>
                      {userStats.wins || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#155724' }}>Wins</div>
                  </div>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    backgroundColor: '#f8d7da', 
                    borderRadius: '6px',
                    border: '1px solid #f1b0b7'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#721c24' }}>
                      {userStats.losses || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#721c24' }}>Losses</div>
                  </div>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '12px', 
                    backgroundColor: '#fff3cd', 
                    borderRadius: '6px',
                    border: '1px solid #ffeaa7'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#856404' }}>
                      {userStats.draws || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#856404' }}>Draws</div>
                  </div>
                </div>

                {(userStats.currentStreak || 0) > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '15px', 
                    backgroundColor: userStats.streakType === 'win' ? '#e8f5e8' : '#f8d7da', 
                    borderRadius: '8px',
                    border: `2px solid ${userStats.streakType === 'win' ? '#c3e6cb' : '#f1b0b7'}`,
                    marginBottom: '20px'
                  }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: userStats.streakType === 'win' ? '#155724' : '#721c24'
                    }}>
                      🔥 Current Streak: {userStats.currentStreak || 0} {userStats.streakType === 'win' ? 'Wins' : 'Losses'}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>No statistics available yet.</p>
                <p>Play some online games to see your stats!</p>
              </div>
            )}

            {authState.user && (
              <div style={{ 
                textAlign: 'center', 
                padding: '10px', 
                color: '#6c757d', 
                fontSize: '14px',
                borderTop: '1px solid #e9ecef',
                marginTop: '15px',
                paddingTop: '15px'
              }}>
                Stats for {authState.user.username || 'User'}
              </div>
            )}
            <div className="notification-buttons">
              <button className="btn" onClick={() => setShowStats(false)}>Close</button>
            </div>
          </div>
        </>
      )}

      {/* PWA Installation Banner */}
      {showPWABanner && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2c3e50',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          maxWidth: '90vw',
          width: '400px',
          textAlign: 'center',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>
            🎮 Install migoyugo Game
          </div>
          <div style={{ marginBottom: '15px', fontSize: '14px', opacity: 0.9 }}>
            Add to your home screen for fullscreen play with no browser bars!
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={handleInstallPWA}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '5px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📱 Install App
            </button>
            <button 
              onClick={dismissPWABanner}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                padding: '8px 16px',
                borderRadius: '5px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Mobile Review Bar - Only visible on mobile when in review mode */}
      {isReviewMode && isMobileDevice && (
        <div id="mobile-review-bar">
          <div className="review-buttons">
            <button 
              className="btn" 
              onClick={firstMove}
              disabled={currentReviewMove <= 0}
              title="First Move"
            >
              ⏮
            </button>
            <button 
              className="btn" 
              onMouseDown={(e) => startHoldScroll('prev', e)}
              onMouseUp={(e) => stopHoldScroll('prev', e)}
              onMouseLeave={(e) => stopHoldScroll('prev', e)}
              onTouchStart={(e) => startHoldScroll('prev', e)}
              onTouchEnd={(e) => stopHoldScroll('prev', e)}
              disabled={currentReviewMove <= 0}
              title="Previous Move"
            >
              ◀
            </button>
            <button 
              className="btn" 
              onMouseDown={(e) => startHoldScroll('next', e)}
              onMouseUp={(e) => stopHoldScroll('next', e)}
              onMouseLeave={(e) => stopHoldScroll('next', e)}
              onTouchStart={(e) => startHoldScroll('next', e)}
              onTouchEnd={(e) => stopHoldScroll('next', e)}
              disabled={currentReviewMove >= moveHistory.length}
              title="Next Move"
            >
              ▶
            </button>
            <button 
              className="btn" 
              onClick={lastMove}
              disabled={currentReviewMove >= moveHistory.length}
              title="Last Move"
            >
              ⏭
            </button>
            {moveHistory.length > 0 && (
              <button
                className="btn"
                id="copy-move-history-btn"
                onClick={copyMoveHistory}
                title="Copy move history to clipboard"
              >
                📋 Copy
              </button>
            )}
            <button 
              className="btn" 
              onClick={exitReviewMode}
              title="Exit Review"
              style={{ backgroundColor: '#dc3545', color: 'white' }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Mobile Button Container - All mobile bottom elements in one container */}
      <div id="mobile-button-container">
        {/* Action Bar - Always visible */}
        <div id="mobile-action-bar">
          <button 
            className="btn" 
            onClick={isGameStarted && gameState.gameStatus === 'active' ? resignGame : () => startGame()}
            style={{ 
              backgroundColor: !isGameStarted ? '#28a745' : undefined,
              color: !isGameStarted ? 'white' : undefined
            }}
          >
            {isGameStarted && gameState.gameStatus === 'active' ? 'Resign' : 'Start'}
          </button>
          <button 
            className="btn" 
            onClick={isGameStarted && gameState.gameStatus === 'active' ? 
              (gameMode === 'online' && !isTournamentGame ? offerDraw : openMobileControls) :
              openMobileControls}
          >
            {isGameStarted && gameState.gameStatus === 'active' ? 
              (gameMode === 'online' && !isTournamentGame ? 'Offer Draw' : 'Opponent') : 
              'Opponent'}
          </button>
          <button 
            className="btn" 
            onClick={resetGame}
          >
            Reset
          </button>
          {/* Undo button - only for local/AI games */}
          {(gameMode === 'local' || gameMode.startsWith('ai-')) && boardHistory.length > 0 && (
            <button
              className="btn"
              onClick={undoMove}
              title="Undo last move"
            >
              Undo
            </button>
          )}
        </div>

        {/* Chat - Only visible during online games, inside container between action bar and utility bar */}
        {isMobileDevice && isGameStarted && gameMode === 'online' && (
          <GameChat
            gameId={gameId}
            currentPlayerName={authState.isAuthenticated ? (authState.user?.username || 'Guest') : 'Guest'}
            currentPlayerColor={playerColor}
            socket={socket}
            isOnline={gameMode === 'online'}
            isMobile={true}
            soundEnabled={notificationsEnabled}
            playChatSound={playChatSound}
          />
        )}

        {/* Utility Bar - Always visible, at bottom of container */}
        <div id="mobile-utility-bar">
          {/* Add Review button conditionally */}
          {moveHistory.length > 0 && !isReviewMode && (
            <button 
              className="btn" 
              onClick={enterReviewMode}
              style={{ backgroundColor: '#28a745', color: 'white' }}
            >
              Review
            </button>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="toast" style={{ display: 'block' }}>
          {toast}
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authModalMode}
        onModeChange={setAuthModalMode}
        onUsernameSet={handleUsernameSet}
      />


      {/* Challenge Toast */}
      {currentChallenge && (
        <ChallengeToast
          challengeId={currentChallenge.challengeId}
          challengerUsername={currentChallenge.challengerUsername}
          timerSettings={currentChallenge.timerSettings}
          isRated={currentChallenge?.isRated}
          isInGame={gameMode === 'online' && isGameStarted && gameState.gameStatus === 'active'}
          onAccept={() => {
            setCurrentChallenge(null);
            // Show next challenge in queue if any
            if (challengeQueue.length > 0) {
              const next = challengeQueue[0];
              setChallengeQueue(prev => prev.slice(1));
              setCurrentChallenge(next);
            }
          }}
          onDecline={() => {
            setCurrentChallenge(null);
            // Show next challenge in queue if any
            if (challengeQueue.length > 0) {
              const next = challengeQueue[0];
              setChallengeQueue(prev => prev.slice(1));
              setCurrentChallenge(next);
            }
          }}
          onTimeout={async () => {
            // Auto-decline after timeout
            try {
              await ApiService.declineChallenge(currentChallenge.challengeId);
              setCurrentChallenge(null);
              // Show next challenge in queue if any
              if (challengeQueue.length > 0) {
                const next = challengeQueue[0];
                setChallengeQueue(prev => prev.slice(1));
                setCurrentChallenge(next);
              }
            } catch (err) {
              console.error('Failed to auto-decline challenge:', err);
              setCurrentChallenge(null);
            }
          }}
        />
      )}

      {/* Challenge Declined Toast */}
      {challengeDeclinedToast && (
        <ChallengeDeclinedToast
          challengedUsername={challengeDeclinedToast.challengedUsername}
          reason={challengeDeclinedToast.reason}
          onClose={() => setChallengeDeclinedToast(null)}
        />
      )}


        {sharedNavOverlaysEl}
      {welcomeAndHumanModalsEl}

      <GlobalChat
        isOpen={GLOBAL_CHAT_UI_ENABLED && showGlobalChat}
        onClose={() => { setShowGlobalChat(false); setMessagePlayerTarget(null); }}
        socket={socket}
        authState={authState}
        openConversationWith={messagePlayerTarget}
      />
      {globalTournamentOverlaysEl}
      </div>
    </div>
  );
};

export default App;